import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import cytoscape, { Core, ElementDefinition } from 'cytoscape';
import { LinkAnalysisService, LinkAnalysisManualNodePayload } from '../../../services/link-analysis/link-analysis.service';
import { isSearchCompleted } from '../viewer/utils/graph-search-completion.util';
import { SearchResultProcessorService } from '../../../services/search-result-processor/search-result-processor.service';
import { resolveLayeredGraphIcon } from '../../../services/link-analysis/link-analysis-graph-icon.util';
import {
  expandGraphWithSourceTypeHubs,
  isLayerSourceHeroNode,
  isSourceTypeHubNode,
  orderLayerNodesForSourceHubs,
  resolveNodeDisplayValue,
  resolveNodeSourceType,
  resolveSourceTypeLabel,
} from '../../../services/link-analysis/link-analysis-source-hub.util';
import {
  getAggregateCollectedValues,
  getNodeSearchableValues,
  getServerActionsForValueType,
  getServerAvailableActions,
  getServerAvailableActionsByValueType,
  hasServerAvailableActionsContract,
  isActionableEmailValue,
  isActionableNameValue,
  isAggregateHubNode,
  isEmailHintValue,
} from '../../../services/link-analysis/link-analysis-searchable.util';
import {
  BRAND_ICON_VISUALS,
  createPlatformIconDataUrl,
  resolveBrandKeyFromPlatform,
} from '../../../services/link-analysis/platform-icon.util';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { ThemeService } from '../../../services/theme/theme.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { LinkAnalysisGraphDashboardComponent } from '../viewer/graph-dashboard/graph-dashboard.component';
import { NodeActionItem, SearchValueOption } from '../viewer/models/graph-viewer.models';
import { shouldDisplayOsintSourceOnGraph } from '../viewer/utils/osint-search-parity.util';
import {
  canStartImageSearch,
  isAuthGatedOsintImageUrl,
  isSearchableImageSrc,
} from '../../../services/osint-search/image-search-from-result.util';
import { buildDetailFieldSearchActions } from '../viewer/utils/node-detail-panel.util';

@Component({
  selector: 'app-link-analysis-result',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LinkAnalysisGraphDashboardComponent,
  ],
  templateUrl: './link-analysis-result.component.html',
  styleUrls: ['./link-analysis-result.component.css'],
})
export class LinkAnalysisResultComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('graphCanvas') graphCanvas?: ElementRef<HTMLDivElement>;
  @ViewChild('rynoMain') rynoMain?: ElementRef<HTMLElement>;
  @ViewChild(LinkAnalysisGraphDashboardComponent)
  graphDashboard?: LinkAnalysisGraphDashboardComponent;

  loading = true;
  regenerating = false;
  stoppingAutomation = false;
  linkAnalysisId = '';
  investigation: any = null;
  manualNodeOpen = false;
  manualNodeSaving = false;
  manualNodeError = '';
  manualNodeDraft: LinkAnalysisManualNodePayload = this.emptyManualNode();

  private emptyManualNode(): LinkAnalysisManualNodePayload {
    return { label:'', type:'person', name:'', phone:'', email:'', username:'', image_url:'', profile_url:'', notes:'', details:[] };
  }

  openManualNode(): void {
    this.manualNodeDraft = this.emptyManualNode();
    this.manualNodeError = '';
    this.manualNodeOpen = true;
  }

  closeManualNode(): void {
    if (!this.manualNodeSaving) this.manualNodeOpen = false;
  }

  saveManualNode(): void {
    if (this.manualNodeSaving || !this.manualNodeDraft.label.trim()) return;
    if (this.manualNodeDraft.details.some(detail => !detail.label.trim() || !detail.value.trim())) {
      this.manualNodeError = 'Enter both a label and value for each custom field, or remove the empty field.';
      return;
    }
    this.manualNodeSaving = true;
    this.manualNodeError = '';
    this.linkAnalysisService.addManualNode(this.linkAnalysisId, this.manualNodeDraft).subscribe({
      next: response => {
        this.manualNodeSaving = false;
        this.manualNodeOpen = false;
        this.graphPayload = response?.data?.graph_payload ?? this.graphPayload;
        this.investigation = { ...this.investigation, graph_payload: this.graphPayload };
        this.rebuildDisplayGraph();
        this.rebuildNodeLayers();
        this.cdr.detectChanges();
        this.fetchResult(false);
      },
      error: error => {
        this.manualNodeSaving = false;
        this.manualNodeError = error?.error?.message || 'Unable to add the node. Please try again.';
        this.cdr.markForCheck();
      }
    });
  }
  graphPayload: any = null;
  errorMessage = '';
  pollingHandle: any = null;
  cy?: Core;

  selectedEntity: any = null;
  selectedEntityKind: 'node' | 'edge' | null = null;
  /** Pending value to preselect after aggregate → source jump (R12). */
  private pendingAggregatePreselect: string | null = null;
  /**
   * Detail-card search icon focus: force this query value/type through Run
   * (even when the value is only on the card field, not in multi-select options).
   */
  private detailFieldFocusQuery: {
    queryValue: string;
    queryType: string;
  } | null = null;
  multiSelectedNodeCount = 0;
  multiSelectMode = false;
  creditCosts: Record<string, number> | null = null;
  private creditCostsSubscription: Subscription | null = null;
  private selectedNodeIds = new Set<string>();
  private groupDragState: {
    sourceId: string;
    sourceStart: { x: number; y: number };
    nodeStarts: Array<{ id: string; x: number; y: number }>;
  } | null = null;
  private syncingGroupDrag = false;
  private layeredRings: Array<{ layer: number; radius: number }> = [];

  nodeTypeFilter = 'all';
  roleFilter = 'all';
  tierFilter = 'all';
  graphSearchTerm = '';
  minConfidence = 0;
  focusedNodeId: string | null = null;
  canvasDocked = true;
  inspectorDockSide: 'left' | 'right' = 'right';
  inspectorWidth = 360;
  inspectorDragging = false;
  inspectorDockPreview: 'left' | 'right' | null = null;
  graphSidebarExpanded = false;
  graphFullscreenOpen = false;
  graphViewMode: 'hierarchical' | 'layered' = 'layered';
  /** Show nodes with layer <= this value. -1 = all layers visible. */
  visibleUpTo = -1;
  showInspectorActions = false;
  hoveredNodeLabel: string | null = null;
  hoverTooltipX = 0;
  hoverTooltipY = 0;
  nodeActionPopoverVisible = false;
  nodeActionPopoverX = 0;
  nodeActionPopoverY = 0;
  nodeActionPopoverPinned = false;
  nodeActionPopoverDragging = false;
  selectedPreviewImageFailed = false;
  private hoveredNodeId: string | null = null;
  private nodeActionPopoverNodeId: string | null = null;
  private nodeActionPopoverDragState: {
    pointerId: number;
    startClientX: number;
    startClientY: number;
    originX: number;
    originY: number;
  } | null = null;
  private nodeActionPopoverDragMoveHandler: ((event: PointerEvent) => void) | null = null;
  private nodeActionPopoverDragEndHandler: ((event: PointerEvent) => void) | null = null;
  private inspectorDragState: {
    pointerId: number;
    mode: 'dock' | 'resize';
    startClientX: number;
    startWidth: number;
    startDockSide: 'left' | 'right';
  } | null = null;
  private inspectorDragMoveHandler: ((event: PointerEvent) => void) | null = null;
  private inspectorDragEndHandler: ((event: PointerEvent) => void) | null = null;
  private layeredFlowAnimationId: number | null = null;
  private layeredFlowDashOffset = 0;
  private nodeLayerById = new Map<string, number>();
  private searchParentById = new Map<string, string>();
  private displayGraphPayload: { nodes: any[]; edges: any[] } | null = null;
  private graphResizeObserver: ResizeObserver | null = null;
  private fitGraphTimer: ReturnType<typeof setTimeout> | null = null;
  actionLoading = false;
  runningActionKeys = new Set<string>();
  pendingLocalActions: any[] = [];
  private trackedManualActionIds = new Set<string>();
  private readonly pendingActionPollingGraceMs = 10 * 60 * 1000;
  starterQueryType: 'phone' | 'email' | 'username' = 'phone';
  starterQueryValue = '';
  aiMode: 'quick' | 'deep-cheap' | 'deep' = 'quick';
  isLightTheme = false;
  selectedCollectionValues: string[] = [];
  selectedNodeActionKeys: string[] = [];
  private themeSubscription: Subscription | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private linkAnalysisService: LinkAnalysisService,
    private searchResultProcessorService: SearchResultProcessorService,
    private searchServiceStatusService: SearchServiceStatusService,
    private themeService: ThemeService,
    private utilsService: UtilsService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.isLightTheme = this.themeService.currentTheme.value === 'light-theme';
    this.themeSubscription = this.themeService.currentTheme$.subscribe((theme) => {
      const nextLight = theme === 'light-theme';
      if (this.isLightTheme === nextLight) return;
      this.isLightTheme = nextLight;
      this.applyGraphTheme();
    });
    this.creditCostsSubscription = this.searchServiceStatusService.creditCosts$.subscribe(
      (creditCosts) => {
        this.creditCosts = creditCosts;
      },
    );
    this.linkAnalysisId = this.route.snapshot.paramMap.get('id') || '';
    if (!this.linkAnalysisId) {
      this.errorMessage = 'Missing investigation id.';
      this.loading = false;
      return;
    }

    this.fetchResult(true);
  }

  ngAfterViewInit(): void {
    this.observeGraphCanvas();
    if (this.graphPayload?.nodes?.length) {
      this.renderGraph();
    }
  }

  ngOnDestroy(): void {
    this.stopPoll();
    if (this.fitGraphTimer) {
      clearTimeout(this.fitGraphTimer);
    }
    this.graphResizeObserver?.disconnect();
    this.creditCostsSubscription?.unsubscribe();
    this.themeSubscription?.unsubscribe();
    this.stopGraphFlowAnimation();
    this.stopNodeActionPopoverDrag();
    this.stopInspectorDrag();
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
    this.cy?.destroy();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.graphFullscreenOpen) {
      this.setGraphFullscreenOpen(false);
    }
  }

  private observeGraphCanvas(): void {
    const canvas = this.graphCanvas?.nativeElement;
    if (!canvas || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observeTarget = canvas.parentElement || canvas;
    this.graphResizeObserver?.disconnect();
    this.graphResizeObserver = new ResizeObserver(() => {
      if (!this.cy) return;
      this.cy.resize();
      this.fitVisibleGraph();
      if (this.nodeActionPopoverNodeId && this.cy) {
        const node = this.cy.getElementById(this.nodeActionPopoverNodeId);
        if (node?.length) {
          this.updateNodeActionPopoverPosition(node);
          this.cdr.detectChanges();
        }
      }
    });
    this.graphResizeObserver.observe(observeTarget);
    this.graphResizeObserver.observe(canvas);
  }

  get summary() {
    return (
      this.graphPayload?.summary ||
      this.investigation?.summary || {
        nodeCount: 0,
        edgeCount: 0,
        nodeTypeCounts: {},
      }
    );
  }

  get isDrillDown(): boolean {
    return this.investigation?.mode === 'drill_down';
  }

  get isAutomatedMode(): boolean {
    return this.investigation?.mode === 'automated';
  }

  get hasRenderableGraph(): boolean {
    return Boolean(this.graphPayload?.nodes?.length);
  }

  get isAutomatedExpansionLocked(): boolean {
    if (!this.isAutomatedMode) return false;
    const status = (this.investigation?.status || '').toLowerCase();
    if (status === 'pending' || status === 'processing') return true;
    return this.isInvestigationProcessing();
  }

  get resultLoadingMessage(): string {
    if (this.isDrillDown && (this.loading || this.isInvestigationProcessing())) {
      return 'Running drill-down search…';
    }
    if (this.isAutomatedMode && (this.loading || this.isInvestigationProcessing())) {
      return this.autoExpansionProgressMessage;
    }
    if (this.actionLoading) {
      return 'Running search action…';
    }
    return 'Loading investigation…';
  }

  get autoExpansionProgressMessage(): string {
    const summary = this.investigation?.summary || this.graphPayload?.summary || {};
    const maxLayer = Number(summary.autoExpandMaxLayer || 2);
    const readyLayer = Number(summary.readyLayer || 0);
    const wave = Number(summary.autoExpandWave || summary.expandingWave || 0);
    const runningAuto = (this.actionHistory || []).some(
      (action) => action.status === 'processing' && action.auto_expansion,
    );

    if (!this.hasRenderableGraph) {
      return 'Running automated OSINT + leak search for layer 1…';
    }
    if (runningAuto || wave > 0) {
      const shown = Math.max(1, readyLayer || 1);
      return `Layer ${shown} ready · expanding deeper layers in background (up to layer ${maxLayer}). Click Stop to end automation early — manual search unlocks after stop or completion.`;
    }
    return `Layer 1 ready · finishing automated expansion (up to layer ${maxLayer}). Click Stop to unlock manual search early.`;
  }

  get showGraphLoader(): boolean {
    // Progressive automated: once layer 1 exists, keep the graph visible.
    if (this.isAutomatedMode && this.hasRenderableGraph && this.isInvestigationProcessing()) {
      return false;
    }
    if (this.hasRenderableGraph && this.isAutomatedMode) {
      return this.actionLoading && !this.isInvestigationProcessing();
    }
    return this.loading || this.actionLoading || this.isInvestigationProcessing();
  }

  get showBackgroundProgress(): boolean {
    return this.isAutomatedMode && this.hasRenderableGraph && this.isInvestigationProcessing();
  }

  get showStopAutomation(): boolean {
    return this.isAutomatedMode && this.isInvestigationProcessing();
  }

  get inspectorDockClass(): 'left' | 'right' {
    return this.inspectorDockPreview || this.inspectorDockSide;
  }

  get showInspectorOpen(): boolean {
    return !this.canvasDocked;
  }

  get actionHistory(): any[] {
    return Array.isArray(this.investigation?.action_history)
      ? this.investigation.action_history
      : [];
  }

  get selectedNodeActions(): Array<{ key: string; label: string; actionType: any; queryType: any }> {
    if (!this.selectedEntity || this.selectedEntityKind !== 'node') {
      return [];
    }
    return this.getNodeActions(this.selectedEntity);
  }

  get showInspectorSearchPanel(): boolean {
    if (this.selectedEntityKind !== 'node' || !this.selectedEntity) {
      return false;
    }
    if (this.isAutomatedExpansionLocked) {
      return false;
    }
    // Aggregates are view-only; actions live on the source after row jump (R12).
    if (isAggregateHubNode(this.selectedEntity) || this.selectedEntity.metadata?.isAggregateCollection) {
      return false;
    }
    if (this.isDrillDown) {
      return true;
    }
    return (
      this.getNodeActions(this.selectedEntity).length > 0 ||
      this.canRunAiForNode(this.selectedEntity) ||
      this.selectedSearchableValueRows.length > 0 ||
      this.collectionSelectionRequired
    );
  }

  get showNodeActionPopover(): boolean {
    return Boolean(
      this.nodeActionPopoverVisible &&
      this.selectedEntity &&
      this.selectedEntityKind === 'node' &&
      !this.showDrillDownAddMenu &&
      this.shouldOfferNodeSearchOptions(this.selectedEntity),
    );
  }

  get selectedActionCardTitle(): string {
    if (!this.selectedEntity || this.selectedEntityKind !== 'node') return 'Add new node';
    if (this.isDrillDown && this.selectedEntity.type === 'starter') return 'Add starter search';
    if (this.selectedEntity.metadata?.isAggregateCollection || isAggregateHubNode(this.selectedEntity)) {
      const type =
        this.selectedEntity.metadata?.collectionType ||
        (this.selectedEntity.type === 'aggregate_phones'
          ? 'phone'
          : this.selectedEntity.type === 'aggregate_emails'
            ? 'email'
            : this.selectedEntity.type === 'aggregate_names'
              ? 'name'
              : this.selectedEntity.type === 'aggregate_photos'
                ? 'photo'
                : null);
      if (type === 'email') return 'Collected emails';
      if (type === 'phone') return 'Collected phones';
      if (type === 'username') return 'Collected usernames';
      if (type === 'name') return 'Collected names';
      if (type === 'photo' || type === 'image') return 'Collected photos';
    }
    if (this.selectedEntity.type === 'image') return 'Face Search';
    if (this.selectedEntity.type === 'email') return 'Email Expansion';
    if (this.selectedEntity.type === 'phone') return 'Phone Expansion';
    if (this.selectedEntity.type === 'username' || this.selectedEntity.type === 'social_profile') {
      return 'Username Expansion';
    }
    if (this.selectedEntity.type === 'leak_source') return 'Leak source expansion';
    if (this.selectedEntity.type === 'osint_source' || this.isOsintPlatformSourceNode(this.selectedEntity)) {
      return 'OSINT source expansion';
    }
    return this.isDrillDown ? 'Evidence Node' : 'Search actions';
  }

  get selectedActionAvailabilityLabel(): string {
    if (!this.selectedEntity || this.selectedEntityKind !== 'node') return '';
    if (this.investigation?.search_access?.has_active_subscription === false) {
      return 'Your subscription has expired or is inactive. Activate a plan to run searches.';
    }
    if (this.isAutomatedExpansionLocked) {
      return 'Automated expansion still running — manual search unlocks after all default layers finish.';
    }
    if (this.selectedEntity.type === 'starter') {
      return 'Choose the first search type to start the workspace.';
    }
    if (this.collectionSelectionRequired && !this.selectedCollectionValues.length) {
      return `Select one or more values below, then run searches.`;
    }
    if (this.showSourceSearchValuePicker && !this.selectedCollectionValues.length) {
      return 'Select one or more values below, then run searches.';
    }
    const actionCount =
      this.getNodeActions(this.selectedEntity).length +
      (this.canRunAiForNode(this.selectedEntity) ? 1 : 0);
    if (!this.isDrillDown) {
      return actionCount
        ? `${actionCount} search action${actionCount === 1 ? '' : 's'} available for this node`
        : 'No search action is available for this node.';
    }
    return actionCount
      ? `${actionCount} search action${actionCount === 1 ? '' : 's'} available`
      : 'No expansion action is available for this node.';
  }

  get selectedHasAvailableActions(): boolean {
    return this.hasAvailableActions(this.selectedEntity);
  }

  private buildOsintLeakActions(
    queryType: 'phone' | 'email' | 'username' | 'name',
  ): Array<{ key: string; label: string; actionType: any; queryType: any }> {
    const actions: Array<{ key: string; label: string; actionType: any; queryType: any }> = [
      {
        key: `${queryType}-osint`,
        label: `Run OSINT ${queryType} search`,
        actionType: 'osint_search',
        queryType,
      },
    ];
    // Name is OSINT-only; leak search does not support name queries.
    if (queryType !== 'name') {
      actions.push({
        key: `${queryType}-leak`,
        label: `Run leak ${queryType} search`,
        actionType: 'leak_search',
        queryType,
      });
    }
    return actions;
  }

  private aggregateCollectionHasValues(node: any): boolean {
    return getAggregateCollectedValues(node).length > 0;
  }

  private aggregateHasValidValue(node: any, queryType: string): boolean {
    const values = getAggregateCollectedValues(node);
    if (!values.length) return false;
    if (queryType === 'phone') {
      return values.some((value: string) => {
        const digits = String(value).replace(/\D/g, '');
        return digits.length >= 10 && digits.length <= 15;
      });
    }
    if (queryType === 'email') {
      return values.some((value: string) => isActionableEmailValue(value));
    }
    if (queryType === 'username') {
      return values.some((value: string) => String(value).trim().length >= 2);
    }
    if (queryType === 'name') {
      return values.some(
        (value: string) => String(value).trim().length >= 2 && !isEmailHintValue(value),
      );
    }
    if (queryType === 'image') {
      return values.some((value: string) => this.isValidImageSearchUrl(String(value)));
    }
    return false;
  }

  private getNodeActions(node: any): Array<{ key: string; label: string; actionType: any; queryType: any }> {
    if (!node) {
      return [];
    }
    const type = node.type;
    if (type === 'starter') return [];
    if (node.metadata?.isIdentityHub) return [];
    if (type === 'email_hint' || type === 'phone_hint' || node.metadata?.isHint) {
      return [];
    }
    // Aggregates are view-only (R10–R12). Actions bind to the source after row jump.
    if (isAggregateHubNode(node) || node.metadata?.isAggregateCollection) {
      return [];
    }

    // Phase 6: server availableActions is the source of truth (do not invent buttons).
    if (hasServerAvailableActionsContract(node)) {
      const serverUnion = getServerAvailableActions(node);
      if (!serverUnion.length && !Object.keys(getServerAvailableActionsByValueType(node)).length) {
        return [];
      }
      return this.buildActionsFromServerContract(node);
    }

    const actions: Array<{ key: string; label: string; actionType: any; queryType: any }> = [];

    if (type === 'phone' && this.isCompletePhoneNode(node)) {
      actions.push(...this.buildOsintLeakActions('phone'));
    } else if (type === 'email' && this.isCompleteEmailNode(node)) {
      actions.push(...this.buildOsintLeakActions('email'));
    } else if (type === 'person' && this.getNodeQueryValue(node, 'name')) {
      actions.push(...this.buildOsintLeakActions('name'));
    } else if (type === 'leak_source' || type === 'osint_source' || this.isOsintPlatformSourceNode(node)) {
      if (this.sourceHasValidValues(node, 'phone')) {
        actions.push(...this.buildOsintLeakActions('phone'));
      }
      if (this.sourceHasValidValues(node, 'email')) {
        actions.push(...this.buildOsintLeakActions('email'));
      }
      if (this.sourceHasValidValues(node, 'username')) {
        actions.push(...this.buildOsintLeakActions('username'));
      }
      if (this.sourceHasValidValues(node, 'name')) {
        actions.push(...this.buildOsintLeakActions('name'));
      }
      if (this.sourceHasValidValues(node, 'image')) {
        if (this.nodeHasFaceSearchableImage(node)) {
          actions.push(this.buildFaceSearchAction());
        }
      }
      return actions;
    } else if ((type === 'username' || type === 'social_profile') && this.getNodeQueryValue(node, 'username')) {
      actions.push(...this.buildOsintLeakActions('username'));
    } else if ((type === 'image' || type === 'face_result') && this.getNodeQueryValue(node, 'image')) {
      if (this.nodeHasFaceSearchableImage(node)) {
        actions.push(this.buildFaceSearchAction());
      }
    } else if (type === 'face_image' && this.nodeHasFaceSearchableImage(node)) {
      actions.push(this.buildFaceSearchAction());
    }

    // Phone / email / username nodes can also carry other searchable values (e.g. images, names).
    this.appendExtraSearchableActions(actions, node);
    return actions;
  }

  /** Build node actions strictly from server availableActionsByValueType / availableActions. */
  private buildActionsFromServerContract(
    node: any,
  ): Array<{ key: string; label: string; actionType: any; queryType: any }> {
    const actions: Array<{ key: string; label: string; actionType: any; queryType: any }> = [];
    let byType = getServerAvailableActionsByValueType(node);
    const union = getServerAvailableActions(node);

    // Builders may only set availableActions; derive per-type until decorate runs.
    if (!Object.keys(byType).length) {
      if (!union.length) return [];
      const searchable = getNodeSearchableValues(node);
      const type = String(node?.type || '').toLowerCase();
      if (type === 'face_image') {
        byType = { image: union.filter((action) => action === 'face_search') };
      } else {
        const matrix: Record<string, string[]> = {
          phone: ['osint_search', 'leak_search'],
          email: ['osint_search', 'leak_search'],
          username: ['osint_search', 'leak_search'],
          name: ['osint_search'],
          image: ['face_search'],
        };
        byType = {};
        (Object.keys(matrix) as Array<keyof typeof matrix>).forEach((valueType) => {
          const values = (searchable as any)[valueType];
          if (!Array.isArray(values) || !values.length) return;
          const allowed = matrix[valueType].filter((action) => union.includes(action));
          if (allowed.length) byType[valueType] = allowed;
        });
      }
    }

    Object.keys(byType).forEach((valueType) => {
      const allowed = byType[valueType] || [];
      if (!allowed.length) return;
      if (valueType === 'image') {
        if (!allowed.includes('face_search')) return;
        if (
          String(node?.type || '').toLowerCase() === 'face_image' ||
          this.nodeHasFaceSearchableImage(node)
        ) {
          if (!actions.some((action) => action.key === 'image-face')) {
            actions.push(this.buildFaceSearchAction());
          }
        }
        return;
      }
      if (!this.sourceHasValidValues(node, valueType as any)) return;
      this.buildOsintLeakActions(valueType as any)
        .filter((action) => allowed.includes(action.actionType))
        .forEach((action) => {
          if (!actions.some((existing) => existing.key === action.key)) {
            actions.push(action);
          }
        });
    });
    return actions;
  }

  private buildFaceSearchAction(): { key: string; label: string; actionType: any; queryType: any } {
    return {
      key: 'image-face',
      label: 'Run face search from image',
      actionType: 'face_search',
      queryType: 'image',
    };
  }

  /** Add OSINT/leak/face actions for searchable values beyond the node's primary type. */
  private appendExtraSearchableActions(
    actions: Array<{ key: string; label: string; actionType: any; queryType: any }>,
    node: any,
  ): void {
    const hasKey = (key: string) => actions.some((action) => action.key === key);
    const appendOsintLeak = (queryType: 'phone' | 'email' | 'username' | 'name') => {
      if (!this.sourceHasValidValues(node, queryType)) return;
      this.buildOsintLeakActions(queryType).forEach((action) => {
        if (!hasKey(action.key)) actions.push(action);
      });
    };

    appendOsintLeak('phone');
    appendOsintLeak('email');
    appendOsintLeak('username');
    appendOsintLeak('name');

    if (this.nodeHasFaceSearchableImage(node) && !hasKey('image-face')) {
      actions.push(this.buildFaceSearchAction());
    }
  }

  private sourceHasValidValues(
    node: any,
    queryType: 'phone' | 'email' | 'username' | 'name' | 'image',
  ): boolean {
    const values = this.sourceValuesForQueryType(node, queryType);
    if (!values.length) return false;
    if (queryType === 'phone') {
      return values.some((value) => {
        const digits = String(value).replace(/\D/g, '');
        return digits.length >= 10 && digits.length <= 15;
      });
    }
    if (queryType === 'email') {
      return values.some((value) => isActionableEmailValue(value));
    }
    if (queryType === 'username' || queryType === 'name') {
      return values.some((value) => String(value).trim().length >= 2 && !isEmailHintValue(value));
    }
    if (queryType === 'image') {
      return values.some((value) => this.canRunFaceSearchForImage(String(value), node));
    }
    return false;
  }

  /**
   * Face search gate: face_result / ladder face_image are product-confirmed faces.
   * Other images still require in-browser faceDetectCache (same as OSINT result pages).
   */
  private canRunFaceSearchForImage(value: string, node?: any): boolean {
    const url = String(value || '').trim();
    if (!url || !isSearchableImageSrc(url)) return false;
    const type = String(node?.type || node?.raw?.type || '').toLowerCase();
    const metadata = node?.metadata || node?.raw?.metadata || {};
    const alreadyFaceResult =
      type === 'face_result' ||
      metadata?.searchedForFace === true;
    // Ladder face_image nodes are R14-approved human-face nodes (not logos).
    const confirmedFaceImage =
      type === 'face_image' && metadata?.isHumanFace !== false;
    if (alreadyFaceResult || confirmedFaceImage || metadata?.isHumanFace === true) {
      return canStartImageSearch({ src: url, detectUrl: url, faceDetected: true });
    }
    return canStartImageSearch(url);
  }

  private nodeHasFaceSearchableImage(node: any): boolean {
    const primary = this.getNodeQueryValue(node, 'image');
    if (primary && this.canRunFaceSearchForImage(primary, node)) return true;
    return this.sourceValuesForQueryType(node, 'image').some((value) =>
      this.canRunFaceSearchForImage(String(value), node),
    );
  }

  onGraphGalleryUpdated(): void {
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  private sourceValuesForQueryType(node: any, queryType: string): string[] {
    const searchableValues = getNodeSearchableValues(node);
    if (queryType === 'name') {
      return searchableValues.name || [];
    }
    if (queryType === 'email') {
      return searchableValues.email || [];
    }
    const values = (searchableValues as any)[queryType];
    return Array.isArray(values) ? values.filter(Boolean) : [];
  }

  private getSourceSearchValueOptions(
    node: any,
  ): Array<{ value: string; type: string; label: string }> {
    const searchableValues = getNodeSearchableValues(node);
    const options: Array<{ value: string; type: string; label: string }> = [];
    const pushValues = (type: string, label: string, values: string[]) => {
      values.forEach((value) => {
        const normalized = String(value || '').trim();
        if (!normalized) return;
        if (type === 'email' && !isActionableEmailValue(normalized)) return;
        if (type === 'image' && !this.canRunFaceSearchForImage(normalized, node)) return;
        options.push({
          value: normalized,
          type,
          label: type === 'image' ? 'Photo' : `${normalized} (${label})`,
        });
      });
    };
    pushValues('phone', 'phone', searchableValues.phone || []);
    pushValues('email', 'email', searchableValues.email || []);
    pushValues('username', 'username', searchableValues.username || []);
    pushValues('name', 'name', searchableValues.name || []);
    pushValues('image', 'image', searchableValues.image || []);
    return options;
  }

  get selectedActionHistory(): any[] {
    if (!this.selectedEntity?.id) return [];
    const selectedId = this.selectedEntity.id;
    const selectedImageUrl =
      this.selectedEntity.type === 'image'
        ? this.getNodeQueryValue(this.selectedEntity, 'image')
        : '';

    return this.actionHistory.filter((action) => {
      if (action.source_node_id === selectedId) return true;
      return Boolean(
        selectedImageUrl &&
        action.action_type === 'face_search' &&
        action.query_type === 'image' &&
        action.image_url === selectedImageUrl,
      );
    });
  }

  get isRootSelected(): boolean {
    return Boolean(
      this.selectedEntityKind === 'node' &&
      this.selectedEntity?.id &&
      this.rootNode?.id &&
      this.selectedEntity.id === this.rootNode.id,
    );
  }

  get showDrillDownAddMenu(): boolean {
    return Boolean(
      this.isDrillDown &&
      this.selectedEntityKind === 'node' &&
      this.selectedEntity &&
      (this.selectedEntity.type === 'starter' || this.isRootSelected),
    );
  }

  get rootNode(): any | null {
    const nodes = this.graphPayload?.nodes || [];
    if (this.graphPayload?.rootNode) {
      return this.graphPayload.rootNode;
    }

    const queryRoot =
      nodes.find((node: any) => String(node?.type || '').toLowerCase() === 'query_root') ||
      nodes.find((node: any) => String(node?.id || '').startsWith('query_root:'));
    if (queryRoot) return queryRoot;

    if (!this.isDrillDown) {
      const rootType = String(this.investigation?.root_type || 'phone').toLowerCase();
      const rootValue = this.investigation?.root_value || '';
      const normalizedRoot = rootValue ? this.normalizeEntityValue(rootValue) : '';
      if (normalizedRoot) {
        const exact = nodes.find((node: any) => {
          const nodeType = String(node?.type || '').toLowerCase();
          const nodeValue = this.normalizeEntityValue(node?.label || node?.metadata?.queryValue || '');
          return nodeType === rootType && nodeValue === normalizedRoot;
        });
        if (exact) return exact;
      }
      const typed = nodes.find((node: any) => String(node?.type || '').toLowerCase() === rootType);
      if (typed) return typed;
    }

    return (
      nodes.find((node: any) => node.type === 'starter') ||
      nodes.find((node: any) => Number(node?.metadata?.layer) === 0) ||
      nodes.find((node: any) => node.id === this.investigation?.root_value) ||
      nodes[0] ||
      null
    );
  }

  get rootDisplayValue(): string {
    const drillRoot = this.presentedDrillDownRoot;
    if (drillRoot?.label) return drillRoot.label;
    return (
      this.investigation?.root_value ||
      this.rootNode?.label ||
      (this.isDrillDown ? 'Drill-down workspace' : 'Investigation')
    );
  }

  get dashboardSubtitle(): string {
    const mode = this.isDrillDown ? 'Drill-down search' : 'Automated search';
    return `${mode} • Target: ${this.rootDisplayValue} • ${this.exploredLabel}`;
  }

  get dashboardNodeActions(): NodeActionItem[] {
    return this.selectedNodeActions.map((action) => ({
      ...action,
      running: this.isActionButtonRunning(action),
      disabled: this.isActionSearchBlocked(action),
    }));
  }

  get dashboardSelectedActionKeys(): string[] {
    return this.selectedNodeActionKeys;
  }

  get dashboardSelectedSearchValues(): string[] {
    return this.selectedCollectionValues;
  }

  get dashboardShowSearchValuePicker(): boolean {
    // Aggregates are view-only (R10–R12) — never feed SELECT VALUE checkboxes.
    if (isAggregateHubNode(this.selectedEntity) || this.selectedEntity?.metadata?.isAggregateCollection) {
      return false;
    }
    return this.showCollectionValuePicker || this.showSourceSearchValuePicker;
  }

  get dashboardSearchValueOptions(): SearchValueOption[] {
    if (isAggregateHubNode(this.selectedEntity) || this.selectedEntity?.metadata?.isAggregateCollection) {
      return [];
    }
    if (this.showCollectionValuePicker) {
      const type = String(this.selectedEntity?.metadata?.collectionType || 'value');
      return this.selectedAggregateValues.map((value) => ({
        value,
        type,
        label: value,
      }));
    }
    return this.sourceSearchValueOptions;
  }

  get dashboardShowStarterSearch(): boolean {
    return Boolean(this.isDrillDown && this.selectedEntity?.type === 'starter');
  }

  get exploredLabel(): string {
    const nodeCount = this.summary.nodeCount || this.graphPayload?.nodes?.length || 0;
    if (this.isDrillDown) {
      return `${this.actionHistory.length || 0} actions, ${nodeCount} nodes explored`;
    }
    return `${nodeCount} nodes, ${this.summary.edgeCount || 0} links`;
  }

  get graphModeLabel(): string {
    return this.isDrillDown ? 'Drill-down' : 'Automated';
  }

  get isLayeredView(): boolean {
    return this.graphViewMode === 'layered';
  }

  get maxGraphLayer(): number {
    const nodes = this.graphPayload?.nodes || [];
    if (!nodes.length) return 0;
    return Math.max(...nodes.map((node: any) => this.getNodeLayer(node)));
  }

  get levelMeta(): Array<{ index: number; label: string; color: string; count: number }> {
    const layerColors = ['#22c55e', '#3b82f6', '#a855f7', '#f59e0b', '#64748b'];
    const counts = new Map<number, number>();
    (this.graphPayload?.nodes || []).forEach((node: any) => {
      const layer = this.getNodeLayer(node);
      counts.set(layer, (counts.get(layer) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort(([left], [right]) => left - right)
      .map(([index, count]) => ({
        index,
        label: index === 0 ? 'Root' : index === 1 ? 'Layer 1' : `Layer ${index}`,
        color: layerColors[index] || layerColors[layerColors.length - 1],
        count,
      }));
  }

  get visibleNodeCount(): number {
    return this.countVisibleNodes();
  }

  get totalNodeCount(): number {
    return this.summary.nodeCount || this.graphPayload?.nodes?.length || 0;
  }

  get depthDisplay(): string {
    if (this.nodeTypeFilter !== 'all') {
      return this.formatTypeLabel(this.nodeTypeFilter);
    }
    if (this.visibleUpTo < 0 || this.visibleUpTo >= this.maxGraphLayer) {
      return `All (${this.maxGraphLayer + 1} levels)`;
    }
    if (this.visibleUpTo === 0) {
      return 'Root only';
    }
    return `Up to Layer ${this.visibleUpTo}`;
  }

  get hasActiveGraphFilters(): boolean {
    return (
      this.nodeTypeFilter !== 'all' ||
      (this.visibleUpTo >= 0 && this.visibleUpTo < this.maxGraphLayer)
    );
  }

  get starterTypeLabel(): string {
    const type = String(
      this.investigation?.root_type || this.rootNode?.metadata?.queryType || this.rootNode?.type || 'phone',
    );
    return this.formatTypeLabel(type === 'starter' ? 'phone' : type);
  }

  get selectedNodeLayer(): number {
    if (!this.selectedEntity || this.selectedEntityKind !== 'node') return 0;
    return this.getNodeLayer(this.selectedEntity);
  }

  get selectedNodeRole(): string {
    return String(this.selectedEntity?.role || 'supporting');
  }

  get selectedEvidenceTier(): string {
    return String(this.selectedEntity?.evidenceTier || 'tier2');
  }

  get selectedHasHiddenChildren(): boolean {
    if (!this.selectedEntity || this.selectedEntityKind !== 'node') return false;
    const childLayer = this.selectedNodeLayer + 1;
    if (childLayer > this.maxGraphLayer) return false;
    if (this.visibleUpTo < 0) return false;
    if (this.visibleUpTo >= childLayer) return false;
    const nodeId = this.selectedEntity.id;
    return (this.graphPayload?.edges || []).some((edge: any) => {
      const childId = edge.source === nodeId ? edge.target : edge.target === nodeId ? edge.source : null;
      if (!childId) return false;
      const child = (this.graphPayload?.nodes || []).find((item: any) => item.id === childId);
      return child && this.getNodeLayer(child) === childLayer;
    });
  }

  get nodeTypeLegend(): Array<{ label: string; color: string; type: string }> {
    return [
      { label: 'Phone', color: '#22c55e', type: 'phone' },
      { label: 'Email', color: '#3b82f6', type: 'email' },
      { label: 'Person', color: '#f59e0b', type: 'person' },
      { label: 'OSINT source', color: '#14b8a6', type: 'osint_source' },
      { label: 'Leak source', color: '#ef5350', type: 'leak_source' },
      { label: 'Social profile', color: '#a855f7', type: 'social_profile' },
      { label: 'Image', color: '#64748b', type: 'image' },
      { label: 'Hint', color: '#94a3b8', type: 'email_hint' },
      { label: 'Location', color: '#e2761b', type: 'location' },
    ];
  }

  get edgeTypeLegend(): Array<{ label: string; color: string; dashed?: boolean }> {
    return [
      { label: 'Confirmed', color: '#22c55e' },
      { label: 'Candidate', color: '#3b82f6' },
      { label: 'Supporting', color: '#64748b', dashed: true },
    ];
  }

  get selectedSourcePlatformResult(): any | null {
    if (!this.isInspectableSourceNode(this.selectedEntity)) {
      return null;
    }
    const platform = this.selectedEntity?.metadata?.platform || this.selectedEntity?.label;
    const sourceData = this.selectedEntity?.metadata?.sourceData || this.selectedEntity?.metadata?.leakData;
    if (!platform || !sourceData) return null;
    const processed = this.searchResultProcessorService.process({
      sources: {
        [platform]: sourceData?.leak_data ? sourceData.leak_data : sourceData,
      },
    });
    return processed?.platforms?.[0] || null;
  }

  get selectedAggregateValues(): string[] {
    if (!this.selectedEntity || !isAggregateHubNode(this.selectedEntity)) return [];
    return getAggregateCollectedValues(this.selectedEntity);
  }

  get showCollectionValuePicker(): boolean {
    // Aggregates never expose selectable search values.
    if (!this.selectedEntity || isAggregateHubNode(this.selectedEntity)) return false;
    return this.selectedAggregateValues.length > 1;
  }

  get showSourceSearchValuePicker(): boolean {
    if (!this.selectedEntity) return false;
    if (isAggregateHubNode(this.selectedEntity) || this.selectedEntity.metadata?.isAggregateCollection) {
      return false;
    }
    // Any node with multiple searchable values (phone + image, etc.) needs the picker.
    return this.getCollectionValueOptions(this.selectedEntity).length > 1;
  }

  get sourceSearchValueOptions(): Array<{ value: string; type: string; label: string }> {
    if (!this.selectedEntity) return [];
    return this.getSourceSearchValueOptions(this.selectedEntity);
  }

  get collectionSelectionRequired(): boolean {
    return this.getCollectionValueOptions().length > 1;
  }

  get isCollectionSearchBlocked(): boolean {
    return this.isSearchValueSelectionBlocked();
  }

  isActionSearchBlocked(action: { queryType: any }): boolean {
    return this.isSearchValueSelectionBlocked(action);
  }

  private isSearchValueSelectionBlocked(action?: { queryType: any }): boolean {
    if (!this.selectedEntity) return false;
    if (
      this.detailFieldFocusQuery?.queryValue &&
      (!action ||
        String(action.queryType || '').toLowerCase() === this.detailFieldFocusQuery.queryType)
    ) {
      return false;
    }
    const node = this.selectedEntity;
    const valueOptions = this.getCollectionValueOptions();
    if (valueOptions.length > 1) {
      if (!this.selectedCollectionValues.length) return true;
      if (!action) return false;
      return this.getSelectedValuesForQueryType(action.queryType).length === 0;
    }
    if (isAggregateHubNode(node) || node.metadata?.isAggregateCollection) {
      return false;
    }
    if (node.type === 'leak_source' || node.type === 'osint_source' || this.isOsintPlatformSourceNode(node)) {
      return false;
    }
    return false;
  }

  get collectionRadioGroupName(): string {
    return `collection-${this.selectedEntity?.id || 'none'}`;
  }

  get selectedCollectionTypeLabel(): string {
    const type =
      this.selectedEntity?.metadata?.collectionType ||
      (this.selectedEntity?.type === 'aggregate_phones'
        ? 'phone'
        : this.selectedEntity?.type === 'aggregate_emails'
          ? 'email'
          : this.selectedEntity?.type === 'aggregate_names'
            ? 'name'
            : this.selectedEntity?.type === 'aggregate_photos'
              ? 'photo'
              : null);
    const labels: Record<string, string> = {
      phone: 'phone number',
      email: 'email',
      username: 'username',
      name: 'name',
      photo: 'photo',
      image: 'image',
    };
    return labels[String(type || '')] || 'value';
  }

  get selectedHintValueRows(): Array<{ key: string; label: string; values: string[] }> {
    const metadata = this.selectedEntity?.metadata || {};
    if (isAggregateHubNode(this.selectedEntity) || metadata.isAggregateCollection) {
      const hints = Array.isArray(metadata.hintValues)
        ? metadata.hintValues
        : Array.isArray(metadata.displayOnly?.hints)
          ? metadata.displayOnly.hints
          : [];
      if (
        (metadata.collectionType === 'email' || this.selectedEntity?.type === 'aggregate_emails') &&
        hints.length
      ) {
        return [{ key: 'email', label: 'Email hints (not searchable)', values: hints }];
      }
      if (
        (metadata.collectionType === 'phone' || this.selectedEntity?.type === 'aggregate_phones') &&
        hints.length
      ) {
        return [{ key: 'phone', label: 'Phone hints (not searchable)', values: hints }];
      }
      return [];
    }
    const hintValues = metadata.hintValues;
    if (!hintValues || typeof hintValues !== 'object') return [];
    return [
      { key: 'phone', label: 'Phone hints (not searchable)', values: hintValues.phone || [] },
      { key: 'email', label: 'Email hints (not searchable)', values: hintValues.email || [] },
    ].filter((row) => row.values.length > 0);
  }

  get selectedExtractedNames(): string[] {
    const names = this.selectedEntity?.metadata?.extractedNames;
    return Array.isArray(names) ? names : [];
  }

  get selectedSearchableValueRows(): Array<{ key: string; label: string; values: string[] }> {
    if (!this.selectedEntity) return [];
    // Aggregates are view-only — popover/sidebar render their own unique list (no search values).
    if (isAggregateHubNode(this.selectedEntity) || this.selectedEntity.metadata?.isAggregateCollection) {
      return [];
    }
    const searchableValues = getNodeSearchableValues(this.selectedEntity);
    return [
      { key: 'phone', label: 'Phones', values: searchableValues.phone || [] },
      { key: 'email', label: 'Emails', values: searchableValues.email || [] },
      { key: 'username', label: 'Usernames', values: searchableValues.username || [] },
      { key: 'name', label: 'Names', values: searchableValues.name || [] },
      { key: 'image', label: 'Images', values: searchableValues.image || [] },
    ].filter((row) => row.values.length > 0);
  }

  get selectedSourceDataFieldCount(): number {
    const sourceData =
      this.selectedEntity?.metadata?.sourceData || this.selectedEntity?.metadata?.leakData;
    if (!sourceData || typeof sourceData !== 'object') return 0;
    return Object.keys(sourceData).length;
  }

  get selectedLeakInspectorDetail(): {
    name: string;
    description: string;
    leakDate: string;
    logo: string;
    records: Array<{
      label: string;
      fields: Array<{ key: string; value: string; isLink: boolean }>;
    }>;
  } | null {
    if (this.selectedEntity?.type !== 'leak_source') return null;
    const record = this.selectedEntity.metadata?.leakData || this.selectedEntity.metadata?.sourceData;
    if (!record || typeof record !== 'object') return null;

    const leakRecords = Array.isArray(record.leak_data) ? record.leak_data : [];
    const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value);
    const formatValue = (value: any): string => {
      if (value === null || value === undefined) return '';
      if (Array.isArray(value)) {
        return value
          .map((item) => formatValue(item))
          .filter(Boolean)
          .join('\n');
      }
      if (typeof value === 'object') {
        if ('value' in value) return formatValue(value.value);
        return this.utilsService.formatKeyValueObject(value);
      }
      return String(value).trim();
    };
    const formatFieldLabel = (key: string): string =>
      key
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');

    return {
      name: record.name || this.selectedEntity.label || 'Leak source',
      description: record.description || this.selectedEntity.metadata?.description || '',
      leakDate: record.leak_date || this.selectedEntity.metadata?.leakDate || '',
      logo: record.logo || this.selectedEntity.metadata?.logo || '',
      records: leakRecords.map((entry: any, index: number) => {
        const fields = Object.entries(entry || {})
          .map(([key, value]) => ({
            key,
            value: formatValue(value),
          }))
          .filter((field) => field.value && field.value !== ',');
        return {
          label: leakRecords.length > 1 ? `Record ${index + 1}` : 'Leak record',
          fields: fields.map((field) => ({
            key: formatFieldLabel(field.key),
            value: field.value,
            isLink: isHttpUrl(field.value),
          })),
        };
      }),
    };
  }

  private collectLeakRecordUrls(record: any): string[] {
    if (!record || typeof record !== 'object') return [];
    const urls: string[] = [];
    const walk = (value: any) => {
      if (value === null || value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach((item) => walk(item));
        return;
      }
      if (typeof value === 'object') {
        if ('value' in value) {
          walk(value.value);
          return;
        }
        Object.values(value).forEach((item) => walk(item));
        return;
      }
      const text = String(value).trim();
      if (/^https?:\/\//i.test(text)) {
        urls.push(text);
      }
    };
    walk(record.leak_data);
    if (record.logo && /^https?:\/\//i.test(String(record.logo))) {
      urls.push(String(record.logo));
    }
    return urls;
  }

  get confidencePercent(): number {
    return Math.round(Number(this.selectedEntity?.confidence || 0) * 100);
  }

  get selectedSourceLinks(): Array<{ label: string; url: string }> {
    if (!this.selectedEntity) return [];
    const leakRecord =
      this.selectedEntity.type === 'leak_source'
        ? this.selectedEntity.metadata?.leakData || this.selectedEntity.metadata?.sourceData
        : null;
    const refs = [
      ...(this.selectedEntity.sourceReferences || []),
      ...(this.selectedEntity.metadata?.urls || []).map((url: string) => ({ label: url, url })),
      ...(leakRecord ? this.collectLeakRecordUrls(leakRecord).map((url) => ({ label: url, url })) : []),
      this.selectedEntity.metadata?.sourceProfileUrl
        ? { label: 'Source profile', url: this.selectedEntity.metadata.sourceProfileUrl }
        : null,
      this.selectedEntity.metadata?.sourceImageUrl
        ? { label: 'Source image', url: this.selectedEntity.metadata.sourceImageUrl }
        : null,
    ].filter(Boolean);

    const seen = new Set<string>();
    return refs
      .map((ref: any) => ({
        label: ref.label || ref.url || 'Source',
        url: ref.url || '',
      }))
      .filter((ref: any) => {
        if (!ref.url || seen.has(ref.url)) return false;
        seen.add(ref.url);
        return true;
      })
      .slice(0, 5);
  }

  get latestAction(): any | null {
    const history = this.appendPendingActionsToHistory([...this.actionHistory]);
    return this.sortActionsByTime(history).at(-1) || null;
  }

  get latestActionTitle(): string {
    const action = this.latestAction;
    if (!action) return '';
    const label = this.formatActionLabel(action);
    if (action.status === 'processing') return `Running ${label}...`;
    if (action.status === 'completed') {
      const count = Array.isArray(action.new_node_ids) ? action.new_node_ids.length : 0;
      if (count === 0) {
        return action.action_type === 'face_search'
          ? 'Completed: no high-probability face matches'
          : 'Completed: no new nodes added';
      }
      return `Completed: ${count} node${count === 1 ? '' : 's'} added`;
    }
    if (action.status === 'failed') {
      return `Failed: ${action.error || 'Action failed'}`;
    }
    return `${this.formatStatusLabel(action.status || 'queued')}: ${label}`;
  }

  get latestActionSubtitle(): string {
    const action = this.latestAction;
    if (!action) return '';
    const sourceLabel = this.findNodeLabel(action.source_node_id);
    const time = action.completed_at || action.started_at;
    return [
      sourceLabel ? `Source: ${sourceLabel}` : null,
      action.query_value || action.image_url || null,
      time ? new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  get latestActionStatusClass(): string {
    const status = String(this.latestAction?.status || 'queued');
    if (status === 'completed') return 'status-completed';
    if (status === 'failed') return 'status-failed';
    if (status === 'warning') return 'status-warning';
    return 'status-processing';
  }

  get selectedNodeActionRows(): Array<{
    label: string;
    status: string;
    nodeCount: number;
    error: string;
    message: string;
  }> {
    const rows = [...this.selectedActionHistory];
    const selectedImageUrl =
      this.selectedEntity?.type === 'image'
        ? this.getNodeQueryValue(this.selectedEntity, 'image')
        : '';
    this.pendingLocalActions.forEach((pendingLocalAction) => {
      if (
        pendingLocalAction &&
        this.selectedEntity?.id &&
        (pendingLocalAction.source_node_id === this.selectedEntity.id ||
          (selectedImageUrl &&
            pendingLocalAction.action_type === 'face_search' &&
            pendingLocalAction.image_url === selectedImageUrl)) &&
        !rows.some((action) => action.action_id === pendingLocalAction.action_id)
      ) {
        rows.push(pendingLocalAction);
      }
    });
    return this.sortActionsByTime(rows)
      .slice(-4)
      .reverse()
      .map((action) => this.toActionStatusRow(action));
  }

  get selectedNodeLatestActionRow():
    | {
      label: string;
      status: string;
      nodeCount: number;
      error: string;
      message: string;
    }
    | null {
    return this.selectedNodeActionRows[0] || null;
  }

  get actionStatusRows(): Array<{ label: string; status: string; nodeCount?: number; error?: string; message?: string }> {
    const history = this.appendPendingActionsToHistory([...this.actionHistory]);
    const expansionRows = this.sortActionsByTime(history)
      .slice(-6)
      .reverse()
      .map((action) => this.toActionStatusRow(action));

    if (this.isDrillDown) {
      return expansionRows;
    }

    const subSearchRows = this.subSearchStatuses.map((item) => ({
      label: item.label,
      status: item.value?.status || 'skipped',
    }));

    if (!expansionRows.length) {
      return subSearchRows;
    }

    return [...expansionRows, ...subSearchRows];
  }

  get creditsCharged(): number {
    const expansionCredits = this.actionHistory.reduce(
      (sum, action) => sum + Number(action.charged_credits || 0),
      0,
    );

    if (this.isDrillDown) {
      return expansionCredits;
    }

    const initialCredits = this.subSearchStatuses.reduce(
      (sum, item) => sum + Number(item.value?.chargedCredits || 0),
      0,
    );

    return initialCredits + expansionCredits;
  }

  get legendItems(): Array<{ type: string; label: string }> {
    const preferred = [
      'phone',
      'email',
      'person',
      'osint_source',
      'social_profile',
      'image',
      'website',
      'leak_source',
      'company',
      'address',
    ];
    const types = new Set(this.nodeTypeOptions);
    return preferred
      .filter((type) => types.has(type))
      .map((type) => ({
        type,
        label: this.formatTypeLabel(type),
      }));
  }

  get starterActionCostLabel(): string {
    return this.formatActionCost('osint_search');
  }

  get aiActionCostLabel(): string {
    const osintCost = this.resolveActionCost('osint_search') || 0;
    const aiCost = this.resolveActionCost('ai_analysis', this.aiMode) || 0;
    const total = osintCost + aiCost;
    if (total > 0) {
      return `${total} credit${total === 1 ? '' : 's'} including OSINT context`;
    }
    return 'Configured AI + OSINT credits';
  }

  get isStarterActionRunning(): boolean {
    if (!this.selectedEntity?.id) return false;
    return this.isActionRunningFor(
      this.selectedEntity.id,
      'osint_search',
      this.starterQueryType,
    );
  }

  get isAiActionRunning(): boolean {
    if (!this.selectedEntity?.id) return false;
    return this.isActionRunningFor(
      this.selectedEntity.id,
      'ai_analysis',
      this.selectedEntity.type,
      this.aiMode,
    );
  }

  get warnings(): string[] {
    const rawWarnings = this.graphPayload?.warnings || this.investigation?.warnings || [];
    return Array.isArray(rawWarnings) ? rawWarnings.map((item) => String(item)) : [];
  }

  get displayUpdatedOn(): string | null {
    return this.investigation?.updated_on || this.investigation?.created_on || null;
  }

  get rootSummaryLabel(): string {
    if (this.isDrillDown) return 'Starter node';
    const type = String(this.investigation?.root_type || 'investigation');
    return `${type.charAt(0).toUpperCase()}${type.slice(1)} root`;
  }

  get subSearchStatuses(): Array<{ key: string; label: string; value: any }> {
    const subSearches = this.investigation?.sub_searches || {};
    return [
      { key: 'osint', label: 'Raw OSINT search', value: subSearches.osint || null },
      { key: 'leak', label: 'Leak search', value: subSearches.leak || null },
      {
        key: 'face_user_photos',
        label: 'Face search from user photos',
        value: subSearches.face_user_photos || null,
      },
      {
        key: 'face_result_images',
        label: 'Face search from result images',
        value: subSearches.face_result_images || null,
      },
    ];
  }

  get nodeTypeOptions(): string[] {
    const types = (this.graphPayload?.nodes || [])
      .map((node: any) => node.type)
      .filter((type: unknown): type is string => typeof type === 'string');
    return Array.from(new Set<string>(types)).sort();
  }

  fetchResult(showLoader = false): void {
    if (showLoader && !this.hasRenderableGraph) {
      this.loading = true;
    }

    this.linkAnalysisService.getResult(this.linkAnalysisId).subscribe({
      next: (response) => {
        const previousSignature = this.getGraphProgressSignature(this.graphPayload);
        this.investigation = response?.data || null;
        this.graphPayload = this.investigation?.graph_payload || null;
        this.notifyManualActionOutcomes();
        const nextSignature = this.getGraphProgressSignature(this.graphPayload);
        this.reconcilePendingAction(nextSignature);
        this.loading = this.isInvestigationProcessing() && !this.hasRenderableGraph;
        this.errorMessage = this.investigation?.error || '';
        this.rebuildDisplayGraph();
        this.rebuildNodeLayers();
        this.ensureDefaultSelection();
        this.updateActionLoadingState();
        const graphChanged = nextSignature !== previousSignature;

        if (this.isInvestigationProcessing()) {
          // Progressive reveal: paint each layer as soon as it lands.
          if (this.hasRenderableGraph && graphChanged) {
            setTimeout(() => this.renderGraph(), 0);
          }
          this.cdr.detectChanges();
          this.schedulePoll();
        } else {
          this.stopPoll();
          if (this.graphPayload?.nodes?.length) {
            setTimeout(() => this.renderGraph(), 0);
          }
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        this.loading = false;
        this.errorMessage =
          error?.error?.message || 'Unable to fetch link analysis result.';
      },
    });
  }

  private getGraphProgressSignature(graph: any): string {
    if (!graph) return '0:0:0';
    const nodes = Array.isArray(graph.nodes) ? graph.nodes : [];
    const edges = Array.isArray(graph.edges) ? graph.edges : [];
    const nodeCount = nodes.length;
    const edgeCount = edges.length;
    const readyLayer = Number(graph.summary?.readyLayer || 0);
    const wave = Number(graph.summary?.autoExpandWave || 0);
    const generatedAt = graph.investigation?.generated_at || graph.summary?.generatedAt || '';
    const nodeIds = nodes.map((node: any) => node?.id || '').sort().join('|');
    const edgeIds = edges
      .map((edge: any) => edge?.id || `${edge?.source || ''}->${edge?.target || ''}:${edge?.type || edge?.label || ''}`)
      .sort()
      .join('|');
    return `${nodeCount}:${edgeCount}:${readyLayer}:${wave}:${generatedAt}:${nodeIds}:${edgeIds}`;
  }

  private isInvestigationProcessing(): boolean {
    const status = (this.investigation?.status || '').toLowerCase();
    const summary = this.investigation?.summary || this.graphPayload?.summary || {};
    const hasServerProcessing = this.actionHistory.some(
      (action) => action.status === 'processing',
    );
    const hasLocalProcessing = this.pendingLocalActions.some(
      (action) => action.status === 'processing',
    );

    // Nothing in flight — stop polling (covers drill-down stuck on stale `processing`).
    if (!hasServerProcessing && !hasLocalProcessing) {
      if (
        status === 'completed' ||
        status === 'complete' ||
        status === 'failed' ||
        status === 'error' ||
        status === 'success'
      ) {
        return false;
      }
      // Drill-down is action-driven; if no actions are running, do not keep polling.
      if (this.isDrillDown) {
        return false;
      }
      // Automated can briefly sit in processing between waves; keep polling only then.
      if (summary.autoExpandComplete || summary.autoExpandStopped) {
        return false;
      }
    }

    // Terminal / success envelope while some action still runs.
    if (
      status === 'completed' ||
      status === 'complete' ||
      status === 'failed' ||
      status === 'error' ||
      status === 'success'
    ) {
      return hasServerProcessing || hasLocalProcessing;
    }

    // Automated expansion finished (or stopped) with no in-flight actions.
    if (
      this.isAutomatedMode &&
      (summary.autoExpandComplete || summary.autoExpandStopped) &&
      !hasServerProcessing
    ) {
      return hasLocalProcessing;
    }

    if (status === 'pending' || status === 'processing') {
      return true;
    }
    return hasServerProcessing || hasLocalProcessing;
  }

  schedulePoll(): void {
    if (this.pollingHandle) {
      clearTimeout(this.pollingHandle);
    }
    // Faster polls once layer 1 is visible so deeper layers appear sooner.
    const delayMs = this.hasRenderableGraph && this.isAutomatedMode ? 2500 : 4000;
    this.pollingHandle = setTimeout(() => this.fetchResult(false), delayMs);
  }

  private stopPoll(): void {
    if (!this.pollingHandle) {
      return;
    }
    clearTimeout(this.pollingHandle);
    this.pollingHandle = null;
  }

  regenerate(): void {
    this.regenerating = true;
    const maxLayer = Number(
      this.investigation?.summary?.autoExpandMaxLayer ||
      this.graphPayload?.summary?.autoExpandMaxLayer ||
      2,
    );
    this.linkAnalysisService.regenerate(this.linkAnalysisId, undefined, maxLayer).subscribe({
      next: () => {
        this.regenerating = false;
        this.errorMessage = '';
        this.selectedEntity = null;
        this.selectedEntityKind = null;
        this.focusedNodeId = null;
        this.graphPayload = null;
        this.investigation = {
          ...(this.investigation || {}),
          status: 'pending',
          graph_payload: null,
          summary: {
            ...(this.investigation?.summary || {}),
            autoExpandMaxLayer: maxLayer,
            autoExpandComplete: false,
            autoExpandStopped: false,
            readyLayer: 0,
          },
        };
        this.toastr.success('We started a fresh graph regeneration.');
        this.fetchResult(true);
      },
      error: (error) => {
        this.regenerating = false;
        this.toastr.error(
          error?.error?.message ||
          'Unable to regenerate link analysis investigation.',
        );
      },
    });
  }

  stopAutomation(): void {
    if (!this.showStopAutomation || this.stoppingAutomation) {
      return;
    }
    this.stoppingAutomation = true;
    this.linkAnalysisService.stopAutomation(this.linkAnalysisId).subscribe({
      next: (response) => {
        this.stoppingAutomation = false;
        this.investigation = response?.data || this.investigation;
        this.graphPayload = this.investigation?.graph_payload || this.graphPayload;
        this.loading = false;
        this.stopPoll();
        this.rebuildDisplayGraph();
        this.rebuildNodeLayers();
        this.toastr.success(
          'Automated expansion stopped. Manual searches are unlocked.',
        );
        this.cdr.detectChanges();
        this.fetchResult(false);
      },
      error: (error) => {
        this.stoppingAutomation = false;
        this.toastr.error(
          error?.error?.message || 'Unable to stop automated expansion.',
        );
      },
    });
  }

  runStarterAction(): void {
    if (
      !this.selectedEntity ||
      (this.selectedEntity.type !== 'starter' && !this.isRootSelected)
    ) {
      return;
    }
    const value = this.starterQueryValue.trim();
    if (!value) {
      this.toastr.error('Enter a value to start the drill-down branch.');
      return;
    }
    this.runAction({
      source_node_id: this.selectedEntity.id,
      action_type: 'osint_search',
      query_type: this.starterQueryType,
      query_value: value,
    });
  }

  onDashboardNodeSelect(node: any): void {
    if (!node) return;
    const resolved = this.resolveSelectedNode(node);
    const isSameNode = this.selectedEntity?.id === resolved?.id;
    this.selectedEntity = resolved;
    this.selectedEntityKind = 'node';
    this.resetSelectedPreviewImageState();
    if (this.pendingAggregatePreselect) {
      this.selectedCollectionValues = [this.pendingAggregatePreselect];
      this.pendingAggregatePreselect = null;
    } else if (!isSameNode) {
      this.detailFieldFocusQuery = null;
    }
    if (!isSameNode) {
      this.resetNodeActionSelection();
    }
    this.cdr.detectChanges();
  }

  /** Aggregate sidebar row → owning source + preselect that value (R12). */
  onAggregateEntryJump(event: {
    sourceNodeId: string;
    value: string;
    valueType?: string;
  }): void {
    const sourceNodeId = String(event?.sourceNodeId || '').trim();
    const value = String(event?.value || '').trim();
    if (!sourceNodeId || !value) return;

    const fromGraph =
      this.graphPayload?.nodes?.find((node: any) => node?.id === sourceNodeId) ||
      this.investigation?.graph?.nodes?.find((node: any) => node?.id === sourceNodeId);
    if (!fromGraph) {
      this.toastr.error('Source node for this aggregate entry was not found.');
      return;
    }

    this.pendingAggregatePreselect = value;
    this.detailFieldFocusQuery = null;
    if (this.graphDashboard) {
      this.graphDashboard.selectNode(sourceNodeId);
      return;
    }

    this.selectedEntity = this.resolveSelectedNode(fromGraph);
    this.selectedEntityKind = 'node';
    this.resetSelectedPreviewImageState();
    this.selectedCollectionValues = [value];
    this.pendingAggregatePreselect = null;
    this.resetNodeActionSelection();
    this.cdr.detectChanges();
  }

  setNodeActionKeySelected(key: string, selected: boolean): void {
    const normalized = String(key || '').trim();
    if (!normalized) return;
    const current = this.selectedNodeActionKeys;
    const hasKey = current.includes(normalized);
    if (selected && !hasKey) {
      this.selectedNodeActionKeys = [...current, normalized];
    } else if (!selected && hasKey) {
      this.selectedNodeActionKeys = current.filter((item) => item !== normalized);
    }
    this.cdr.markForCheck();
  }

  onPrepareDetailFieldSearch(event: {
    queryValue: string;
    queryType: string;
    sourceNodeId?: string;
  }): void {
    const queryValue = String(event?.queryValue || '').trim();
    const queryType = String(event?.queryType || '').trim().toLowerCase();
    if (!queryValue || !queryType) {
      this.toastr.error('Nothing to search.');
      return;
    }

    // Ensure the selected entity matches the detail card node before expanding.
    if (event.sourceNodeId) {
      const fromGraph =
        this.graphPayload?.nodes?.find((node: any) => node?.id === event.sourceNodeId) ||
        this.investigation?.graph?.nodes?.find((node: any) => node?.id === event.sourceNodeId);
      if (fromGraph) {
        this.selectedEntity = this.resolveSelectedNode(fromGraph);
        this.selectedEntityKind = 'node';
      }
    }

    if (!this.selectedEntity?.id && event.sourceNodeId) {
      this.selectedEntity = { id: event.sourceNodeId };
      this.selectedEntityKind = 'node';
    }

    if (!this.selectedEntity?.id) {
      this.toastr.error('Select a graph node before searching from the detail card.');
      return;
    }

    // Preselect the field value; leave SELECT ACTION unchecked for the user.
    this.detailFieldFocusQuery = { queryValue, queryType };
    this.selectedCollectionValues = [queryValue];
    this.selectedNodeActionKeys = [];
    this.cdr.detectChanges();
  }

  onClearDetailFieldSearch(): void {
    this.detailFieldFocusQuery = null;
    this.cdr.markForCheck();
  }

  runDashboardSelectedAction(): void {
    const selectedKeys = this.selectedNodeActionKeys;
    let actions = this.dashboardNodeActions.filter(
      (item) => selectedKeys.includes(item.key) && !item.disabled && !item.running,
    );

    // Detail-field popover may show matrix actions before/without matching nodeActions.
    if (!actions.length && this.detailFieldFocusQuery && selectedKeys.length) {
      const queryType = this.detailFieldFocusQuery.queryType as
        | 'phone'
        | 'email'
        | 'username'
        | 'name'
        | 'image';
      const allowed = getServerActionsForValueType(this.selectedEntity, queryType);
      actions = buildDetailFieldSearchActions(queryType, allowed)
        .filter((action) => selectedKeys.includes(action.key))
        .map((action) => ({
          key: action.key,
          label: action.label,
          actionType: action.actionType,
          queryType: action.queryType,
        }));
    }

    if (!actions.length) {
      this.toastr.error('Select at least one action to run.');
      return;
    }

    let started = 0;
    let attempted = 0;
    for (const action of actions) {
      const queryValues = this.getSelectedValuesForQueryType(action.queryType);
      for (const queryValue of queryValues) {
        attempted += 1;
        if (
          this.runSelectedNodeAction(action, {
            queryValue,
            showSuccessToast: false,
            showErrorToast: true,
          })
        ) {
          started += 1;
        }
      }
    }

    if (started > 0) {
      this.detailFieldFocusQuery = null;
      this.toastr.success(
        `Started ${started} search action${started === 1 ? '' : 's'}. The graph will update automatically.`,
      );
      return;
    }

    // Specific failure toasts already shown by runSelectedNodeAction when attempted > 0.
    if (attempted === 0) {
      this.toastr.error('Unable to start the selected searches.');
    }
  }

  runSelectedNodeAction(
    action: { actionType: any; queryType: any },
    options: { queryValue?: string; showSuccessToast?: boolean; showErrorToast?: boolean } = {},
  ): boolean {
    if (!this.selectedEntity) return false;
    const showErrorToast = options.showErrorToast !== false;
    const hasExplicitQueryValue = Boolean(options.queryValue?.trim());
    if (!hasExplicitQueryValue && this.isSearchValueSelectionBlocked(action)) {
      const label =
        this.selectedEntity.type === 'leak_source' || this.selectedEntity.type === 'osint_source'
          ? 'value'
          : this.selectedCollectionTypeLabel;
      if (showErrorToast) {
        this.toastr.error(`Select at least one ${label} before searching.`);
      }
      return false;
    }
    const queryValue = options.queryValue || this.getSelectedNodeQueryValue(action.queryType);
    if (isSearchCompleted(this.actionHistory, {
      source_node_id: this.selectedEntity.id, action_type: action.actionType,
      query_type: action.queryType, query_value: queryValue,
      image_url: action.queryType === 'image' ? queryValue : undefined,
    })) {
      if (showErrorToast) this.toastr.info('This search is already completed.');
      return false;
    }
    if (!queryValue) {
      this.registerLocalActionFailure(
        action,
        'This node does not contain a searchable value.',
      );
      if (showErrorToast) {
        this.toastr.error('This node does not contain a searchable value.');
      }
      return false;
    }
    if (action.queryType === 'image' && !this.isValidImageSearchUrl(queryValue)) {
      this.registerLocalActionFailure(
        action,
        'This image node does not contain a valid image URL for face search.',
        { image_url: queryValue },
      );
      if (showErrorToast) {
        this.toastr.error('This image node does not contain a valid image URL for face search.');
      }
      return false;
    }
    if (action.actionType === 'face_search' && !this.canRunFaceSearchForImage(queryValue, this.selectedEntity)) {
      this.registerLocalActionFailure(
        action,
        'Face search is available only for images that contain a human face.',
        { image_url: queryValue },
      );
      if (showErrorToast) {
        this.toastr.error('Face search is available only for images that contain a human face.');
      }
      return false;
    }
    this.runAction(
      {
        source_node_id: this.selectedEntity.id,
        action_type: action.actionType,
        query_type: action.queryType,
        ...(action.queryType === 'image'
          ? { image_url: queryValue }
          : { query_value: queryValue }),
      },
      options,
    );
    return true;
  }

  runSelectedNodeActionFromEvent(
    event: Event,
    action: { actionType: any; queryType: any },
  ): void {
    event.preventDefault();
    event.stopPropagation();
    if ('stopImmediatePropagation' in event) {
      (event as any).stopImmediatePropagation();
    }
    this.runSelectedNodeAction(action);
  }

  onDetailFieldSearch(event: {
    queryValue: string;
    sourceNodeId?: string;
    actions: Array<{ actionType: string; queryType: string }>;
  }): void {
    const queryValue = String(event?.queryValue || '').trim();
    const actions = Array.isArray(event?.actions) ? event.actions : [];
    if (!queryValue || !actions.length) {
      this.toastr.error('Nothing to search.');
      return;
    }

    // Ensure the selected entity matches the detail card node before expanding.
    if (event.sourceNodeId) {
      const fromGraph =
        this.graphPayload?.nodes?.find((node: any) => node?.id === event.sourceNodeId) ||
        this.investigation?.graph?.nodes?.find((node: any) => node?.id === event.sourceNodeId);
      if (fromGraph) {
        this.selectedEntity = this.resolveSelectedNode(fromGraph);
        this.selectedEntityKind = 'node';
      }
    }

    if (!this.selectedEntity?.id && event.sourceNodeId) {
      this.selectedEntity = { id: event.sourceNodeId };
      this.selectedEntityKind = 'node';
    }

    if (!this.selectedEntity?.id) {
      this.toastr.error('Select a graph node before searching from the detail card.');
      return;
    }

    let started = 0;
    for (const action of actions) {
      if (
        this.runSelectedNodeAction(
          { actionType: action.actionType, queryType: action.queryType },
          {
            queryValue,
            showSuccessToast: false,
            showErrorToast: true,
          },
        )
      ) {
        started += 1;
      }
    }

    if (started > 0) {
      this.toastr.success(
        `Started ${started} search action${started === 1 ? '' : 's'} for ${queryValue}.`,
      );
      return;
    }

    this.toastr.error(`Unable to start search for ${queryValue}.`);
  }

  onSelectedPreviewImageError(): void {
    this.selectedPreviewImageFailed = true;
    this.cdr.detectChanges();
  }

  private resetSelectedPreviewImageState(): void {
    this.selectedPreviewImageFailed = false;
  }

  formatInspectorActionLabel(action: { label: string }): string {
    return action.label;
  }

  formatPopoverActionLabel(action: {
    label: string;
    actionType: string;
    queryType: string;
  }): string {
    if (action.actionType === 'osint_search') {
      return `OSINT ${action.queryType}`;
    }
    if (action.actionType === 'leak_search') {
      return `Leak ${action.queryType}`;
    }
    if (action.actionType === 'face_search') {
      return 'Face search';
    }
    return this.formatInspectorActionLabel(action);
  }

  closeNodeActionPopover(): void {
    this.nodeActionPopoverVisible = false;
    this.nodeActionPopoverNodeId = null;
    this.nodeActionPopoverPinned = false;
    this.stopNodeActionPopoverDrag();
    this.cdr.detectChanges();
  }

  onNodeActionPopoverHeaderPointerDown(event: PointerEvent): void {
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.stopNodeActionPopoverDrag();
    this.nodeActionPopoverPinned = true;
    this.nodeActionPopoverDragging = true;
    this.nodeActionPopoverDragState = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      originX: this.nodeActionPopoverX,
      originY: this.nodeActionPopoverY,
    };

    this.nodeActionPopoverDragMoveHandler = (moveEvent: PointerEvent) => {
      if (
        !this.nodeActionPopoverDragState ||
        moveEvent.pointerId !== this.nodeActionPopoverDragState.pointerId
      ) {
        return;
      }
      const dx = moveEvent.clientX - this.nodeActionPopoverDragState.startClientX;
      const dy = moveEvent.clientY - this.nodeActionPopoverDragState.startClientY;
      this.setNodeActionPopoverPosition(
        this.nodeActionPopoverDragState.originX + dx,
        this.nodeActionPopoverDragState.originY + dy,
      );
      this.cdr.detectChanges();
    };

    this.nodeActionPopoverDragEndHandler = (endEvent: PointerEvent) => {
      if (
        !this.nodeActionPopoverDragState ||
        endEvent.pointerId !== this.nodeActionPopoverDragState.pointerId
      ) {
        return;
      }
      this.stopNodeActionPopoverDrag();
      this.cdr.detectChanges();
    };

    document.addEventListener('pointermove', this.nodeActionPopoverDragMoveHandler);
    document.addEventListener('pointerup', this.nodeActionPopoverDragEndHandler);
    document.addEventListener('pointercancel', this.nodeActionPopoverDragEndHandler);
  }

  private stopNodeActionPopoverDrag(): void {
    if (this.nodeActionPopoverDragMoveHandler) {
      document.removeEventListener('pointermove', this.nodeActionPopoverDragMoveHandler);
      this.nodeActionPopoverDragMoveHandler = null;
    }
    if (this.nodeActionPopoverDragEndHandler) {
      document.removeEventListener('pointerup', this.nodeActionPopoverDragEndHandler);
      document.removeEventListener('pointercancel', this.nodeActionPopoverDragEndHandler);
      this.nodeActionPopoverDragEndHandler = null;
    }
    this.nodeActionPopoverDragState = null;
    this.nodeActionPopoverDragging = false;
  }

  private setNodeActionPopoverPosition(x: number, y: number): void {
    const popoverWidth = 196;
    const popoverHeight = 148;
    this.nodeActionPopoverX = Math.min(
      Math.max(12, x),
      window.innerWidth - popoverWidth - 12,
    );
    this.nodeActionPopoverY = Math.min(
      Math.max(12, y),
      window.innerHeight - popoverHeight - 12,
    );
  }

  filterGraphBySelectedNode(): void {
    if (!this.selectedEntity) return;
    const value = this.getSelectedNodeFilterValue();
    if (!value) {
      this.toastr.info('This node does not have a value to filter the graph.');
      return;
    }
    this.graphSearchTerm = value;
    this.onVisibilityChange();
    this.toastr.success('Graph filtered to matching nodes.');
  }

  clearGraphSearchFilter(): void {
    if (!this.graphSearchTerm) return;
    this.graphSearchTerm = '';
    this.onVisibilityChange();
  }

  formatActionCost(actionType: string, aiMode?: string): string {
    const cost = this.resolveActionCost(actionType, aiMode);
    if (typeof cost === 'number') {
      return `${cost} credit${cost === 1 ? '' : 's'}`;
    }
    return 'Configured credits';
  }

  private resolveActionCost(actionType: string, aiMode?: string): number | null {
    const key =
      actionType === 'leak_search'
        ? 'leakSearchCost'
        : actionType === 'face_search'
          ? 'searchCreditCount'
          : actionType === 'ai_analysis'
            ? aiMode === 'deep'
              ? 'aiAnalysisDeepCreditCost'
              : aiMode === 'deep-cheap'
                ? 'aiAnalysisDeepCheapCreditCost'
                : 'aiAnalysisQuickCreditCost'
            : 'osintSearchCost';

    return this.searchServiceStatusService.resolveCreditCost(this.creditCosts, key);
  }

  runAiAnalysis(): void {
    if (!this.canRunAiForNode(this.selectedEntity)) {
      return;
    }
    const queryValue = this.getSelectedNodeQueryValue(this.selectedEntity.type);
    if (!queryValue) {
      this.toastr.error('This node does not contain a full phone or email value.');
      return;
    }
    this.runAction({
      source_node_id: this.selectedEntity.id,
      action_type: 'ai_analysis',
      query_type: this.selectedEntity.type,
      query_value: queryValue,
      ai_mode: this.aiMode,
    });
  }

  isActionButtonRunning(action: { actionType: any; queryType: any }): boolean {
    if (!this.selectedEntity?.id) return false;
    return this.isActionRunningFor(
      this.selectedEntity.id,
      action.actionType,
      action.queryType,
    );
  }

  private runAction(
    payload: any,
    options: { showSuccessToast?: boolean; showErrorToast?: boolean } = {},
  ): void {
    if (isSearchCompleted(this.actionHistory, payload)) {
      if (options.showErrorToast !== false) this.toastr.info('This search is already completed.');
      return;
    }
    if (this.isAutomatedExpansionLocked) {
      this.toastr.warning(
        'Manual search is locked until automated layer expansion finishes.',
      );
      return;
    }
    const actionKey = this.makeActionKey(payload);
    if (payload.query_type === 'name' && !isActionableNameValue(payload.query_value)) {
      this.toastr.error('This name is incomplete or masked. Choose a complete name to search.');
      return;
    }
    const localActionId = `local:${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    this.runningActionKeys.add(actionKey);
    const localAction = {
      ...payload,
      action_id: localActionId,
      status: 'processing',
      charged_credits: 0,
      new_node_ids: [],
      new_edge_ids: [],
      warnings: [],
      error: null,
      started_at: new Date().toISOString(),
      completed_at: null,
      poll_until: Date.now() + this.pendingActionPollingGraceMs,
      initial_graph_signature: this.getGraphProgressSignature(this.graphPayload),
    };
    this.pendingLocalActions.push(localAction);
    this.updateActionLoadingState();
    setTimeout(() => this.renderGraph(), 0);
    this.linkAnalysisService.runAction(this.linkAnalysisId, payload).subscribe({
      next: (response) => {
        this.runningActionKeys.delete(actionKey);
        const actionId = response?.data?.action_id;
        if (actionId) this.trackedManualActionIds.add(actionId);
        const pending = this.pendingLocalActions.find((action) => action.action_id === localActionId);
        if (pending && actionId) {
          pending.action_id = actionId;
        }
        this.updateActionLoadingState();
        if (options.showSuccessToast !== false) {
          this.toastr.success('Action started. The graph will update automatically.');
        }
        this.fetchResult(false);
      },
      error: (error) => {
        this.runningActionKeys.delete(actionKey);
        const pending = this.pendingLocalActions.find((action) => action.action_id === localActionId);
        if (pending) {
          pending.status = 'failed';
          pending.error =
            error?.error?.message_key ||
            error?.error?.message ||
            'Unable to run this link-analysis action.';
          pending.completed_at = new Date().toISOString();
        }
        this.updateActionLoadingState();
        setTimeout(() => this.renderGraph(), 0);
        if (options.showErrorToast !== false) {
          this.toastr.error(
            error?.error?.message_key ||
            error?.error?.message ||
            'Unable to run this link-analysis action.',
          );
        }
      },
    });
  }

  private notifyManualActionOutcomes(): void {
    this.actionHistory.forEach((action) => {
      if (!this.trackedManualActionIds.has(action.action_id)) return;
      if (!['completed', 'failed', 'cancelled'].includes(action.status)) return;
      this.trackedManualActionIds.delete(action.action_id);
      const label = this.formatActionLabel(action);
      if (action.status === 'failed') {
        this.toastr.error(`${label}: ${action.error || 'The search failed. Please try again.'}`);
      } else if (action.status === 'completed' && !action.new_node_ids?.length) {
        this.toastr.info(`${label}: ${action.warnings?.[0] || 'No new results were found.'}`);
      }
    });
  }

  private updateActionLoadingState(): void {
    this.actionLoading =
      this.runningActionKeys.size > 0 ||
      this.pendingLocalActions.some((action) => action.status === 'processing');
    this.cdr.detectChanges();
    if (!this.actionLoading) {
      setTimeout(() => this.renderGraph(), 0);
    }
  }

  private getSelectedNodeQueryValue(queryType: string): string {
    const values = this.getSelectedValuesForQueryType(queryType);
    return values[0] || '';
  }

  private getCollectionValueOptions(
    node: any = this.selectedEntity,
  ): Array<{ value: string; type: string; label: string }> {
    if (!node) return [];

    // Aggregates are view-only (R10–R12) — never selectable search values.
    if (isAggregateHubNode(node) || node.metadata?.isAggregateCollection) {
      return [];
    }

    const sourceOptions = this.getSourceSearchValueOptions(node);
    if (sourceOptions.length > 1) {
      return sourceOptions;
    }

    const searchableValues = getNodeSearchableValues(node);
    const options: Array<{ value: string; type: string; label: string }> = [];
    const pushValues = (type: string, label: string, values: string[]) => {
      values.forEach((value) => {
        const normalized = String(value || '').trim();
        if (!normalized) return;
        if (type === 'email' && !isActionableEmailValue(normalized)) return;
        if (type === 'image' && !this.canRunFaceSearchForImage(normalized, node)) return;
        options.push({
          value: normalized,
          type,
          label: type === 'image' ? 'Photo' : `${normalized} (${label})`,
        });
      });
    };
    pushValues('phone', 'phone', searchableValues.phone || []);
    pushValues('email', 'email', searchableValues.email || []);
    pushValues('username', 'username', searchableValues.username || []);
    pushValues('name', 'name', searchableValues.name || []);
    pushValues('image', 'image', searchableValues.image || []);
    if (options.length > 1) {
      return options;
    }

    return [];
  }

  private getSelectedValuesForQueryType(queryType: string): string[] {
    if (!this.selectedEntity) return [];
    if (
      this.detailFieldFocusQuery &&
      this.detailFieldFocusQuery.queryType === String(queryType || '').toLowerCase() &&
      this.detailFieldFocusQuery.queryValue
    ) {
      return [this.detailFieldFocusQuery.queryValue];
    }
    const options = this.getCollectionValueOptions();
    if (options.length > 1) {
      return options
        .filter(
          (option) =>
            option.type === queryType && this.selectedCollectionValues.includes(option.value),
        )
        .map((option) => option.value);
    }
    const single = this.getNodeQueryValue(this.selectedEntity, queryType);
    if (single) return [single];
    // Single-photo face_image nodes may lack collection options; still run from node fields.
    if (
      queryType === 'image' &&
      String(this.selectedEntity.type || '').toLowerCase() === 'face_image'
    ) {
      const fallback = this.resolveFaceImageFallbackUrl(this.selectedEntity);
      return fallback ? [fallback] : [];
    }
    return [];
  }

  /** Prefer searchable http(s) / auth-gated OSINT image URLs on a face_image node. */
  private resolveFaceImageFallbackUrl(node: any): string {
    if (!node) return '';
    const metadata = node.metadata || {};
    const searchable = getNodeSearchableValues(node).image || [];
    const candidates = [
      node.imageUrl,
      node.image_url,
      metadata.canonicalUrl,
      metadata.canonicalImage,
      metadata.queryValue,
      metadata.imageUrl,
      metadata.image_url,
      searchable[0],
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    return (
      candidates.find((value) => this.isValidImageSearchUrl(value)) ||
      candidates[0] ||
      ''
    );
  }

  private getNodeQueryValue(node: any, queryType: string): string {
    if (!node) return '';
    if (isAggregateHubNode(node) || node.metadata?.isAggregateCollection) {
      const values = getAggregateCollectedValues(node);
      if (this.selectedCollectionValues.length) {
        const selected = values.filter((value: string) =>
          this.selectedCollectionValues.includes(String(value)),
        );
        if (selected.length) return selected[0];
      }
      if (queryType === 'image') {
        return values.find((value: string) => this.isValidImageSearchUrl(String(value))) || '';
      }
      if (queryType === 'email') {
        return values.find((value: string) => isActionableEmailValue(value)) || '';
      }
      if (queryType === 'phone') {
        return (
          values.find((value: string) => {
            const digits = String(value).replace(/\D/g, '');
            return digits.length >= 10 && digits.length <= 15;
          }) || ''
        );
      }
      if (queryType === 'username') {
        return values.find((value: string) => String(value).trim().length >= 2) || '';
      }
      if (queryType === 'name') {
        return (
          values.find(
            (value: string) => String(value).trim().length >= 2 && !isEmailHintValue(value),
          ) || ''
        );
      }
      return '';
    }
    if (node.type === 'leak_source' || node.type === 'osint_source' || this.isOsintPlatformSourceNode(node)) {
      const values = this.sourceValuesForQueryType(node, queryType);
      if (this.selectedCollectionValues.length) {
        const selected = values.filter((value: string) =>
          this.selectedCollectionValues.includes(String(value)),
        );
        if (selected.length) return selected[0];
      }
      if (queryType === 'image') {
        return values.find((value: string) => this.isValidImageSearchUrl(String(value))) || '';
      }
      if (queryType === 'email') {
        return values.find((value: string) => isActionableEmailValue(value)) || '';
      }
      if (queryType === 'phone') {
        return (
          values.find((value: string) => {
            const digits = String(value).replace(/\D/g, '');
            return digits.length >= 10 && digits.length <= 15;
          }) || ''
        );
      }
      if (queryType === 'username') {
        return values.find((value: string) => String(value).trim().length >= 2) || '';
      }
      if (queryType === 'name') {
        return (
          values.find(
            (value: string) => String(value).trim().length >= 2 && !isEmailHintValue(value),
          ) || ''
        );
      }
      return '';
    }
    if (queryType === 'image') {
      const searchableImages = getNodeSearchableValues(node).image || [];
      if (this.selectedCollectionValues.length) {
        const selected = searchableImages.find((value) =>
          this.selectedCollectionValues.includes(String(value)),
        );
        if (selected) return String(selected);
      }
      const fromSearchable = searchableImages.find((value) =>
        this.isValidImageSearchUrl(String(value)),
      );
      if (fromSearchable) return String(fromSearchable);
      return this.getNodeImageSearchUrl(node);
    }
    if (queryType === 'name' || node.type === 'person') {
      return String(node.metadata?.queryValue || node.label || '').trim();
    }
    if (queryType === 'username') {
      return (
        node.metadata?.handle ||
        node.metadata?.usernames?.[0] ||
        String(node.label || '').replace(/^@/, '')
      ).trim();
    }
    return String(node.label || '').trim();
  }

  setCollectionValueSelected(value: string, selected: boolean): void {
    const normalized = String(value || '').trim();
    if (!normalized) return;
    const current = this.selectedCollectionValues;
    const hasValue = current.includes(normalized);
    if (selected && !hasValue) {
      this.selectedCollectionValues = [...current, normalized];
    } else if (!selected && hasValue) {
      this.selectedCollectionValues = current.filter((item) => item !== normalized);
    }
    this.cdr.markForCheck();
  }

  private resolveCollectionSelection(node: any): void {
    const options = this.getCollectionValueOptions(node);
    if (options.length === 1) {
      this.selectedCollectionValues = [options[0].value];
      return;
    }
    if (options.length > 1) {
      const optionValues = options.map((option) => option.value);
      const preserved = this.selectedCollectionValues.filter((value) => optionValues.includes(value));
      this.selectedCollectionValues = preserved.length ? preserved : optionValues;
      return;
    }
    this.selectedCollectionValues = [];
  }

  private resetNodeActionSelection(): void {
    const actions = this.dashboardNodeActions;
    this.selectedNodeActionKeys = actions
      .filter((action) => !action.disabled && !action.running)
      .map((action) => action.key);
  }

  formatInspectorDetailValue(detail: any): string {
    if (!detail) return '';
    if (detail.type === 'boolean') return detail.value ? 'Yes' : 'No';
    if (detail.type === 'list' && Array.isArray(detail.value)) {
      return detail.value.map((item: any) => String(item)).join(', ');
    }
    if (detail.value === null || detail.value === undefined || detail.value === '') {
      return 'N/A';
    }
    return String(detail.value);
  }

  onSourceDetailIconError(event: Event): void {
    const image = event.target as HTMLImageElement | null;
    if (image) {
      image.style.display = 'none';
    }
  }

  private getNodeImageSearchUrl(node: any): string {
    const metadata = node?.metadata || {};
    const searchableImages = getNodeSearchableValues(node).image || [];
    const sourceRefs = Array.isArray(node?.sourceReferences)
      ? node.sourceReferences
      : [];
    const candidateValues = [
      ...searchableImages,
      node?.imageUrl,
      node?.image_url,
      node?.url,
      node?.thumbnail,
      metadata?.logo,
      metadata?.previewImageUrl,
      metadata?.sourceImageUrl,
      metadata?.imageUrl,
      metadata?.image_url,
      metadata?.url,
      metadata?.thumbnail,
      metadata?.sourceUrl,
      metadata?.originalImageRef,
      metadata?.original_image_ref,
      metadata?.sourceData?.logo,
      metadata?.leakData?.logo,
      ...(Array.isArray(metadata?.urls) ? metadata.urls : []),
      ...sourceRefs.map((ref: any) => ref?.url),
    ]
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);

    const validImageUrl = candidateValues.find((value) => this.isValidImageSearchUrl(value));
    if (validImageUrl) return validImageUrl;

    return candidateValues[0] || '';
  }

  private registerLocalActionFailure(
    action: { actionType: any; queryType: any },
    error: string,
    extra: Record<string, any> = {},
  ): void {
    if (!this.selectedEntity?.id) return;
    const now = new Date().toISOString();
    this.pendingLocalActions.push({
      source_node_id: this.selectedEntity.id,
      action_type: action.actionType,
      query_type: action.queryType,
      query_value:
        action.queryType === 'image'
          ? null
          : this.getSelectedNodeQueryValue(action.queryType) || null,
      image_url:
        action.queryType === 'image'
          ? extra['image_url'] || this.getSelectedNodeQueryValue('image') || null
          : null,
      action_id: `local:${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      status: 'failed',
      charged_credits: 0,
      new_node_ids: [],
      new_edge_ids: [],
      warnings: [],
      error,
      started_at: now,
      completed_at: now,
      ...extra,
    });
    this.updateActionLoadingState();
    setTimeout(() => this.renderGraph(), 0);
  }

  get selectedNodeFilterValue(): string {
    return this.getSelectedNodeFilterValue();
  }

  get selectedEntityPreviewImageUrl(): string {
    if (!this.selectedEntity) return '';
    if (this.isInspectableSourceNode(this.selectedEntity)) {
      return '';
    }
    return this.getNodeImageSearchUrl(this.selectedEntity);
  }

  getInspectorPlatformIcon(platform: string | null | undefined): string {
    return createPlatformIconDataUrl(platform);
  }

  get selectedCanRunAi(): boolean {
    return this.canRunAiForNode(this.selectedEntity);
  }

  private canRunAiForNode(node: any): boolean {
    return Boolean(
      node &&
      ((node.type === 'phone' && this.isCompletePhoneNode(node)) ||
        (node.type === 'email' && this.isCompleteEmailNode(node))),
    );
  }

  private hasAvailableActions(node: any): boolean {
    if (!node) return false;
    if (isSourceTypeHubNode(node)) return false;
    if (node.type === 'starter') return this.isDrillDown;
    return this.getNodeActions(node).length > 0 || this.canRunAiForNode(node);
  }

  private getActionCount(node: any): number {
    if (!node) return 0;
    if (node.type === 'starter') return this.isDrillDown ? 3 : 0;
    return this.getNodeActions(node).length + (this.canRunAiForNode(node) ? 1 : 0);
  }

  private isCompleteEmailNode(node: any): boolean {
    const value = this.getNodeQueryValue(node, 'email');
    return isActionableEmailValue(value);
  }

  private isCompletePhoneNode(node: any): boolean {
    const value = this.getNodeQueryValue(node, 'phone');
    const digits = value.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  }

  goBack(): void {
    this.router.navigate(['/link-analysis']);
  }

  private setGraphFullscreenOpen(open: boolean): void {
    const changed = this.graphFullscreenOpen !== open;
    this.graphFullscreenOpen = open;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = open ? 'hidden' : '';
    }
    if (!changed) return;
    this.cdr.detectChanges();
    this.scheduleGraphLayoutRefresh();
  }

  private scheduleGraphLayoutRefresh(): void {
    if (this.fitGraphTimer) {
      clearTimeout(this.fitGraphTimer);
    }
    this.fitGraphTimer = setTimeout(() => {
      this.fitGraphTimer = null;
      this.cy?.resize();
      this.fitVisibleGraph();
      if (this.nodeActionPopoverNodeId && this.cy) {
        const node = this.cy.getElementById(this.nodeActionPopoverNodeId);
        if (node?.length) {
          this.updateNodeActionPopoverPosition(node);
          this.cdr.detectChanges();
        }
      }
    }, 60);
  }

  onFiltersChanged(): void {
    this.renderGraph();
  }

  focusNeighbors(): void {
    if (!this.selectedEntity || this.selectedEntityKind !== 'node') return;
    this.focusedNodeId = this.selectedEntity.id;
    this.renderGraph();
  }

  resetFocus(): void {
    this.focusedNodeId = null;
    this.renderGraph();
  }

  setGraphViewMode(mode: 'hierarchical' | 'layered'): void {
    if (this.graphViewMode === mode) return;
    this.graphViewMode = mode;
    this.focusedNodeId = null;
    this.renderGraph();
  }

  selectLayerFilter(layerIndex: number): void {
    const layer = Number(layerIndex);
    if (this.visibleUpTo === layer && layer > 0) {
      this.visibleUpTo = layer - 1;
    } else {
      this.visibleUpTo = layer;
    }
    this.onVisibilityChange();
  }

  isLayerFilterActive(layerIndex: number): boolean {
    const layer = Number(layerIndex);
    return this.visibleUpTo < 0 || this.visibleUpTo >= layer;
  }

  toggleLevel(layerIndex: number): void {
    this.selectLayerFilter(layerIndex);
  }

  isLevelActive(layerIndex: number): boolean {
    return this.isLayerFilterActive(layerIndex);
  }

  selectNodeType(type: string): void {
    if (this.nodeTypeFilter !== type) {
      this.nodeTypeFilter = type;
    } else {
      this.nodeTypeFilter = 'all';
    }
    this.onVisibilityChange();
  }

  /** @deprecated Use selectNodeType */
  toggleNodeType(type: string): void {
    this.selectNodeType(type);
  }

  isNodeTypeActive(type: string): boolean {
    return this.nodeTypeFilter === type;
  }

  expandAllLevels(): void {
    this.visibleUpTo = this.maxGraphLayer;
    this.onVisibilityChange();
  }

  collapseToRoot(): void {
    this.visibleUpTo = 0;
    this.onVisibilityChange();
  }

  expandToLayer(layer: number): void {
    this.visibleUpTo = Number(layer);
    this.onVisibilityChange();
    this.closeInspector();
  }

  clearGraphFilters(): void {
    this.visibleUpTo = this.maxGraphLayer;
    this.nodeTypeFilter = 'all';
    this.onVisibilityChange();
  }

  onVisibilityChange(): void {
    this.focusedNodeId = null;
    if (!this.graphCanvas?.nativeElement || !this.graphPayload?.nodes?.length) {
      this.cdr.detectChanges();
      return;
    }

    if (this.cy) {
      this.applyGraphFilter();
    } else {
      this.renderGraph();
    }
    this.cdr.detectChanges();
  }

  private applyGraphFilter(shouldFit = true): void {
    if (!this.cy) return;

    this.clearSelectionIfFilteredOut();
    const visibleIds = new Set(this.filterDisplayNodes().map((node: any) => node.id));
    const rootId = this.rootNode?.id;
    if (!visibleIds.size && rootId) {
      visibleIds.add(rootId);
    }

    this.cy.startBatch();
    try {
      this.cy.nodes().forEach((ele: any) => {
        const data = ele.data();
        const id = ele.id();

        if (data.isLayerRing) {
          if (id.startsWith('layer-ring:')) {
            const ringNumber = Number(id.split(':')[1]);
            this.setGraphElementVisibility(
              ele,
              this.shouldShowLayerRing(ringNumber, visibleIds),
            );
            return;
          }
          if (id.startsWith('layer-ring-label:')) {
            const ringNumber = Number(id.split(':')[1]);
            this.setGraphElementVisibility(
              ele,
              this.shouldShowLayerRing(ringNumber, visibleIds),
            );
            return;
          }
          if (id.startsWith('sector-label:')) {
            const showSectors =
              this.visibleUpTo < 0 || this.visibleUpTo >= 2
                ? Array.from(visibleIds).some(
                  (nodeId) =>
                    nodeId !== rootId && this.getNodeLayer({ id: nodeId }) >= 2,
                )
                : false;
            this.setGraphElementVisibility(ele, showSectors);
            return;
          }
        }

        if (data.isRootHalo) {
          this.setGraphElementVisibility(
            ele,
            Boolean(rootId && visibleIds.has(rootId)),
          );
          return;
        }

        if (data.isActionBadge) {
          this.setGraphElementVisibility(
            ele,
            Boolean(data.parentNodeId && visibleIds.has(data.parentNodeId)),
          );
          return;
        }

        this.setGraphElementVisibility(ele, visibleIds.has(id));
      });

      this.cy.edges().forEach((edge: any) => {
        const show =
          !edge.source().hidden() &&
          !edge.target().hidden() &&
          visibleIds.has(edge.source().id()) &&
          visibleIds.has(edge.target().id());
        this.setGraphElementVisibility(edge, show);
      });
    } finally {
      this.cy.endBatch();
    }

    if (this.isLayeredView) {
      this.applyLayeredSelectionFocus(this.getFocusedNodeIdForLayeredView());
    }

    if (shouldFit) {
      this.scheduleFitVisibleGraph();
    }
  }

  private setGraphElementVisibility(ele: any, visible: boolean): void {
    if (visible) {
      ele.show();
    } else {
      ele.hide();
    }
  }

  private shouldShowLayerRing(ringNumber: number, visibleIds: Set<string>): boolean {
    if (this.visibleUpTo >= 0 && ringNumber > this.visibleUpTo) {
      return false;
    }
    const rootId = this.rootNode?.id;
    const nodes = this.graphPayload?.nodes || [];
    return nodes.some((node: any) => {
      if (!visibleIds.has(node.id) || node.id === rootId) return false;
      return this.getNodeLayer(node) === ringNumber;
    });
  }

  private clearSelectionIfFilteredOut(): void {
    if (!this.selectedEntity?.id || this.selectedEntityKind !== 'node') return;
    const visibleIds = new Set(this.filterNodes().map((node: any) => node.id));
    if (!visibleIds.has(this.selectedEntity.id)) {
      this.selectedEntity = null;
      this.selectedEntityKind = null;
      this.selectedNodeIds.clear();
      this.showInspectorActions = false;
    }
  }

  private getFocusedNodeIdForLayeredView(): string | null {
    if (this.selectedEntityKind !== 'node' || !this.selectedEntity?.id) return null;
    const visibleIds = new Set(this.filterNodes().map((node: any) => node.id));
    return visibleIds.has(this.selectedEntity.id) ? this.selectedEntity.id : null;
  }

  private isNodeVisibleInGraph(nodeId: string | null | undefined): boolean {
    if (!nodeId) return false;
    return this.filterNodes().some((node: any) => node.id === nodeId);
  }

  zoomGraph(factor: number): void {
    if (!this.cy) return;
    const zoom = Math.max(0.2, Math.min(6, (this.cy.zoom() || 1) * factor));
    this.cy.zoom(zoom);
  }

  toggleMultiSelectMode(): void {
    this.multiSelectMode = !this.multiSelectMode;
    this.cdr.detectChanges();
  }

  clearNodeSelection(): void {
    this.selectedNodeIds.clear();
    this.cy?.nodes(':selected').unselect();
    this.groupDragState = null;
    this.updateMultiSelectionState();
  }

  toggleCanvasDock(): void {
    this.canvasDocked = !this.canvasDocked;
    this.syncGraphAfterInspectorLayout();
    this.cdr.detectChanges();
  }

  toggleGraphSidebar(): void {
    this.graphSidebarExpanded = !this.graphSidebarExpanded;
    setTimeout(() => {
      this.cy?.resize();
      this.fitVisibleGraph();
    }, 260);
    this.cdr.detectChanges();
  }

  collapseInspector(): void {
    this.canvasDocked = true;
    this.syncGraphAfterInspectorLayout();
    this.cdr.detectChanges();
  }

  openInspector(): void {
    this.canvasDocked = false;
    this.syncGraphAfterInspectorLayout();
    this.cdr.detectChanges();
  }

  setInspectorDockSide(side: 'left' | 'right'): void {
    if (this.inspectorDockSide === side) return;
    this.inspectorDockSide = side;
    this.inspectorDockPreview = null;
    this.syncGraphAfterInspectorLayout();
    this.cdr.detectChanges();
  }

  onInspectorHeaderPointerDown(event: PointerEvent): void {
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    event.preventDefault();
    this.stopInspectorDrag();
    this.inspectorDragging = true;
    this.inspectorDragState = {
      pointerId: event.pointerId,
      mode: 'dock',
      startClientX: event.clientX,
      startWidth: this.inspectorWidth,
      startDockSide: this.inspectorDockSide,
    };

    this.inspectorDragMoveHandler = (moveEvent: PointerEvent) => {
      if (
        !this.inspectorDragState ||
        moveEvent.pointerId !== this.inspectorDragState.pointerId ||
        this.inspectorDragState.mode !== 'dock'
      ) {
        return;
      }
      const mainRect = this.rynoMain?.nativeElement?.getBoundingClientRect();
      if (!mainRect) return;
      const midpoint = mainRect.left + mainRect.width / 2;
      this.inspectorDockPreview = moveEvent.clientX < midpoint ? 'left' : 'right';
      this.cdr.detectChanges();
    };

    this.inspectorDragEndHandler = (endEvent: PointerEvent) => {
      if (
        !this.inspectorDragState ||
        endEvent.pointerId !== this.inspectorDragState.pointerId
      ) {
        return;
      }
      if (this.inspectorDockPreview) {
        this.inspectorDockSide = this.inspectorDockPreview;
      }
      this.stopInspectorDrag();
      this.syncGraphAfterInspectorLayout();
      this.cdr.detectChanges();
    };

    window.addEventListener('pointermove', this.inspectorDragMoveHandler);
    window.addEventListener('pointerup', this.inspectorDragEndHandler);
    window.addEventListener('pointercancel', this.inspectorDragEndHandler);
  }

  onInspectorResizePointerDown(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.stopInspectorDrag();
    this.inspectorDragging = true;
    this.inspectorDragState = {
      pointerId: event.pointerId,
      mode: 'resize',
      startClientX: event.clientX,
      startWidth: this.inspectorWidth,
      startDockSide: this.inspectorDockSide,
    };

    this.inspectorDragMoveHandler = (moveEvent: PointerEvent) => {
      if (
        !this.inspectorDragState ||
        moveEvent.pointerId !== this.inspectorDragState.pointerId ||
        this.inspectorDragState.mode !== 'resize'
      ) {
        return;
      }
      const dx = moveEvent.clientX - this.inspectorDragState.startClientX;
      const widthDelta =
        this.inspectorDragState.startDockSide === 'right' ? -dx : dx;
      this.inspectorWidth = Math.max(
        280,
        Math.min(520, Math.round(this.inspectorDragState.startWidth + widthDelta)),
      );
      this.syncGraphAfterInspectorLayout();
      this.cdr.detectChanges();
    };

    this.inspectorDragEndHandler = (endEvent: PointerEvent) => {
      if (
        !this.inspectorDragState ||
        endEvent.pointerId !== this.inspectorDragState.pointerId
      ) {
        return;
      }
      this.stopInspectorDrag();
      this.syncGraphAfterInspectorLayout();
      this.cdr.detectChanges();
    };

    window.addEventListener('pointermove', this.inspectorDragMoveHandler);
    window.addEventListener('pointerup', this.inspectorDragEndHandler);
    window.addEventListener('pointercancel', this.inspectorDragEndHandler);
  }

  private stopInspectorDrag(): void {
    if (this.inspectorDragMoveHandler) {
      window.removeEventListener('pointermove', this.inspectorDragMoveHandler);
      this.inspectorDragMoveHandler = null;
    }
    if (this.inspectorDragEndHandler) {
      window.removeEventListener('pointerup', this.inspectorDragEndHandler);
      window.removeEventListener('pointercancel', this.inspectorDragEndHandler);
      this.inspectorDragEndHandler = null;
    }
    this.inspectorDragState = null;
    this.inspectorDragging = false;
    this.inspectorDockPreview = null;
  }

  private syncGraphAfterInspectorLayout(): void {
    setTimeout(() => {
      this.cy?.resize();
      if (this.isDrillDown && !this.canvasDocked) {
        this.fitVisibleGraph();
      } else {
        this.applyInitialGraphViewport();
      }
    }, 0);
  }

  private getGraphLayoutGutter(): number {
    if (this.canvasDocked) return 56;
    return Math.min(128, Math.max(72, Math.round(this.inspectorWidth * 0.24)));
  }

  private revealInspectorForSelection(): void {
    if (!this.canvasDocked) {
      this.cdr.detectChanges();
      return;
    }
    this.canvasDocked = false;
    this.cdr.detectChanges();
    this.syncGraphAfterInspectorLayout();
  }

  private resolveSelectedNode(data: any): any {
    if (!data?.id) return data;
    if (data?.metadata?.isSourceTypeHub || isSourceTypeHubNode(data)) {
      return this.decorateNode(data);
    }
    const raw = (this.graphPayload?.nodes || []).find((node: any) => node.id === data.id);
    const resolved = raw ? this.decorateNode(raw) : data;
    this.resolveCollectionSelection(resolved);
    return resolved;
  }

  fitGraph(): void {
    this.fitVisibleGraph();
  }

  resetGraphPosition(): void {
    if (!this.cy || !this.graphPayload?.nodes?.length) return;

    const nodes = this.getDisplayGraphPayload().nodes;
    const positions = this.calculateGraphPositions(nodes);
    const rootId = this.rootNode?.id;

    this.cy.nodes().forEach((node: any) => {
      const data = node.data();
      if (data?.isActionBadge || data?.isRootHalo || data?.isLayerRing) return;
      const pos = positions[node.id()];
      if (pos) {
        node.position(pos);
      }
    });

    if (this.isLayeredView) {
      this.applyLayerRingPositions(positions);
    }

    this.groupDragState = null;
    this.syncActionBadges();
    this.fitVisibleGraph();
    if (this.nodeActionPopoverNodeId && this.cy) {
      const node = this.cy.getElementById(this.nodeActionPopoverNodeId);
      if (node?.length) {
        this.updateNodeActionPopoverPosition(node);
      }
    }
    this.cdr.detectChanges();
  }

  selectRootNode(): void {
    if (!this.rootNode) return;
    this.selectedEntity = this.decorateNode(this.rootNode);
    this.selectedEntityKind = 'node';
    this.focusedNodeId = null;
    this.revealInspectorForSelection();
    this.cy?.getElementById(this.rootNode.id)?.select();
    this.cy?.animate({
      center: { eles: this.cy.getElementById(this.rootNode.id) },
      zoom: this.isLayeredView ? 0.82 : this.isDrillDown ? 0.92 : 0.76,
      duration: 260,
    });
    this.applyLayeredSelectionFocus(this.rootNode.id);
  }

  chooseStarterType(type: 'phone' | 'email' | 'username'): void {
    this.starterQueryType = type;
    this.selectRootNode();
  }

  closeInspector(): void {
    this.selectedEntity = null;
    this.selectedEntityKind = null;
    this.showInspectorActions = false;
    this.closeNodeActionPopover();
    this.applyLayeredSelectionFocus(null);
    this.collapseInspector();
  }

  exportJson(): void {
    const blob = new Blob([JSON.stringify(this.graphPayload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${this.linkAnalysisId}_link_analysis.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  exportPng(): void {
    if (!this.cy) return;
    const dataUrl = this.cy.png({
      full: true,
      bg: this.isLightTheme ? '#f0f5f2' : '#07141f',
      scale: 2,
    });
    const anchor = document.createElement('a');
    anchor.href = dataUrl;
    anchor.download = `${this.linkAnalysisId}_link_analysis.png`;
    anchor.click();
  }

  getEntityVisual(entity: any): any {
    return this.getNodeVisual(entity, this.inferBrand(entity));
  }

  private renderGraph(): void {
    if (!this.graphCanvas?.nativeElement || !this.graphPayload?.nodes?.length) {
      return;
    }

    this.rebuildDisplayGraph();
    this.rebuildNodeLayers();
    const elements = this.buildElements();
    this.stopGraphFlowAnimation();
    this.cy?.destroy();

    const runRender = () => {
      if (!this.graphCanvas?.nativeElement) return;
      this.cy = cytoscape({
        container: this.graphCanvas.nativeElement,
        elements,
        style: this.buildStyles() as any,
        boxSelectionEnabled: true,
        selectionType: 'additive',
        layout: this.getLayoutConfig(),
        minZoom: 0.12,
        maxZoom: 2.4,
      });

      this.bindGraphEvents();
      this.updateMultiSelectionState();
      this.syncActionBadges();
      this.applyGraphFilter(false);
      this.cy.resize();
      this.fitVisibleGraph();
      this.cy.style().update();
      if (this.isLayeredView) {
        this.applyLayeredSelectionFocus(
          this.selectedEntityKind === 'node' ? this.selectedEntity?.id || null : null,
        );
      }
      this.startGraphFlowAnimation();
      setTimeout(() => {
        if (!this.cy) return;
        this.cy.resize();
        this.fitVisibleGraph();
      }, 120);
    };

    const scheduleRender = () =>
      requestAnimationFrame(() => requestAnimationFrame(runRender));

    if (typeof document !== 'undefined' && document.fonts?.load) {
      document.fonts.load('1em bootstrap-icons').then(scheduleRender).catch(scheduleRender);
      return;
    }

    scheduleRender();
  }

  private bindGraphEvents(): void {
    if (!this.cy) return;

    this.cy.on('tap', 'node', (event) => {
      const data = event.target.data();
      if (data?.isActionBadge && data.parentNodeId) {
        const parent = this.cy?.getElementById(data.parentNodeId);
        if (parent?.length) {
          this.handleNodeTap(parent, event);
        }
        return;
      }
      if (data?.isLayerRing || data?.isRootHalo) return;
      this.handleNodeTap(event.target, event);
    });

    this.cy.on('position', 'node', (event) => {
      this.syncDecorationsForNode(event.target);
      if (
        this.nodeActionPopoverNodeId &&
        event.target.id() === this.nodeActionPopoverNodeId
      ) {
        this.updateNodeActionPopoverPosition(event.target);
        this.cdr.detectChanges();
      }
    });

    this.cy.on('boxselect', 'node', (event) => {
      const data = event.target?.data?.();
      if (!data?.isActionBadge && !data?.isRootHalo) {
        this.selectedNodeIds.add(event.target.id());
      }
      this.updateMultiSelectionState();
    });

    this.cy.on('select unselect', 'node', () => {
      this.updateMultiSelectionState();
    });

    this.cy.on('grab', 'node', (event) => {
      this.startGroupDrag(event.target);
    });

    this.cy.on('drag', 'node', (event) => {
      this.syncGroupDrag(event.target);
    });

    this.cy.on('free', 'node', () => {
      this.groupDragState = null;
      this.syncActionBadges();
    });

    this.cy.on('pan zoom position', () => {
      if (!this.hoveredNodeId || !this.cy) return;
      const hovered = this.cy.getElementById(this.hoveredNodeId);
      if (hovered?.length) {
        this.updateHoverTooltipPosition(hovered);
        this.cdr.detectChanges();
      }
    });

    this.cy.on('mouseover', 'node.layered-node, node.hierarchy-node', (event) => {
      this.setIconHoverGlow(event.target, true);
    });

    this.cy.on('mouseout', 'node.layered-node, node.hierarchy-node', (event) => {
      this.setIconHoverGlow(event.target, false);
    });

    this.cy.on('pan zoom', () => {
      if (!this.nodeActionPopoverNodeId || !this.cy) return;
      const node = this.cy.getElementById(this.nodeActionPopoverNodeId);
      if (node?.length) {
        this.updateNodeActionPopoverPosition(node);
        this.cdr.detectChanges();
      }
    });

    this.cy.on('tap', 'edge', (event) => {
      this.selectedEntity = this.resolveSelectedNode(event.target.data());
      this.selectedEntityKind = 'edge';
      this.resetSelectedPreviewImageState();
      this.revealInspectorForSelection();
    });

    this.cy.on('tap', (event) => {
      if (event.target === this.cy) {
        this.cy?.nodes('.icon-hover').removeClass('icon-hover');
        this.selectedEntity = null;
        this.selectedEntityKind = null;
        this.selectedNodeIds.clear();
        this.cy?.elements(':selected').unselect();
        this.groupDragState = null;
        this.updateMultiSelectionState();
        this.showInspectorActions = false;
        this.closeNodeActionPopover();
        this.applyLayeredSelectionFocus(null);
        this.canvasDocked = true;
        this.cdr.detectChanges();
      }
    });

    if (this.selectedEntityKind === 'node' && this.selectedEntity?.id) {
      this.cy.getElementById(this.selectedEntity.id)?.select();
      this.selectedNodeIds.add(this.selectedEntity.id);
    }
  }

  private setIconHoverGlow(node: any, active: boolean): void {
    if (!node?.length) return;

    const data = node.data();
    if (data?.isActionBadge || data?.isLayerRing || data?.isRootHalo) return;
    if (!node.hasClass('layered-node') && !node.hasClass('hierarchy-node')) return;

    if (active) {
      this.cy?.nodes('.icon-hover').not(node).removeClass('icon-hover');
      node.addClass('icon-hover');

      if (node.hasClass('layered-node') || node.hasClass('hierarchy-node')) {
        const label = String(node.data('displayLabel') || '').trim();
        if (label) {
          this.hoveredNodeId = node.id();
          this.hoveredNodeLabel = label;
          this.updateHoverTooltipPosition(node);
        }
      }
    } else {
      node.removeClass('icon-hover');

      if (this.hoveredNodeId === node.id()) {
        this.hoveredNodeId = null;
        this.hoveredNodeLabel = null;
      }
    }

    this.cdr.detectChanges();
  }

  private updateHoverTooltipPosition(node: any): void {
    if (!this.cy || !this.graphCanvas?.nativeElement || !node?.length) return;
    const rendered = node.renderedPosition();
    const rect = this.graphCanvas.nativeElement.getBoundingClientRect();
    const size = Number(node.data('nodeSize') || node.width() || 42);
    this.hoverTooltipX = rect.left + rendered.x;
    this.hoverTooltipY = rect.top + rendered.y + size * 0.55 + 8;
  }

  private updateNodeActionPopoverPosition(node: any): void {
    if (this.nodeActionPopoverPinned || !this.cy || !this.graphCanvas?.nativeElement || !node?.length) {
      return;
    }
    const rendered = node.renderedPosition();
    const rect = this.graphCanvas.nativeElement.getBoundingClientRect();
    const size = Number(node.data('nodeSize') || node.width() || 42);
    let x = rect.left + rendered.x + size * 0.42;
    let y = rect.top + rendered.y - size * 0.12;
    this.setNodeActionPopoverPosition(x, y);
  }

  private syncNodeActionPopover(node: any): void {
    if (!node?.length || !this.shouldOfferNodeSearchOptions(this.resolveSelectedNode(node.data()))) {
      this.closeNodeActionPopover();
      return;
    }
    const isNewNode = this.nodeActionPopoverNodeId !== node.id();
    this.nodeActionPopoverNodeId = node.id();
    if (isNewNode) {
      this.nodeActionPopoverPinned = false;
    }
    this.updateNodeActionPopoverPosition(node);
    this.nodeActionPopoverVisible = true;
    this.cdr.detectChanges();
  }

  private shouldOfferNodeSearchOptions(node: any): boolean {
    if (!node) return false;
    if (this.isAutomatedExpansionLocked) return false;
    if (node.type === 'starter') return false;
    if (isAggregateHubNode(node) || node.metadata?.isAggregateCollection) return false;
    if (this.getNodeActions(node).length > 0) return true;
    if (!this.isDrillDown && this.getSelectedNodeFilterValue(node)) return true;
    return this.canRunAiForNode(node);
  }

  private getSelectedNodeFilterValue(node: any = this.selectedEntity): string {
    if (!node) return '';
    return String(
      node.label ||
      node.metadata?.queryValue ||
      node.metadata?.handle ||
      '',
    ).trim();
  }

  private handleNodeTap(node: any, event?: any): void {
    if (!this.cy || !node?.length) return;
    const data = node.data();
    if (data?.isActionBadge || data?.isRootHalo || data?.isLayerRing) return;

    const originalEvent = event?.originalEvent as MouseEvent | KeyboardEvent | undefined;
    const additiveGesture = Boolean(
      this.multiSelectMode ||
      originalEvent?.shiftKey ||
      originalEvent?.metaKey ||
      originalEvent?.ctrlKey,
    );

    this.selectedEntity = this.resolveSelectedNode(data);
    this.selectedEntityKind = 'node';
    this.resetSelectedPreviewImageState();

    if (additiveGesture) {
      if (this.selectedNodeIds.has(node.id())) {
        this.selectedNodeIds.delete(node.id());
        node.unselect();
      } else {
        this.selectedNodeIds.add(node.id());
        node.select();
      }
      this.closeNodeActionPopover();
      this.updateMultiSelectionState();
      return;
    }

    this.selectedNodeIds.clear();
    this.cy.nodes(':selected').unselect();
    this.selectedNodeIds.add(node.id());
    node.select();
    this.revealInspectorForSelection();
    this.syncNodeActionPopover(node);
    this.updateMultiSelectionState();
    this.applyLayeredSelectionFocus(node.id());
  }

  private updateMultiSelectionState(): void {
    if (!this.cy) {
      this.multiSelectedNodeCount = 0;
      return;
    }
    this.selectedNodeIds.forEach((nodeId) => {
      const node = this.cy?.getElementById(nodeId);
      if (!node?.length) {
        this.selectedNodeIds.delete(nodeId);
      } else if (!node.selected()) {
        node.select();
      }
    });
    this.multiSelectedNodeCount = this.selectedNodeIds.size;
    this.cdr.detectChanges();
  }

  private getMovableSelectedNodes(): any {
    if (!this.cy) return null;
    return this.cy.nodes().filter((node: any) => {
      const data = node.data();
      return this.selectedNodeIds.has(node.id()) && !data?.isActionBadge && !data?.isRootHalo;
    });
  }

  private startGroupDrag(target: any): void {
    const data = target?.data?.();
    if (!this.cy || data?.isActionBadge || data?.isRootHalo) {
      this.groupDragState = null;
      return;
    }
    const selectedNodes = this.getMovableSelectedNodes();
    if (!selectedNodes || selectedNodes.length <= 1 || !this.selectedNodeIds.has(target.id())) {
      this.groupDragState = null;
      return;
    }
    const sourceStart = target.position();
    this.groupDragState = {
      sourceId: target.id(),
      sourceStart: { x: sourceStart.x, y: sourceStart.y },
      nodeStarts: selectedNodes.map((node: any) => ({
        id: node.id(),
        x: node.position('x'),
        y: node.position('y'),
      })),
    };
  }

  private syncGroupDrag(target: any): void {
    if (!this.cy || !this.groupDragState || this.syncingGroupDrag) return;
    if (target?.id?.() !== this.groupDragState.sourceId) return;

    const currentPosition = target.position();
    const dx = currentPosition.x - this.groupDragState.sourceStart.x;
    const dy = currentPosition.y - this.groupDragState.sourceStart.y;

    this.syncingGroupDrag = true;
    this.groupDragState.nodeStarts.forEach((item) => {
      if (item.id === target.id()) return;
      const node = this.cy?.getElementById(item.id);
      if (node?.length) {
        node.position({
          x: item.x + dx,
          y: item.y + dy,
        });
        this.syncDecorationsForNode(node);
      }
    });
    this.syncingGroupDrag = false;
  }

  private buildElements(): ElementDefinition[] {
    const displayGraph = this.getDisplayGraphPayload();
    const nodes = (displayGraph.nodes || []).filter((node: any) =>
      this.shouldDisplayOsintSourceNode(node),
    );
    const positions = this.calculateGraphPositions(nodes);
    const rootId = this.rootNode?.id;
    const edges = displayGraph.edges || [];
    const actionableNodes = nodes.filter((node: any) => this.getActionCount(node) > 0);
    const rootHalo =
      rootId && positions[rootId]
        ? [
          {
            position: positions[rootId],
            data: {
              id: `root-halo:${rootId}`,
              parentNodeId: rootId,
              isRootHalo: true,
            },
            grabbable: false,
            selectable: false,
            classes: `root-halo${this.isLayeredView ? ' layered-root-halo' : ' hierarchy-root-halo'}${this.isDrillDown ? ' drill-root-halo' : ' automated-root-halo'}`,
          },
        ]
        : [];
    const layerRings = this.buildLayerRingElements(positions);
    const fallbackPosition =
      (rootId && positions[rootId]) ||
      positions[nodes[0]?.id] || { x: 600, y: 360 };

    return [
      ...layerRings,
      ...rootHalo,
      ...nodes.map((node: any) => {
        const decorated = this.decorateNode(node);
        return {
          position: positions[node.id] || fallbackPosition,
          data: decorated,
          classes: this.buildGraphNodeClasses(decorated, rootId),
        };
      }),
      ...actionableNodes.map((node: any) => {
        const decorated = this.decorateNode(node);
        const size = decorated.nodeSize || 52;
        const actionCount = this.getActionCount(node);
        const nodePosition = positions[node.id] || fallbackPosition;
        const badgeMetrics = this.getActionBadgeMetrics(actionCount, decorated.iconColor, size);
        return {
          position: this.getActionBadgePosition(
            nodePosition,
            size,
            badgeMetrics.badgeWidth,
            badgeMetrics.badgeHeight,
          ),
          data: {
            id: `action-badge:${node.id}`,
            parentNodeId: node.id,
            graphLabel: String(actionCount),
            badgeColor: badgeMetrics.badgeColor,
            badgeFontSize: badgeMetrics.badgeFontSize,
            badgeWidth: badgeMetrics.badgeWidth,
            badgeHeight: badgeMetrics.badgeHeight,
            isActionBadge: true,
          },
          grabbable: false,
          selectable: false,
          classes: 'action-badge layered-action-badge',
        };
      }),
      ...edges.map((edge: any) => {
        const decorated = this.decorateEdge(edge);
        const flow = this.resolveLayeredEdgeFlow(decorated);
        const source = this.resolveVisibleEdgeEndpoint(flow.source);
        const target = this.resolveVisibleEdgeEndpoint(flow.target);
        const flowClass = this.isSearchFlowEdge(edge?.type) ? ' graph-flow-edge' : '';
        return {
          data: {
            ...decorated,
            source,
            target,
          },
          classes: `edge-${edge.type} role-${edge.role || 'supporting'} tier-${edge.evidenceTier}${this.isLayeredView ? ' layered-edge' : ' hierarchy-edge'}${flowClass}`,
        };
      }),
    ];
  }

  private buildGraphNodeClasses(node: any, rootId?: string): string {
    const isRoot = node?.id === rootId;
    const isLayerHero = isLayerSourceHeroNode(node, rootId);
    const isSourceHub = isSourceTypeHubNode(node);
    const heroClass = isRoot || isLayerHero || isSourceHub ? ' hero-root' : '';
    const layeredHeroClass =
      (isRoot || isLayerHero || isSourceHub) && this.isLayeredView ? ' layered-hero-root' : '';
    const layerHeroClass = isLayerHero ? ' layer-hero-root' : '';
    const sourceHubClass = isSourceHub ? ' source-type-hub' : '';
    const iconModeClass = node?.iconSvg
      ? `graph-svg-icon-node${String(node.iconSvg).startsWith('data:') ? '' : ' graph-photo-icon-node'}`
      : 'graph-icon-node';

    return `node-${node.type} role-${node.role || 'supporting'} tier-${node.evidenceTier} ${iconModeClass}${isRoot ? ' node-root' : ''}${heroClass}${layeredHeroClass}${layerHeroClass}${sourceHubClass}${isRoot && this.isDrillDown && !this.isLayeredView ? ' drill-root' : ''}${isRoot && !this.isDrillDown && !this.isLayeredView ? ' automated-root' : ''}${this.isLayeredView ? ' layered-node' : ' hierarchy-node'}${!this.isLayeredView && isRoot ? ' hierarchy-root' : ''}${this.hasAvailableActions(node) ? ' node-actionable' : ' node-passive'}${this.isNodeRunningAction(node.id) ? ' node-action-running' : ''}${this.isRecentlyAddedNode(node.id) ? ' node-action-new' : ''}`;
  }

  private buildLayerRingElements(
    positions: Record<string, { x: number; y: number }>,
  ): ElementDefinition[] {
    if (!this.isLayeredView || !this.rootNode?.id) {
      return [];
    }

    const center = positions[this.rootNode.id];
    if (!center) {
      return [];
    }
    const ringLabels = [
      'Layer 1 · Direct results',
      'Layer 2 · Follow-up searches',
      'Layer 3 · Extended searches',
      'Layer 4 · Deep results',
    ];
    const sectorLabels: Array<{ label: string; angle: number }> = [];
    const outerRadius = this.layeredRings[this.layeredRings.length - 1]?.radius || 0;

    const ringElements = this.layeredRings.flatMap(({ layer, radius }) => {
      const ringIndex = Math.max(0, layer - 1);
      return [
        {
          position: center,
          data: {
            id: `layer-ring:${layer}`,
            isLayerRing: true,
            ringSize: radius * 2,
          },
          grabbable: false,
          selectable: false,
          classes: `layer-ring layer-ring-${layer}`,
        },
        {
          position: {
            x: center.x + Math.cos(-Math.PI / 2 + ringIndex * 0.55) * (radius + 30),
            y: center.y + Math.sin(-Math.PI / 2 + ringIndex * 0.55) * (radius + 30),
          },
          data: {
            id: `layer-ring-label:${layer}`,
            isLayerRing: true,
            graphLabel: ringLabels[ringIndex] || `Layer ${layer}`,
          },
          grabbable: false,
          selectable: false,
          classes: 'layer-ring-label',
        },
      ];
    });

    const sectorElements = outerRadius
      ? sectorLabels.map((sector, index) => ({
        position: {
          x: center.x + Math.cos(sector.angle) * (outerRadius + 42),
          y: center.y + Math.sin(sector.angle) * (outerRadius + 42),
        },
        data: {
          id: `sector-label:${index}`,
          isLayerRing: true,
          graphLabel: sector.label,
        },
        grabbable: false,
        selectable: false,
        classes: 'sector-cluster-label',
      }))
      : [];

    return [...ringElements, ...sectorElements];
  }

  private applyLayerRingPositions(
    positions: Record<string, { x: number; y: number }>,
  ): void {
    if (!this.cy || !this.rootNode?.id) return;
    const center = positions[this.rootNode.id];
    if (!center) return;

    this.layeredRings.forEach(({ layer, radius }) => {
      const ringIndex = Math.max(0, layer - 1);
      const ring = this.cy!.getElementById(`layer-ring:${layer}`);
      const label = this.cy!.getElementById(`layer-ring-label:${layer}`);
      if (ring?.length) {
        ring.position(center);
      }
      if (label?.length) {
        label.position({
          x: center.x + Math.cos(-Math.PI / 2 + ringIndex * 0.55) * (radius + 30),
          y: center.y + Math.sin(-Math.PI / 2 + ringIndex * 0.55) * (radius + 30),
        });
      }
    });
  }

  private getActionBadgeMetrics(
    actionCount: number,
    iconColor = '#22c55e',
    nodeSize = 52,
  ): {
    badgeColor: string;
    badgeFontSize: number;
    badgeWidth: number;
    badgeHeight: number;
  } {
    const label = String(Math.max(0, actionCount));
    const digits = Math.max(1, label.length);
    const badgeFontSize = digits >= 3 ? 9 : digits === 2 ? 11 : 12;
    const badgeHeight = digits === 1 ? 10 : Math.round(badgeFontSize * 1.28);
    const badgeWidth =
      digits === 1
        ? 10
        : Math.round(badgeFontSize * (digits === 2 ? 1.72 : 2.05));

    return {
      badgeColor: iconColor,
      badgeFontSize,
      badgeWidth: Math.max(10, badgeWidth),
      badgeHeight: Math.max(10, badgeHeight),
    };
  }

  private getActionBadgePosition(
    nodePosition: { x: number; y: number },
    nodeSize: number,
    badgeWidth = 18,
    badgeHeight = 18,
  ): { x: number; y: number } {
    const half = nodeSize / 2;
    return {
      x: nodePosition.x + half - badgeWidth * 0.22,
      y: nodePosition.y - half + badgeHeight * 0.22,
    };
  }

  private resolveGraphNodeForActions(nodeId: string, fallback?: any): any {
    return (this.graphPayload?.nodes || []).find((node: any) => node.id === nodeId) || fallback;
  }

  private syncActionBadges(): void {
    if (!this.cy) return;
    this.cy.nodes().forEach((node: any) => {
      this.syncDecorationsForNode(node);
    });
  }

  private syncDecorationsForNode(node: any): void {
    if (
      !this.cy ||
      !node?.length ||
      node.data('isActionBadge') ||
      node.data('isRootHalo') ||
      node.data('isLayerRing')
    ) return;
    const nodeId = node.id();
    const badge = this.cy.getElementById(`action-badge:${nodeId}`);

    const position = node.position();
    const size = Number(node.data('nodeSize') || node.width() || 52);
    const actionCount = this.getActionCount(this.resolveGraphNodeForActions(nodeId, node.data()));
    if (badge?.length) {
      const badgeMetrics = this.getActionBadgeMetrics(
        actionCount,
        node.data('iconColor') || '#22c55e',
        size,
      );
      badge.position(
        this.getActionBadgePosition(
          position,
          size,
          badgeMetrics.badgeWidth,
          badgeMetrics.badgeHeight,
        ),
      );
      badge.data('graphLabel', String(actionCount));
      badge.data('badgeColor', badgeMetrics.badgeColor);
      badge.data('badgeFontSize', badgeMetrics.badgeFontSize);
      badge.data('badgeWidth', badgeMetrics.badgeWidth);
      badge.data('badgeHeight', badgeMetrics.badgeHeight);
      this.setGraphElementVisibility(badge, actionCount > 0 && !node.hidden());
    }

    const halo = this.cy.getElementById(`root-halo:${nodeId}`);
    if (halo?.length) {
      halo.position(position);
    }
  }

  private rebuildDisplayGraph(): void {
    const nodes = this.graphPayload?.nodes || [];
    const edges = this.graphPayload?.edges || [];
    if (!nodes.length) {
      this.displayGraphPayload = null;
      return;
    }

    this.displayGraphPayload = expandGraphWithSourceTypeHubs(nodes, edges, {
      rootId: this.rootNode?.id || '',
      getNodeLayer: (node) => this.getNodeLayer(node),
    });
  }

  private getDisplayGraphPayload(): { nodes: any[]; edges: any[] } {
    if (!this.displayGraphPayload) {
      this.rebuildDisplayGraph();
    }
    return this.displayGraphPayload || {
      nodes: this.graphPayload?.nodes || [],
      edges: this.graphPayload?.edges || [],
    };
  }

  private rebuildNodeLayers(): void {
    this.nodeLayerById.clear();
    this.searchParentById.clear();
    const displayGraph = this.getDisplayGraphPayload();
    const nodes = displayGraph.nodes || [];
    const rootId = this.rootNode?.id;
    if (!rootId) return;

    // Phase 7: prefer metadata.parentNodeId + metadata.layer (R13). Edge BFS only fills gaps.
    nodes.forEach((node: any) => {
      if (!node?.id) return;
      const parentId = String(node?.metadata?.parentNodeId || '').trim();
      if (parentId) {
        this.searchParentById.set(node.id, parentId);
      }
      const metaLayer = Number(node?.metadata?.layer);
      if (Number.isFinite(metaLayer)) {
        this.nodeLayerById.set(node.id, metaLayer);
      }
    });

    this.nodeLayerById.set(rootId, 0);
    const edges = displayGraph.edges || [];
    const children = new Map<string, string[]>();
    edges.forEach((edge: any) => {
      if (!children.has(edge.source)) children.set(edge.source, []);
      children.get(edge.source)?.push(edge.target);
    });

    const visited = new Set<string>([rootId]);
    const fillQueue = [rootId];
    while (fillQueue.length) {
      const current = fillQueue.shift() as string;
      const depth = this.nodeLayerById.get(current) ?? 0;
      (children.get(current) || []).forEach((childId) => {
        if (visited.has(childId)) return;
        visited.add(childId);
        if (!this.searchParentById.has(childId)) {
          this.searchParentById.set(childId, current);
        }
        if (!this.nodeLayerById.has(childId)) {
          this.nodeLayerById.set(childId, depth + 1);
        }
        fillQueue.push(childId);
      });
    }

    nodes.forEach((node: any) => {
      if (this.nodeLayerById.has(node.id)) return;
      if (node.id === rootId || node.type === 'starter' || node.type === 'query_root') {
        this.nodeLayerById.set(node.id, 0);
        return;
      }
      this.nodeLayerById.set(node.id, 1);
    });
  }

  getNodeLayer(node: any): number {
    if (!node?.id) return 0;
    if (isSourceTypeHubNode(node)) {
      const hubLayer = Number(node?.metadata?.layer);
      if (Number.isFinite(hubLayer)) return hubLayer;
    }
    if (this.rootNode?.id === node.id || node.type === 'starter' || node.type === 'query_root') {
      return 0;
    }
    const metaLayer = Number(node?.metadata?.layer);
    if (Number.isFinite(metaLayer)) {
      return metaLayer;
    }
    return this.nodeLayerById.get(node.id) ?? 1;
  }

  private countVisibleNodes(): number {
    return this.filterNodes().length;
  }

  private isOsintPlatformSourceNode(node: any): boolean {
    return Boolean(
      node?.metadata?.isOsintPlatformSource &&
      node?.metadata?.sourceData &&
      typeof node.metadata.sourceData === 'object',
    );
  }

  private isInspectableSourceNode(node: any): boolean {
    if (!node) return false;
    return node.type === 'osint_source' || node.type === 'leak_source' || this.isOsintPlatformSourceNode(node);
  }

  private osintSourceNodeVisible(node: any): boolean {
    return shouldDisplayOsintSourceOnGraph(node);
  }

  private shouldDisplayOsintSourceNode(node: any): boolean {
    if (node?.type === 'osint_source' || this.isOsintPlatformSourceNode(node)) {
      return this.osintSourceNodeVisible(node);
    }
    return true;
  }

  private filterNodes(): any[] {
    const allNodes = this.graphPayload?.nodes || [];
    const searchTerm = this.graphSearchTerm.trim().toLowerCase();
    const presentedRoot = this.presentedDrillDownRoot;
    const rootId = this.rootNode?.id;
    const presentedRootValue = this.normalizeEntityValue(presentedRoot?.label || presentedRoot?.metadata?.queryValue || '');
    const presentedRootType = presentedRoot?.metadata?.queryType || presentedRoot?.type;

    let filtered = allNodes.filter((node: any) => {
      if (
        this.isDrillDown &&
        rootId &&
        node.id !== rootId &&
        presentedRootValue &&
        node.type === presentedRootType &&
        this.normalizeEntityValue(node.label || node.metadata?.queryValue || '') === presentedRootValue
      ) {
        return false;
      }
      if (!this.shouldDisplayOsintSourceNode(node)) {
        return false;
      }
      const confidence = Number(node.confidence || 0);
      const searchable = [
        node.label,
        node.type,
        node.metadata?.platform,
        node.metadata?.sourceName,
        node.metadata?.handle,
        ...(node.metadata?.urls || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesType =
        this.nodeTypeFilter === 'all' ||
        this.matchesNodeTypeFilter(node.type, this.nodeTypeFilter);
      const matchesRole =
        this.roleFilter === 'all' || node.role === this.roleFilter;
      const matchesTier =
        this.tierFilter === 'all' || node.evidenceTier === this.tierFilter;
      const matchesSearch = !searchTerm || searchable.includes(searchTerm);
      const nodeLayer = this.getNodeLayer(node);
      const matchesLayer = this.visibleUpTo < 0 || nodeLayer <= this.visibleUpTo;
      return (
        matchesType &&
        matchesRole &&
        matchesTier &&
        matchesSearch &&
        matchesLayer &&
        confidence >= this.minConfidence
      );
    });

    if (this.focusedNodeId) {
      const neighborIds = new Set<string>([this.focusedNodeId]);
      (this.graphPayload?.edges || []).forEach((edge: any) => {
        if (edge.source === this.focusedNodeId) {
          neighborIds.add(edge.target);
        }
        if (edge.target === this.focusedNodeId) {
          neighborIds.add(edge.source);
        }
      });
      filtered = filtered.filter((node: any) => neighborIds.has(node.id));
    }

    return filtered;
  }

  private filterDisplayNodes(): any[] {
    const visibleRealIds = new Set(this.filterNodes().map((node: any) => node.id));
    const rootId = this.rootNode?.id;
    if (!visibleRealIds.size && rootId) {
      visibleRealIds.add(rootId);
    }

    return this.getDisplayGraphPayload().nodes.filter((node: any) => {
      if (!isSourceTypeHubNode(node)) {
        return visibleRealIds.has(node.id);
      }

      const layer = this.getNodeLayer(node);
      const matchesLayer = this.visibleUpTo < 0 || layer <= this.visibleUpTo;
      if (!matchesLayer) return false;

      const parentId = node?.metadata?.parentNodeId;
      const childIds: string[] = node?.metadata?.hubChildIds || [];
      const parentVisible = !parentId || visibleRealIds.has(parentId);
      const childVisible = childIds.some((childId) => visibleRealIds.has(childId));
      return parentVisible && childVisible;
    });
  }

  private normalizeEntityValue(value: string): string {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, '');
  }

  private normalizeNodeTypeForFilter(type: string | undefined): string {
    const value = String(type || '').toLowerCase();
    const aliases: Record<string, string> = {
      social: 'social_profile',
      username: 'social_profile',
      face_result: 'image',
      face_image: 'image',
      image_query: 'image',
      website: 'social_profile',
      address: 'location',
      starter: 'phone',
      query_root: 'phone',
    };
    return aliases[value] || value;
  }

  private matchesNodeTypeFilter(nodeType: string, filterType: string): boolean {
    return (
      this.normalizeNodeTypeForFilter(nodeType) ===
      this.normalizeNodeTypeForFilter(filterType)
    );
  }

  private applyGraphTheme(): void {
    if (!this.cy) {
      this.cdr.detectChanges();
      return;
    }

    this.cy.style(this.buildStyles());
    this.refreshGraphNodeThemeData();
    this.cy.style().update();
    this.startGraphFlowAnimation();
    this.cdr.detectChanges();
  }

  private refreshGraphNodeThemeData(): void {
    if (!this.cy || !this.graphPayload?.nodes?.length) return;

    this.graphPayload.nodes.forEach((node: any) => {
      const element = this.cy!.getElementById(node.id);
      if (!element?.length || element.data('isActionBadge') || element.data('isRootHalo')) {
        return;
      }
      const decorated = this.decorateNode(node);
      element.data('labelBgColor', decorated.labelBgColor);
      element.data('graphIcon', decorated.graphIcon);
      element.data('iconColor', decorated.iconColor);
      element.data('iconFontSize', decorated.iconFontSize);
      element.data('iconSvg', decorated.iconSvg || '');
      if (decorated.iconSvg) {
        element.removeClass('graph-icon-node');
        element.addClass('graph-svg-icon-node');
        if (String(decorated.iconSvg).startsWith('data:')) {
          element.removeClass('graph-photo-icon-node');
        } else {
          element.addClass('graph-photo-icon-node');
        }
      } else {
        element.removeClass('graph-svg-icon-node');
        element.removeClass('graph-photo-icon-node');
        element.addClass('graph-icon-node');
      }

      const badge = this.cy!.getElementById(`action-badge:${node.id}`);
      if (badge?.length) {
        const actionCount = this.getActionCount(node);
        const badgeMetrics = this.getActionBadgeMetrics(
          actionCount,
          decorated.iconColor,
          decorated.nodeSize || 52,
        );
        badge.data('badgeColor', badgeMetrics.badgeColor);
        badge.data('badgeFontSize', badgeMetrics.badgeFontSize);
        badge.data('badgeWidth', badgeMetrics.badgeWidth);
        badge.data('badgeHeight', badgeMetrics.badgeHeight);
      }
    });
  }

  private getGraphStyleTokens(): {
    nodeLabelColor: string;
    edgeLabelColor: string;
    edgeLabelBg: string;
    layerRingLabelColor: string;
    layerRingLabelBg: string;
    sectorLabelColor: string;
    layerRing1: string;
    layerRing2: string;
    layerRing3: string;
    layerRing4: string;
    hierarchicalLabelBg: string;
  } {
    if (this.isLightTheme) {
      return {
        nodeLabelColor: '#1f2d3d',
        edgeLabelColor: '#5a6b7d',
        edgeLabelBg: 'rgba(255, 255, 255, 0.94)',
        layerRingLabelColor: '#4b5f73',
        layerRingLabelBg: 'rgba(255, 255, 255, 0.92)',
        sectorLabelColor: '#9a7b28',
        layerRing1: 'rgba(34, 197, 94, 0.42)',
        layerRing2: 'rgba(59, 130, 246, 0.34)',
        layerRing3: 'rgba(168, 85, 247, 0.3)',
        layerRing4: 'rgba(245, 158, 11, 0.3)',
        hierarchicalLabelBg: 'rgba(255, 255, 255, 0.92)',
      };
    }

    return {
      nodeLabelColor: '#f4fbff',
      edgeLabelColor: '#b9c9d6',
      edgeLabelBg: 'rgba(2,8,14,0.94)',
      layerRingLabelColor: '#c9d8e6',
      layerRingLabelBg: 'rgba(6, 14, 24, 0.88)',
      sectorLabelColor: '#e8d08a',
      layerRing1: 'rgba(34, 197, 94, 0.55)',
      layerRing2: 'rgba(59, 130, 246, 0.42)',
      layerRing3: 'rgba(168, 85, 247, 0.28)',
      layerRing4: 'rgba(245, 158, 11, 0.28)',
      hierarchicalLabelBg: 'rgba(3, 10, 15, 0.86)',
    };
  }

  private getLightLabelBgColor(type: string): string {
    const map: Record<string, string> = {
      phone: '#e8f8ef',
      email: '#e8f2fc',
      person: '#fef3e8',
      social_profile: '#f3ebfc',
      image: '#f8ecfc',
      company: '#e8f8f6',
      leak_source: '#fdecec',
      osint_source: '#e6fffb',
      address: '#eceffc',
      website: '#f5efe9',
      username: '#f0f8e8',
      starter: '#e8f8ef',
      ai_analysis: '#fef8e8',
      location: '#fef3e8',
      face_result: '#e8f4fc',
      wallet: '#e8f2fc',
      identifier: '#f0f4f8',
    };
    return map[type] || '#f0f4f8';
  }

  private buildStyles(): any[] {
    const theme = this.getGraphStyleTokens();
    const layeredIconBgOpacity = this.isLightTheme ? 0.8 : 0.4;
    const layeredNodeBorderColor = this.isLightTheme ? '#475569' : 'rgba(255, 255, 255, 0.58)';
    const layeredNodeBorderOpacity = this.isLightTheme ? 0.55 : 0.82;
    const ringFillColor = this.isLightTheme ? '#ffffff' : '#000000';
    const lightEdgeOutline = this.isLightTheme
      ? {
        'line-outline-width': 2,
        'line-outline-color': '#ffffff',
        'target-arrow-fill': 'filled',
      }
      : {};
    return [
      {
        selector: 'node',
        style: {
          'font-weight': 800,
          color: theme.nodeLabelColor,
          'text-wrap': 'wrap',
          'text-max-width': 118,
          'text-valign': 'bottom',
          'text-margin-y': 12,
          'text-background-opacity': 0.96,
          'text-background-padding': 6,
          'text-background-shape': 'round-rectangle',
          'background-fit': 'contain',
          'background-clip': 'none',
          'background-width': '74%',
          'background-height': '74%',
          padding: 12,
          'underlay-opacity': 0.06,
          'underlay-padding': 5,
          'z-index': 10,
        },
      },
      {
        selector: 'node[nodeColor]',
        style: {
          label: 'data(graphLabel)',
          'font-size': 'data(labelSize)',
          'text-background-color': 'data(labelBgColor)',
          'border-width': 'data(nodeBorderWidth)',
          'border-color': 'data(nodeBorderColor)',
          'background-color': 'data(nodeColor)',
          'background-image': 'data(iconSvg)',
          shape: 'data(nodeShape)',
          width: 'data(nodeSize)',
          height: 'data(nodeSize)',
          'underlay-color': 'data(nodeGlowColor)',
        },
      },
      {
        selector: 'edge',
        style: {
          width: 'mapData(confidence, 0.5, 1, 1.35, 2.8)',
          'curve-style': 'unbundled-bezier',
          'control-point-distances': '74 -54',
          'control-point-weights': '0.36 0.64',
          'target-arrow-shape': 'triangle',
          'line-color': 'data(edgeColor)',
          'target-arrow-color': 'data(edgeColor)',
          label: 'data(graphLabel)',
          'font-size': 8,
          'font-weight': 800,
          color: theme.edgeLabelColor,
          'text-background-color': theme.edgeLabelBg,
          'text-background-opacity': 0.82,
          'text-background-padding': 3,
          'text-border-opacity': 0,
          'arrow-scale': 0.62,
          opacity: 0.72,
          'z-index': 1,
          'underlay-opacity': 0,
          'underlay-padding': 0,
          ...lightEdgeOutline,
        },
      },
      {
        selector: '.edge-searched, .edge-has_image, .edge-matched_face',
        style: {
          width: 'mapData(confidence, 0.5, 1, 1.8, 3.05)',
          opacity: 0.9,
          'line-color': '#45e58b',
          'target-arrow-color': '#45e58b',
        },
      },
      {
        selector: '.root-halo',
        style: {
          label: '',
          width: 150,
          height: 150,
          shape: 'ellipse',
          'background-color': 'rgba(53,239,132,0.03)',
          'background-image': 'none',
          'border-width': 2,
          'border-style': 'dotted',
          'border-color': 'rgba(93,245,157,0.42)',
          'underlay-opacity': 0,
          events: 'no',
          'z-index': 0,
        },
      },
      {
        selector: '.layer-ring',
        style: {
          label: '',
          shape: 'ellipse',
          width: 'data(ringSize)',
          height: 'data(ringSize)',
          'background-color': ringFillColor,
          'background-opacity': 0,
          'background-image': 'none',
          'border-width': 1.25,
          'border-color': theme.layerRing1,
          'border-style': 'dashed',
          'border-dash-pattern': [7, 9],
          events: 'no',
          'z-index': -20,
        },
      },
      {
        selector: '.layer-ring-1',
        style: {
          'border-color': theme.layerRing1,
          'border-width': 1.75,
          opacity: 0.92,
        },
      },
      {
        selector: '.layer-ring-2',
        style: {
          'border-color': theme.layerRing2,
          'border-width': 1.5,
          opacity: 0.88,
        },
      },
      {
        selector: '.layer-ring-3',
        style: {
          'border-color': theme.layerRing3,
        },
      },
      {
        selector: '.layer-ring-4',
        style: {
          'border-color': theme.layerRing4,
        },
      },
      {
        selector: '.layer-ring-label',
        style: {
          label: 'data(graphLabel)',
          width: 1,
          height: 1,
          'background-opacity': 0,
          'background-image': 'none',
          'border-width': 0,
          color: theme.layerRingLabelColor,
          'font-size': 10,
          'font-weight': 700,
          'text-background-color': theme.layerRingLabelBg,
          'text-background-opacity': 1,
          'text-background-padding': 5,
          'text-background-shape': 'round-rectangle',
          'text-margin-y': 0,
          events: 'no',
          'z-index': -10,
        },
      },
      {
        selector: '.sector-cluster-label',
        style: {
          label: 'data(graphLabel)',
          width: 1,
          height: 1,
          'background-opacity': 0,
          'background-image': 'none',
          'border-width': 0,
          color: theme.sectorLabelColor,
          'font-size': 13,
          'font-weight': 900,
          'text-background-opacity': 0,
          'text-margin-y': 0,
          events: 'no',
          'z-index': -8,
        },
      },
      {
        selector: '.layered-edge, .hierarchy-edge',
        style: {
          width: 'mapData(confidence, 0.5, 1, 0.9, 1.75)',
          opacity: 0.48,
          'curve-style': 'bezier',
          'control-point-step-size': 56,
          'line-style': 'dashed',
          'line-dash-pattern': [10, 12],
          'line-dash-offset': 0,
          'target-arrow-shape': 'triangle',
          'target-arrow-color': 'data(edgeColor)',
          'arrow-scale': 0.62,
          'source-endpoint': 'outside-to-node',
          'target-endpoint': 'outside-to-node',
          'source-distance-from-node': 2,
          'target-distance-from-node': 6,
          'line-color': 'data(edgeColor)',
          'line-cap': 'round',
          label: '',
          'text-background-opacity': 0,
          'underlay-opacity': 0,
          'underlay-padding': 0,
          'line-outline-width': 0,
          'z-index': 1,
          ...lightEdgeOutline,
        },
      },
      {
        selector: '.layered-edge.graph-flow-edge, .hierarchy-edge.graph-flow-edge, .layered-edge.layered-flow-edge',
        style: {
          opacity: 0.62,
          width: 'mapData(confidence, 0.5, 1, 1.05, 2)',
          'line-color': '#5ce693',
          'target-arrow-color': '#5ce693',
          'arrow-scale': 0.72,
          'line-dash-pattern': [12, 10],
        },
      },
      {
        selector: '.layered-edge.edge-searched, .layered-edge.edge-has_image, .layered-edge.edge-matched_face, .hierarchy-edge.edge-searched, .hierarchy-edge.edge-has_image, .hierarchy-edge.edge-matched_face',
        style: {
          opacity: 0.68,
          width: 'mapData(confidence, 0.5, 1, 1.15, 2.1)',
          'line-color': '#45e58b',
          'target-arrow-color': '#45e58b',
          'arrow-scale': 0.74,
        },
      },
      {
        selector: '.layered-edge.role-confirmed, .hierarchy-edge.role-confirmed',
        style: {
          opacity: 0.72,
          width: 'mapData(confidence, 0.5, 1, 1.1, 2)',
        },
      },
      {
        selector: '.layered-edge.role-candidate, .hierarchy-edge.role-candidate',
        style: {
          opacity: 0.58,
        },
      },
      {
        selector: '.layered-edge.role-supporting, .hierarchy-edge.role-supporting',
        style: {
          opacity: this.isDrillDown ? 0.42 : this.isLightTheme ? 0.5 : 0.45,
          'line-dash-pattern': [6, 10],
        },
      },
      {
        selector: '.layered-edge.edge-appears_in_leak, .hierarchy-edge.edge-appears_in_leak',
        style: {
          opacity: 0.58,
          'line-color': '#c084fc',
          'target-arrow-color': '#c084fc',
        },
      },
      {
        selector: '.layered-edge.edge-found_in, .hierarchy-edge.edge-found_in',
        style: {
          opacity: 0.54,
          'line-color': '#2dd4bf',
          'target-arrow-color': '#2dd4bf',
        },
      },
      { selector: 'node.role-confirmed', style: { 'border-color': '#6ef5a0', 'underlay-opacity': 0.54 } },
      { selector: 'node.role-candidate', style: { 'border-color': '#ffd166', 'underlay-opacity': 0.42 } },
      { selector: 'node.role-supporting', style: { 'border-color': '#6a8095', 'underlay-opacity': 0.26 } },
      {
        selector: '.node-actionable',
        style: {
          'border-color': '#35ef84',
          'border-width': 'data(actionBorderWidth)',
          'underlay-color': '#35ef84',
          'underlay-opacity': 0.1,
          'underlay-padding': 9,
        },
      },
      {
        selector: '.node-action-running',
        style: {
          'border-color': '#ffcc66',
          'underlay-color': '#ffcc66',
          'underlay-opacity': 0.22,
          'underlay-padding': 18,
        },
      },
      {
        selector: '.node-action-new',
        style: {
          'border-color': '#72c7ff',
          'underlay-color': '#72c7ff',
          'underlay-opacity': 0.16,
          'underlay-padding': 14,
        },
      },
      {
        selector: '.node-passive',
        style: {
          opacity: 0.82,
          'underlay-opacity': 0.18,
        },
      },
      {
        selector: '.action-badge',
        style: {
          label: 'data(graphLabel)',
          width: 18,
          height: 18,
          shape: 'ellipse',
          'background-color': '#35ef84',
          'background-image': 'none',
          'border-width': 2,
          'border-color': '#08161f',
          color: '#06131f',
          'font-size': 9,
          'font-weight': 900,
          'text-valign': 'center',
          'text-halign': 'center',
          'text-background-opacity': 0,
          'underlay-color': '#35ef84',
          'underlay-opacity': 0.72,
          'underlay-padding': 16,
          events: 'no',
          'z-index': 999,
        },
      },
      {
        selector: '.action-badge.layered-action-badge',
        style: {
          label: 'data(graphLabel)',
          width: 'data(badgeWidth)',
          height: 'data(badgeHeight)',
          shape: 'ellipse',
          'background-color': 'data(badgeColor)',
          'background-opacity': 0.88,
          'background-image': 'none',
          'border-width': 1.25,
          'border-color': 'rgba(255, 255, 255, 0.42)',
          color: '#ffffff',
          'font-size': 'data(badgeFontSize)',
          'font-weight': 800,
          'text-valign': 'center',
          'text-halign': 'center',
          'text-margin-y': 0,
          'text-margin-x': 0,
          'text-outline-width': 0,
          'text-background-opacity': 0,
          'underlay-opacity': 0,
          events: 'no',
          'z-index': 1000,
        },
      },
      {
        selector: '.node-starter.graph-icon-node, .node-starter.graph-svg-icon-node',
        style: {
          width: 64,
          height: 64,
          'border-width': 1.75,
          'border-color': layeredNodeBorderColor,
          'underlay-opacity': 0,
        },
      },
      {
        selector: '.automated-root-halo',
        style: {
          width: 100,
          height: 100,
          'background-color': 'rgba(53,239,132,0.02)',
          'border-color': 'rgba(93,245,157,0.28)',
          'underlay-opacity': 0,
        },
      },
      {
        selector: '.hierarchy-root-halo',
        style: {
          width: 72,
          height: 72,
          'background-color': 'rgba(53,239,132,0.02)',
          'border-color': 'rgba(93,245,157,0.3)',
          'border-width': 1.25,
          'border-style': 'dotted',
          'underlay-opacity': 0,
        },
      },
      {
        selector: '.layered-root-halo',
        style: {
          width: 72,
          height: 72,
          'background-color': 'rgba(53,239,132,0.02)',
          'border-color': 'rgba(93,245,157,0.28)',
          'border-width': 1.25,
          'border-style': 'dotted',
          'underlay-opacity': 0,
        },
      },
      {
        selector: '.layered-hero-root',
        style: {
          width: 64,
          height: 64,
          'border-width': 0,
          'underlay-opacity': 0,
          'underlay-padding': 0,
        },
      },
      {
        selector: '.node-ai_analysis',
        style: {
          'border-color': '#f7c948',
          'underlay-color': '#f7c948',
          shape: 'round-diamond',
        },
      },
      { selector: '.tier-tier1', style: { opacity: 1 } },
      { selector: '.tier-tier2', style: { opacity: 0.95 } },
      { selector: '.tier-tier3', style: { opacity: 0.88 } },
      {
        selector: 'node:selected',
        style: {
          'overlay-color': '#49d17c',
          'overlay-opacity': 0.15,
          'overlay-padding': 10,
          'border-width': 5,
        },
      },
      {
        selector: 'edge:selected',
        style: {
          opacity: 1,
          width: 3.6,
          'line-color': '#4de584',
          'target-arrow-color': '#4de584',
          label: 'data(fullLabel)',
        },
      },
      {
        selector: '.graph-icon-node',
        style: {
          label: 'data(graphIcon)',
          color: '#ffffff',
          'font-family': 'bootstrap-icons',
          'font-weight': 400,
          'font-size': 'data(iconFontSize)',
          'text-valign': 'center',
          'text-halign': 'center',
          'text-wrap': 'none',
          'text-max-width': 999,
          'text-margin-y': 0,
          'text-background-opacity': 0,
          shape: 'rectangle',
          'background-color': 'data(iconColor)',
          'background-image': 'none',
          'background-opacity': layeredIconBgOpacity,
          'border-width': 1.75,
          'border-color': layeredNodeBorderColor,
          'border-opacity': layeredNodeBorderOpacity,
          padding: 0,
          'underlay-opacity': 0,
          'underlay-padding': 0,
          opacity: 1,
        },
      },
      {
        selector: 'node.graph-svg-icon-node.graph-photo-icon-node',
        style: {
          'background-color': '#ffffff',
          'background-fit': 'contain',
          'background-width': '78%',
          'background-height': '78%',
          'background-opacity': 1,
        },
      },
      {
        selector: '.graph-svg-icon-node',
        style: {
          label: '',
          'text-background-opacity': 0,
          shape: 'rectangle',
          'background-color': 'data(iconColor)',
          'background-image': 'data(iconSvg)',
          'background-fit': 'contain',
          'background-clip': 'none',
          'background-width': '58%',
          'background-height': '58%',
          'background-opacity': layeredIconBgOpacity,
          'border-width': 1.75,
          'border-color': layeredNodeBorderColor,
          'border-opacity': layeredNodeBorderOpacity,
          padding: 0,
          'underlay-opacity': 0,
          'underlay-padding': 0,
          opacity: 1,
        },
      },
      {
        selector:
          '.graph-icon-node.node-root, .graph-icon-node.hero-root, .graph-icon-node.layered-hero-root, .graph-icon-node.node-starter, .graph-icon-node.node-actionable, .graph-icon-node.node-action-running, .graph-icon-node.node-action-new, .graph-icon-node.node-passive, .graph-icon-node.role-confirmed, .graph-icon-node.role-candidate, .graph-icon-node.role-supporting, .graph-icon-node.tier-tier1, .graph-icon-node.tier-tier2, .graph-icon-node.tier-tier3, .graph-icon-node.node-ai_analysis',
        style: {
          'background-color': 'data(iconColor)',
          'background-image': 'none',
          'background-opacity': layeredIconBgOpacity,
          label: 'data(graphIcon)',
          'font-family': 'bootstrap-icons',
          'font-weight': 400,
          color: '#ffffff',
          'border-width': 1.75,
          'border-color': layeredNodeBorderColor,
          'border-opacity': layeredNodeBorderOpacity,
          'underlay-opacity': 0,
          opacity: 1,
          shape: 'rectangle',
        },
      },
      {
        selector:
          '.graph-svg-icon-node.node-root, .graph-svg-icon-node.hero-root, .graph-svg-icon-node.layered-hero-root, .graph-svg-icon-node.node-starter, .graph-svg-icon-node.node-actionable, .graph-svg-icon-node.node-action-running, .graph-svg-icon-node.node-action-new, .graph-svg-icon-node.node-passive, .graph-svg-icon-node.role-confirmed, .graph-svg-icon-node.role-candidate, .graph-svg-icon-node.role-supporting, .graph-svg-icon-node.tier-tier1, .graph-svg-icon-node.tier-tier2, .graph-svg-icon-node.tier-tier3, .graph-svg-icon-node.node-ai_analysis',
        style: {
          'background-color': 'data(iconColor)',
          'background-image': 'data(iconSvg)',
          'background-fit': 'contain',
          'background-width': '58%',
          'background-height': '58%',
          'background-opacity': layeredIconBgOpacity,
          label: '',
          'border-width': 1.75,
          'border-color': layeredNodeBorderColor,
          'border-opacity': layeredNodeBorderOpacity,
          'underlay-opacity': 0,
          opacity: 1,
          shape: 'rectangle',
        },
      },
      {
        selector:
          '.graph-icon-node.node-root, .graph-icon-node.hero-root, .graph-icon-node.layered-hero-root, .graph-icon-node.hierarchy-root, .graph-icon-node.node-starter, .graph-svg-icon-node.node-root, .graph-svg-icon-node.hero-root, .graph-svg-icon-node.layered-hero-root, .graph-svg-icon-node.hierarchy-root, .graph-svg-icon-node.node-starter',
        style: {
          width: 64,
          height: 64,
          'font-size': 26,
          'background-opacity': this.isLightTheme ? 0.52 : 0.22,
        },
      },
      {
        selector: 'node.graph-icon-node:selected, node.graph-svg-icon-node:selected',
        style: {
          'border-width': 2.5,
          'border-color': 'data(iconColor)',
          'border-opacity': 1,
          'overlay-opacity': 0.12,
          'overlay-padding': 6,
        },
      },
      {
        selector:
          '.layered-node.layered-focus-dim.node-actionable, .layered-node.layered-focus-dim.node-passive, .layered-node.layered-focus-dim.role-confirmed, .layered-node.layered-focus-dim.role-candidate, .layered-node.layered-focus-dim.role-supporting',
        style: {
          opacity: 0.1,
        },
      },
      {
        selector:
          '.layered-node.layered-focus-active.node-root, .layered-node.layered-focus-active.node-actionable, .layered-node.layered-focus-active.node-passive',
        style: {
          opacity: 1,
          'z-index': 30,
        },
      },
      {
        selector: 'node.graph-icon-node.icon-hover, node.graph-svg-icon-node.icon-hover',
        style: {
          'border-width': 2.75,
          'border-color': 'data(iconColor)',
          'border-opacity': 0.85,
          'underlay-color': 'data(iconColor)',
          'underlay-opacity': 0.58,
          'underlay-padding': 12,
          'z-index': 25,
        },
      },
      {
        selector: '.layered-edge.layered-focus-dim',
        style: {
          opacity: 0.05,
        },
      },
      {
        selector: '.layered-edge.layered-focus-active',
        style: {
          opacity: 0.95,
          width: 'mapData(confidence, 0.5, 1, 1.25, 2.35)',
          'target-arrow-color': 'data(edgeColor)',
          'arrow-scale': 0.8,
        },
      },
      {
        selector: '.layer-ring.layered-focus-dim, .root-halo.layered-focus-dim, .layer-ring-label.layered-focus-dim, .sector-cluster-label.layered-focus-dim',
        style: {
          opacity: 0.08,
        },
      },
    ];
  }

  private getLayoutConfig(): any {
    return {
      name: 'preset',
      animate: false,
      fit: false,
      padding: this.canvasDocked ? 72 : Math.max(46, this.getGraphLayoutGutter()),
    };
  }

  private getFittableGraphElements(): any {
    if (!this.cy) return null;
    return this.cy.elements().filter((element: any) => {
      if (element.hidden()) return false;
      const data = element.data?.() || {};
      return !data.isActionBadge && !data.isRootHalo && !data.isLayerRing;
    });
  }

  private scheduleFitVisibleGraph(): void {
    if (this.fitGraphTimer) {
      clearTimeout(this.fitGraphTimer);
    }
    this.fitGraphTimer = setTimeout(() => {
      this.fitGraphTimer = null;
      this.fitVisibleGraph();
    }, 32);
  }

  private fitVisibleGraph(): void {
    if (!this.cy) return;
    this.cy.resize();
    if (!this.isLayeredView && this.rootNode?.id) {
      const root = this.cy.getElementById(this.rootNode.id);
      if (root?.length && !root.hidden()) {
        this.normalizeInvestigationBands(root.position());
      }
    }
    const elements = this.getFittableGraphElements();
    if (!elements?.length) return;
    const padding = this.isLayeredView ? 36 : this.isDrillDown ? 58 : 64;
    this.cy.fit(elements, padding);
  }

  private escapeCytoscapeId(id: string): string {
    return String(id).replace(/([ #;?%&,.+*~':"!^$[\]()=>|/@])/g, '\\$1');
  }

  private applyInitialGraphViewport(): void {
    if (!this.cy || !this.rootNode?.id) return;
    if (this.isLayeredView) {
      this.fitVisibleGraph();
      return;
    }
    const root = this.cy.getElementById(this.rootNode.id);
    if (!root?.length) return;
    setTimeout(() => {
      if (!this.cy) return;
      const container = this.graphCanvas?.nativeElement;
      if (!container) return;
      const width = container.clientWidth || 1200;
      const nodeCount = this.graphPayload?.nodes?.length || 0;
      const zoom = this.isDrillDown
        ? nodeCount > 32 ? 0.68 : nodeCount > 20 ? 0.76 : 0.82
        : nodeCount > 44 ? 0.66 : nodeCount > 28 ? 0.74 : 0.82;
      const rootPosition = root.position();
      this.normalizeInvestigationBands(rootPosition);
      this.cy.zoom(zoom);
      this.cy.pan({
        x: width / 2 - rootPosition.x * zoom,
        y: 176 - rootPosition.y * zoom,
      });
    }, 80);
  }

  private normalizeInvestigationBands(rootPosition: { x: number; y: number }): void {
    if (!this.cy || this.isLayeredView) return;
    this.cy.nodes().forEach((node: any) => {
      if (node.data('isActionBadge') || node.data('isRootHalo') || node.id() === this.rootNode?.id) return;
      const metadata = node.data('metadata') || {};
      const layer = Math.max(1, Number(metadata.layer) || 1);
      const bandOffset = this.isDrillDown
        ? layer === 1 ? 298 : 298 + (layer - 1) * 198
        : layer === 1 ? 284 : 284 + (layer - 1) * 176;
      node.position({
        x: node.position('x'),
        y: rootPosition.y + bandOffset,
      });
    });
    this.syncActionBadges();
  }

  private get presentedDrillDownRoot(): any | null {
    if (!this.isDrillDown || !this.rootNode || this.rootNode.type !== 'starter') {
      return null;
    }
    const firstSearch = this.sortActionsByTime(this.actionHistory).find(
      (action) =>
        action.action_type === 'osint_search' &&
        ['phone', 'email', 'username', 'name'].includes(action.query_type) &&
        action.query_value,
    );
    const fallbackRootChild = this.findPresentedRootChild();
    if (!firstSearch && !fallbackRootChild) return null;
    const fallbackType =
      fallbackRootChild?.type === 'person'
        ? 'name'
        : fallbackRootChild?.type === 'username'
          ? 'username'
          : fallbackRootChild?.type;
    const fallbackValue = fallbackRootChild?.label || fallbackRootChild?.metadata?.queryValue;
    const searchQueryType = firstSearch?.query_type ? String(firstSearch.query_type) : String(fallbackType || 'phone');
    const searchQueryValue = firstSearch?.query_value || fallbackValue;
    if (!searchQueryValue) return null;
    const queryType = searchQueryType;
    const type = queryType === 'name' ? 'person' : queryType === 'username' ? 'username' : queryType;
    return {
      ...this.rootNode,
      type,
      label: searchQueryValue,
      role: 'confirmed',
      attributionStatus: queryType === 'phone' ? 'active' : 'confirmed',
      evidenceTier: 'tier1',
      metadata: {
        ...(this.rootNode.metadata || {}),
        queryType,
        queryValue: searchQueryValue,
        presentedFromStarter: true,
      },
      confidence: Math.max(Number(this.rootNode.confidence || 0), 0.96),
    };
  }

  private getPresentationNode(node: any): any {
    const rootId = this.rootNode?.id;
    if (node?.id === rootId) {
      return this.presentedDrillDownRoot || node;
    }
    return node;
  }

  private findPresentedRootChild(): any | null {
    const rootId = this.rootNode?.id;
    if (!rootId) return null;
    const nodes = this.graphPayload?.nodes || [];
    const nodeById = new Map(nodes.map((node: any) => [node.id, node]));
    const preferredEdges = (this.graphPayload?.edges || []).filter(
      (edge: any) => edge.source === rootId || edge.target === rootId,
    );
    const candidates = preferredEdges
      .map((edge: any) => nodeById.get(edge.source === rootId ? edge.target : edge.source))
      .filter((node: any) => node && ['phone', 'email', 'username'].includes(node.type));

    return (
      candidates.sort((left: any, right: any) => {
        const leftScore = (left.type === this.investigation?.root_type ? 2 : 0) + Number(left.confidence || 0);
        const rightScore = (right.type === this.investigation?.root_type ? 2 : 0) + Number(right.confidence || 0);
        return rightScore - leftScore;
      })[0] || null
    );
  }

  private shouldUseBrandIcon(node: any): boolean {
    const type = String(node?.type || '').toLowerCase();
    return type === 'social_profile' || type === 'osint_source';
  }

  private decorateNode(node: any): any {
    const presentedNode = this.getPresentationNode(node);
    const brand = this.shouldUseBrandIcon(presentedNode) ? this.inferBrand(presentedNode) : null;
    const visual = this.getNodeVisual(presentedNode, brand);
    const rootId = this.rootNode?.id;
    const isRoot = node?.id === rootId;
    const isLayerHero = isLayerSourceHeroNode(node, rootId);
    const isSourceHub = isSourceTypeHubNode(node);
    const isHeroNode = isRoot || isLayerHero || isSourceHub;
    let label = presentedNode?.label || node?.label || node?.id || '';
    if (isLayerHero && !/\([^)]+\)\s*$/.test(label)) {
      const value = resolveNodeDisplayValue(presentedNode);
      label = `${value} (${resolveSourceTypeLabel(resolveNodeSourceType(node))})`;
    }
    const graphIconMeta = resolveLayeredGraphIcon(presentedNode, isHeroNode);
    const labelMaxLength = isSourceHub || isLayerHero ? 46 : 34;

    return {
      ...presentedNode,
      id: node.id,
      originalType: node.type,
      imageUrl: presentedNode?.imageUrl || this.getNodeImageSearchUrl(presentedNode),
      iconSvg: graphIconMeta?.iconSvg || '',
      graphIcon: graphIconMeta?.graphIcon || '',
      iconColor: graphIconMeta?.iconColor || visual.iconBg,
      iconFontSize: graphIconMeta?.iconFontSize || 18,
      iconTextColor: '#ffffff',
      graphLabel: '',
      displayLabel: this.formatGraphLabel(label, labelMaxLength),
      nodeColor: graphIconMeta?.iconColor || visual.iconBg,
      nodeBorderColor: 'transparent',
      nodeGlowColor: 'transparent',
      nodeShape: 'rectangle',
      labelBgColor: visual.labelBgColor,
      labelSize: isHeroNode ? 11 : 9,
      nodeSize: isRoot ? 64 : isSourceHub ? 58 : isLayerHero ? 60 : this.getNodeSize(node),
      nodeBorderWidth: 0,
      layeredBorderWidth: 0,
      actionBorderWidth:
        !isSourceHub && this.hasAvailableActions(node) ? (isHeroNode ? 4 : 3) : 2,
    };
  }

  private decorateEdge(edge: any): any {
    const label = this.formatRelationshipLabel(edge?.label || edge?.type || '');
    const role = String(edge?.role || 'supporting').toLowerCase();
    const edgeType = String(edge?.type || '').toLowerCase();
    const edgeColor =
      role === 'confirmed'
        ? this.isLightTheme ? '#16a34a' : '#22c55e'
        : role === 'candidate'
          ? this.isLightTheme ? '#2563eb' : '#3b82f6'
          : edgeType === 'appears_in_leak'
            ? this.isLightTheme ? '#a855f7' : '#c084fc'
            : edgeType === 'found_in'
              ? this.isLightTheme ? '#14b8a6' : '#2dd4bf'
              : edgeType === 'searched' || edgeType === 'has_image' || edgeType === 'matched_face'
                ? this.isLightTheme ? '#22c55e' : '#45e58b'
                : this.isLightTheme ? '#9db0c3' : '#64748b';
    return {
      ...edge,
      edgeRole: role,
      graphLabel: this.isDrillDown && Number(edge?.confidence || 0) >= 0.9 ? label : '',
      fullLabel: label,
      edgeColor,
    };
  }

  private stopGraphFlowAnimation(): void {
    if (this.layeredFlowAnimationId !== null) {
      cancelAnimationFrame(this.layeredFlowAnimationId);
      this.layeredFlowAnimationId = null;
    }
  }

  private startGraphFlowAnimation(): void {
    this.stopGraphFlowAnimation();
    if (!this.cy) return;

    const edgeSelector = this.isLayeredView ? '.layered-edge' : '.hierarchy-edge';

    const tick = () => {
      if (!this.cy) {
        this.layeredFlowAnimationId = null;
        return;
      }

      this.layeredFlowDashOffset = (this.layeredFlowDashOffset - 1.2 + 44) % 44;
      this.cy
        .style()
        .selector(edgeSelector)
        .style('line-dash-offset', this.layeredFlowDashOffset)
        .update();

      this.layeredFlowAnimationId = requestAnimationFrame(tick);
    };

    this.layeredFlowAnimationId = requestAnimationFrame(tick);
  }

  private isSearchFlowEdge(edgeType: string): boolean {
    return [
      'searched',
      'found_in',
      'appears_in_leak',
      'has_phone',
      'has_email',
      'has_image',
      'matched_face',
      'linked_to',
      'has_name',
      'has_profile',
      'has_username',
      'has_address',
      'has_location',
    ].includes(String(edgeType || '').toLowerCase());
  }

  private resolveVisibleEdgeEndpoint(nodeId: string): string {
    const rootId = this.rootNode?.id;
    const presentedRoot = this.presentedDrillDownRoot;
    if (!rootId || !presentedRoot || nodeId === rootId) {
      return nodeId;
    }

    const presentedRootValue = this.normalizeEntityValue(
      presentedRoot.label || presentedRoot.metadata?.queryValue || '',
    );
    const presentedRootType = presentedRoot.metadata?.queryType || presentedRoot.type;
    const node = (this.graphPayload?.nodes || []).find((item: any) => item.id === nodeId);
    if (
      node &&
      node.type === presentedRootType &&
      this.normalizeEntityValue(node.label || node.metadata?.queryValue || '') === presentedRootValue
    ) {
      return rootId;
    }

    return nodeId;
  }

  private resolveLayeredEdgeFlow(edge: any): { source: string; target: string } {
    const sourceId = String(edge?.source || '');
    const targetId = String(edge?.target || '');
    if (!sourceId || !targetId || sourceId === targetId) {
      return { source: sourceId, target: targetId };
    }

    const rootId = this.rootNode?.id;
    if (rootId) {
      if (sourceId === rootId) {
        return { source: sourceId, target: targetId };
      }
      if (targetId === rootId) {
        return { source: targetId, target: sourceId };
      }
    }

    const parentOfSource = this.searchParentById.get(sourceId);
    const parentOfTarget = this.searchParentById.get(targetId);
    const nodes = this.getDisplayGraphPayload().nodes || [];
    const sourceNode = nodes.find((node: any) => node.id === sourceId);
    const targetNode = nodes.find((node: any) => node.id === targetId);
    if (targetNode?.metadata?.parentNodeId === sourceId || parentOfTarget === sourceId) {
      return { source: sourceId, target: targetId };
    }
    if (sourceNode?.metadata?.parentNodeId === targetId || parentOfSource === targetId) {
      return { source: targetId, target: sourceId };
    }

    const sourceLayer = this.getNodeLayer({ id: sourceId });
    const targetLayer = this.getNodeLayer({ id: targetId });
    if (sourceLayer < targetLayer) {
      return { source: sourceId, target: targetId };
    }
    if (targetLayer < sourceLayer) {
      return { source: targetId, target: sourceId };
    }

    return { source: sourceId, target: targetId };
  }

  private inferBrand(node: any): string | null {
    const platformBrand = resolveBrandKeyFromPlatform(
      node?.metadata?.platform || node?.metadata?.sourceName || node?.label,
    );
    if (platformBrand) {
      return platformBrand;
    }

    const candidates = [
      node?.metadata?.platform,
      node?.metadata?.sourceName,
      node?.metadata?.sourcePlatform,
      node?.metadata?.profileUrl,
      ...(node?.metadata?.urls || []),
      ...(node?.sourceReferences || []).map((ref: any) => ref?.url || ref?.label),
      node?.label,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (candidates.includes('gmail') || candidates.includes('googlemail')) return 'gmail';
    if (candidates.includes('outlook') || candidates.includes('hotmail') || candidates.includes('live.com')) return 'outlook';
    if (candidates.includes('yahoo')) return 'yahoo';
    if (candidates.includes('microsoft')) return 'microsoft';
    if (candidates.includes('apple') || candidates.includes('icloud')) return 'apple';
    if (candidates.includes('linkedin') || candidates.includes('licdn')) return 'linkedin';
    if (candidates.includes('instagram')) return 'instagram';
    if (candidates.includes('facebook') || candidates.includes('fbcdn')) return 'facebook';
    if (candidates.includes('foursquare') || candidates.includes('4sqi')) return 'foursquare';
    if (candidates.includes('eyecon')) return 'eyecon';
    if (candidates.includes('twitter') || candidates.includes(' x ') || candidates.includes('x.com')) return 'x';
    if (candidates.includes('whatsapp')) return 'whatsapp';
    if (candidates.includes('telegram')) return 'telegram';
    if (candidates.includes('truecaller')) return 'truecaller';
    if (candidates.includes('skype')) return 'skype';
    if (candidates.includes('snapchat')) return 'snapchat';
    if (candidates.includes('github')) return 'github';
    if (candidates.includes('gitlab')) return 'gitlab';
    if (candidates.includes('youtube')) return 'youtube';
    if (candidates.includes('tiktok')) return 'tiktok';
    if (candidates.includes('quora')) return 'quora';
    if (candidates.includes('medium')) return 'medium';
    if (candidates.includes('reddit')) return 'reddit';
    if (candidates.includes('pinterest')) return 'pinterest';
    if (candidates.includes('discord')) return 'discord';
    if (candidates.includes('dropbox')) return 'dropbox';
    if (candidates.includes('canva')) return 'canva';
    if (candidates.includes('myfitnesspal')) return 'myfitnesspal';
    if (candidates.includes('whoxy')) return 'whoxy';
    if (candidates.includes('quillbot')) return 'quillbot';
    if (candidates.includes('naukri')) return 'naukri';
    if (candidates.includes('maps') || candidates.includes('google map')) return 'maps';
    return null;
  }

  private formatGraphLabel(value: string, maxLength: number): string {
    const label = String(value || '').replace(/\s+/g, ' ').trim();
    if (label.length <= maxLength) return label;
    return `${label.slice(0, Math.max(8, maxLength - 1)).trim()}...`;
  }

  private formatRelationshipLabel(value: string): string {
    return String(value || '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase()
      .slice(0, 24);
  }

  private formatTypeLabel(value: string): string {
    return String(value || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  private getNodeSize(node: any): number {
    const rootId = this.rootNode?.id;
    if (node?.id === rootId) return 64;
    if (isSourceTypeHubNode(node)) return 58;
    if (isLayerSourceHeroNode(node, rootId)) return 60;
    const confidence = Number(node?.confidence || 0);
    if (['phone', 'email', 'person'].includes(node?.type)) return confidence >= 0.86 ? 50 : 46;
    if (['social_profile', 'website', 'image', 'face_result'].includes(node?.type)) return 46;
    if (['company', 'leak_source', 'osint_source', 'address', 'location'].includes(node?.type)) return 44;
    return 42;
  }

  private calculateGraphPositions(nodes: any[]): Record<string, { x: number; y: number }> {
    if (this.isLayeredView) {
      return this.calculateLayeredGraphPositions(nodes);
    }

    return this.calculateHierarchicalGraphPositions(nodes);
  }

  private calculateHierarchicalGraphPositions(nodes: any[]): Record<string, { x: number; y: number }> {
    const root = this.rootNode || nodes[0];
    const rootId = root?.id;
    const nodeIds = new Set(nodes.map((node) => node.id));
    const edges = (this.getDisplayGraphPayload().edges || []).filter(
      (edge: any) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
    );
    const children = new Map<string, string[]>();
    edges.forEach((edge: any) => {
      if (!children.has(edge.source)) children.set(edge.source, []);
      children.get(edge.source)?.push(edge.target);
    });

    const depthById = new Map<string, number>();
    if (rootId) {
      depthById.set(rootId, 0);
      const queue = [rootId];
      while (queue.length) {
        const current = queue.shift() as string;
        const depth = depthById.get(current) || 0;
        (children.get(current) || []).forEach((childId) => {
          if (!depthById.has(childId)) {
            depthById.set(childId, depth + 1);
            queue.push(childId);
          }
        });
      }
    }

    nodes.forEach((node) => {
      const explicitLayer = Number(node?.metadata?.layer);
      if (Number.isFinite(explicitLayer)) {
        depthById.set(node.id, node.id === rootId ? 0 : Math.max(1, explicitLayer));
        return;
      }
      if (!depthById.has(node.id)) depthById.set(node.id, 1);
    });

    const groups = new Map<number, any[]>();
    nodes.forEach((node) => {
      const depth = Math.min(depthById.get(node.id) || 1, 6);
      if (!groups.has(depth)) groups.set(depth, []);
      groups.get(depth)?.push(node);
    });

    const canvasWidth = this.graphCanvas?.nativeElement?.clientWidth || 1200;
    const usableWidth = Math.max(920, canvasWidth - this.getGraphLayoutGutter());
    const centerX = canvasWidth / 2;
    const horizontalGap = 30;
    const rowPitch = 112;
    const top = this.canvasDocked ? 118 : 108;
    const positions: Record<string, { x: number; y: number }> = {};

    Array.from(groups.entries())
      .sort(([left], [right]) => left - right)
      .forEach(([depth, group]) => {
        const sortedGroup = group.sort(
          (left, right) =>
            this.graphTypeWeight(left.type) - this.graphTypeWeight(right.type) ||
            String(left.label || '').localeCompare(String(right.label || '')),
        );
        const sizes = sortedGroup.map((node) => this.getNodeSize(node));
        const count = sortedGroup.length;
        let gap = horizontalGap;
        const totalNodeWidth = sizes.reduce((sum, size) => sum + size, 0);
        let totalSpan = totalNodeWidth + Math.max(0, count - 1) * gap;

        if (count > 1 && totalSpan > usableWidth) {
          gap = Math.max(18, (usableWidth - totalNodeWidth) / (count - 1));
          totalSpan = totalNodeWidth + (count - 1) * gap;
        }

        let cursor = centerX - totalSpan / 2;
        const y = top + depth * rowPitch;

        sortedGroup.forEach((node, index) => {
          const size = sizes[index];
          positions[node.id] = {
            x: cursor + size / 2,
            y,
          };
          cursor += size + (index < count - 1 ? gap : 0);
        });
      });

    return positions;
  }

  private calculateLayeredGraphPositions(nodes: any[]): Record<string, { x: number; y: number }> {
    const root = this.rootNode || nodes[0];
    const rootId = root?.id;
    const positions: Record<string, { x: number; y: number }> = {};
    this.layeredRings = [];

    if (!rootId) return positions;

    const canvasWidth = this.graphCanvas?.nativeElement?.clientWidth || 1200;
    const canvasHeight = this.graphCanvas?.nativeElement?.clientHeight || 720;
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    positions[rootId] = { x: centerX, y: centerY };

    const minDim = Math.min(canvasWidth, canvasHeight);
    const scale = Math.max(0.72, Math.min(1.15, minDim / 900));
    const nodeGap = 28 * scale;
    const ringGap = 125 * scale;

    const groups = new Map<number, any[]>();
    nodes.forEach((node) => {
      const layer = this.getNodeLayer(node);
      if (!groups.has(layer)) groups.set(layer, []);
      groups.get(layer)?.push(node);
    });

    let previousRadius = (this.getNodeSize(root) / 2) + 40 * scale;

    Array.from(groups.entries())
      .sort(([left], [right]) => left - right)
      .forEach(([layer, group]) => {
        if (layer === 0) return;

        const ringNodes = group.filter((node) => node.id !== rootId);
        if (!ringNodes.length) return;

        const orderedRingNodes = orderLayerNodesForSourceHubs(ringNodes);
        const avgNodeSize =
          orderedRingNodes.reduce((sum, node) => sum + this.getNodeSize(node), 0) /
          orderedRingNodes.length;
        const minRingRadius = this.minLayerRadiusForCount(
          orderedRingNodes.length,
          avgNodeSize,
          nodeGap,
          scale,
        );
        const radius = Math.max(previousRadius + ringGap, minRingRadius);
        this.layeredRings.push({ layer, radius });
        previousRadius = radius;

        orderedRingNodes.forEach((node, index) => {
          const angle = -Math.PI / 2 + (index / orderedRingNodes.length) * Math.PI * 2;
          positions[node.id] = {
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius,
          };
        });
      });

    return positions;
  }

  private minLayerRadiusForCount(
    count: number,
    nodeSize: number,
    nodeGap: number,
    scale: number,
  ): number {
    const footprint = (nodeSize + nodeGap) * 1.2;
    if (count <= 1) {
      return Math.max(96 * scale, footprint * 1.8);
    }
    const circumference = count * footprint;
    return Math.max(110 * scale, circumference / (2 * Math.PI));
  }

  private applyLayeredSelectionFocus(focusNodeId: string | null): void {
    if (!this.cy || !this.isLayeredView) return;

    this.cy.nodes().removeClass('layered-focus-active layered-focus-dim');
    this.cy.edges().removeClass('layered-focus-active layered-focus-dim');

    if (!focusNodeId) return;

    const focusIds = new Set<string>([focusNodeId]);
    this.cy.edges().forEach((edge) => {
      const sourceId = edge.source().id();
      const targetId = edge.target().id();
      if (sourceId === focusNodeId) focusIds.add(targetId);
      if (targetId === focusNodeId) focusIds.add(sourceId);
    });

    this.cy.nodes().forEach((node) => {
      const data = node.data();
      if (data?.isLayerRing || data?.isRootHalo || data?.isActionBadge) {
        node.addClass('layered-focus-dim');
        return;
      }
      if (!node.hasClass('layered-node')) return;
      if (focusIds.has(node.id())) {
        node.addClass('layered-focus-active');
      } else {
        node.addClass('layered-focus-dim');
      }
    });

    this.cy.edges().forEach((edge) => {
      if (!edge.hasClass('layered-edge')) return;
      const sourceId = edge.source().id();
      const targetId = edge.target().id();
      if (sourceId === focusNodeId || targetId === focusNodeId) {
        edge.addClass('layered-focus-active');
      } else {
        edge.addClass('layered-focus-dim');
      }
    });
  }

  private graphTypeWeight(type: string): number {
    const order: Record<string, number> = {
      phone: 1,
      email: 2,
      username: 3,
      social_profile: 4,
      person: 5,
      image: 6,
      leak_source: 7,
      osint_source: 7.5,
      company: 8,
      website: 9,
      address: 10,
      location: 11,
      wallet: 12,
      identifier: 13,
      ai_analysis: 14,
      face_result: 15,
    };
    return order[type] || 99;
  }

  private ensureDefaultSelection(): void {
    if (!this.isDrillDown || this.selectedEntity || !this.rootNode) return;
    this.selectedEntity = this.decorateNode(this.rootNode);
    this.selectedEntityKind = 'node';
    this.resetNodeActionSelection();
  }

  private getNodeVisual(node: any, brand: string | null): any {
    const type = node?.type || 'unknown';
    const visualsByType: Record<string, any> = {
      phone: {
        nodeColor: '#143a2c',
        nodeBorderColor: '#49d17c',
        nodeGlowColor: '#1ca45a',
        labelBgColor: '#0d2018',
        nodeShape: 'round-rectangle',
        iconBg: '#1fc56d',
        iconPath: 'M35 29c1 6 4 12 9 18 5 6 11 10 17 12l6-6c1-1 3-2 4-1l11 4c2 1 3 2 3 4v13c0 2-2 4-4 4C55 77 19 41 19 15c0-2 2-4 4-4h13c2 0 3 1 4 3l4 11c1 1 0 3-1 4l-8 0z',
        iconFill: 'none',
        iconStroke: '#ffffff',
        iconStrokeWidth: 4,
      },
      email: {
        nodeColor: '#102c46',
        nodeBorderColor: '#56b3ff',
        nodeGlowColor: '#1a78d6',
        labelBgColor: '#0b1c2d',
        nodeShape: 'round-rectangle',
        iconBg: '#1f7be6',
        iconPath: 'M20 28h56a4 4 0 0 1 4 4v32a4 4 0 0 1-4 4H20a4 4 0 0 1-4-4V32a4 4 0 0 1 4-4zm0 4 28 20 28-20',
        iconFill: 'none',
        iconStroke: '#ffffff',
        iconStrokeWidth: 3.5,
      },
      person: {
        nodeColor: '#3f2414',
        nodeBorderColor: '#f4a259',
        nodeGlowColor: '#d78134',
        labelBgColor: '#23140b',
        nodeShape: 'ellipse',
        iconBg: '#f4a259',
        iconPath: 'M48 22a12 12 0 1 1 0 24 12 12 0 0 1 0-24zm-20 46c2-12 12-18 20-18s18 6 20 18',
        iconFill: 'none',
        iconStroke: '#1f1309',
        iconStrokeWidth: 4,
      },
      social_profile: {
        nodeColor: '#22153a',
        nodeBorderColor: '#b586ff',
        nodeGlowColor: '#7f4dd0',
        labelBgColor: '#130c21',
        nodeShape: 'round-hexagon',
        iconBg: '#7e57c2',
        iconText: 'SOC',
        iconTextColor: '#ffffff',
      },
      image: {
        nodeColor: '#351449',
        nodeBorderColor: '#d76cff',
        nodeGlowColor: '#8e38b3',
        labelBgColor: '#190922',
        nodeShape: 'diamond',
        iconBg: '#b447de',
        iconPath: 'M22 28h52a6 6 0 0 1 6 6v28a6 6 0 0 1-6 6H22a6 6 0 0 1-6-6V34a6 6 0 0 1 6-6zm12 30 10-11 8 8 9-12 11 15M34 40a5 5 0 1 0 0-.01',
        iconFill: 'none',
        iconStroke: '#ffffff',
        iconStrokeWidth: 3.5,
      },
      company: {
        nodeColor: '#153739',
        nodeBorderColor: '#40d4c4',
        nodeGlowColor: '#189a90',
        labelBgColor: '#0b1f20',
        nodeShape: 'round-rectangle',
        iconBg: '#1ea89e',
        iconPath: 'M26 76V26h20v14h24v36M36 76V58m0-10v-8m16 36V58m0-10v-8m16 36V58m0-10v-8',
        iconFill: 'none',
        iconStroke: '#ffffff',
        iconStrokeWidth: 3.5,
      },
      leak_source: {
        nodeColor: '#461516',
        nodeBorderColor: '#ff7a7a',
        nodeGlowColor: '#b74545',
        labelBgColor: '#260c0c',
        nodeShape: 'round-rectangle',
        iconBg: '#ef5350',
        iconPath: 'M48 16 18 72h60L48 16zm0 18v16m0 10v2',
        iconFill: 'none',
        iconStroke: '#ffffff',
        iconStrokeWidth: 4,
      },
      osint_source: {
        nodeColor: '#0f3330',
        nodeBorderColor: '#2dd4bf',
        nodeGlowColor: '#0f766e',
        labelBgColor: '#08201d',
        nodeShape: 'round-hexagon',
        iconBg: '#14b8a6',
        iconText: 'SRC',
        iconTextColor: '#ffffff',
      },
      address: {
        nodeColor: '#1b2347',
        nodeBorderColor: '#8da2ff',
        nodeGlowColor: '#586fd4',
        labelBgColor: '#0f1428',
        nodeShape: 'diamond',
        iconBg: '#5c6bc0',
        iconPath: 'M48 18c-11 0-20 8-20 19 0 14 20 39 20 39s20-25 20-39c0-11-9-19-20-19zm0 27a8 8 0 1 1 0-16 8 8 0 0 1 0 16z',
        iconFill: 'none',
        iconStroke: '#ffffff',
        iconStrokeWidth: 3.5,
      },
      website: {
        nodeColor: '#32231b',
        nodeBorderColor: '#c79b79',
        nodeGlowColor: '#8d6e63',
        labelBgColor: '#1c120d',
        nodeShape: 'round-octagon',
        iconBg: '#8d6e63',
        iconPath: 'M48 18c16 0 30 14 30 30S64 78 48 78 18 64 18 48 32 18 48 18zm0 0c8 8 12 18 12 30S56 70 48 78m0-60c-8 8-12 18-12 30s4 22 12 30m-24-30h48',
        iconFill: 'none',
        iconStroke: '#ffffff',
        iconStrokeWidth: 3,
      },
      username: {
        nodeColor: '#263614',
        nodeBorderColor: '#a7da4e',
        nodeGlowColor: '#6b9e2b',
        labelBgColor: '#16200b',
        nodeShape: 'ellipse',
        iconBg: '#7cb342',
        iconText: '@',
        iconTextSize: 40,
        iconTextColor: '#ffffff',
      },
      starter: {
        nodeColor: '#0f3d2b',
        nodeBorderColor: '#6ef5a0',
        nodeGlowColor: '#3ae17f',
        labelBgColor: '#0c2218',
        nodeShape: 'round-rectangle',
        iconBg: '#2bdd78',
        iconText: '+',
        iconTextSize: 46,
        iconTextColor: '#07141f',
      },
      ai_analysis: {
        nodeColor: '#3a2b12',
        nodeBorderColor: '#f7c948',
        nodeGlowColor: '#d89d2a',
        labelBgColor: '#211808',
        nodeShape: 'round-diamond',
        iconBg: '#f7c948',
        iconText: 'AI',
        iconTextSize: 30,
        iconTextColor: '#171208',
      },
      face_result: {
        nodeColor: '#12384a',
        nodeBorderColor: '#72c7ff',
        nodeGlowColor: '#3aa6ff',
        labelBgColor: '#061929',
        nodeShape: 'round-rectangle',
        iconBg: '#72c7ff',
        iconText: 'FR',
        iconTextSize: 30,
        iconTextColor: '#06131f',
      },
    };

    const brandVisuals: Record<string, Partial<any>> = BRAND_ICON_VISUALS;

    const base = visualsByType[type] || visualsByType['website'];
    const visual = {
      ...base,
      ...(brand ? brandVisuals[brand] || {} : {}),
    };
    if (this.isLightTheme) {
      visual.labelBgColor = this.getLightLabelBgColor(type);
    }
    return visual;
  }

  createIconDataUrl(visual: any, iconOnly = false): string {
    const text = this.escapeSvg(visual.iconText || '?');
    const accent = visual.iconBg || '#22c55e';
    const iconMarkup = visual.iconPath
      ? iconOnly
        ? `
        <path
          d="${visual.iconPath}"
          fill="none"
          stroke="${accent}"
          stroke-width="5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />`
        : `
        <path
          d="${visual.iconPath}"
          fill="${visual.iconFill || 'none'}"
          stroke="${visual.iconStroke || '#ffffff'}"
          stroke-width="${visual.iconStrokeWidth || 2.5}"
          stroke-linecap="round"
          stroke-linejoin="round"
        />`
      : `
        <text x="48" y="58"
          text-anchor="middle"
          font-family="Inter, Arial, sans-serif"
          font-size="${iconOnly ? Math.max(visual.iconTextSize || 0, 34) : visual.iconTextSize || (text.length >= 3 ? 24 : 34)}"
          font-weight="800"
          fill="${iconOnly ? accent : visual.iconTextColor || '#ffffff'}">${text}</text>`;

    const svg = iconOnly
      ? `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96">
          <g>${iconMarkup}</g>
        </svg>
      `.trim()
      : `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96">
          <defs>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="6" stdDeviation="6" flood-color="rgba(0,0,0,0.35)"/>
            </filter>
          </defs>
          <rect x="13" y="13" width="70" height="70" rx="18" fill="${visual.iconBg}" filter="url(#shadow)"/>
          <g transform="translate(0 0)">${iconMarkup}</g>
        </svg>
      `.trim();

    return this.toSvgDataUrl(svg);
  }

  private toSvgDataUrl(svg: string): string {
    if (typeof btoa !== 'undefined') {
      return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
    }
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  private toActionStatusRow(action: any): {
    label: string;
    status: string;
    nodeCount: number;
    error: string;
    message: string;
  } {
    const nodeCount = Array.isArray(action?.new_node_ids) ? action.new_node_ids.length : 0;
    const warnings = Array.isArray(action?.warnings)
      ? action.warnings.filter(Boolean).map((item: unknown) => String(item))
      : [];
    const status = action?.status || 'queued';
    let message = '';

    if (status === 'processing') {
      message = 'Search is running. Results will appear under this node when ready.';
    } else if (status === 'completed') {
      if (nodeCount > 0) {
        message = `${nodeCount} node${nodeCount === 1 ? '' : 's'} added below this node.`;
      } else if (action?.action_type === 'face_search') {
        message = warnings[0] || 'No high-probability face matches were found for this image.';
      } else {
        message = warnings[0] || 'The search completed but did not add new graph nodes.';
      }
    } else if (status === 'failed') {
      message = action?.error || 'The search failed. Existing graph data was kept.';
    } else if (status === 'warning') {
      message = warnings[0] || 'The search completed with warnings.';
    }

    return {
      label: this.formatActionLabel(action),
      status,
      nodeCount,
      error: action?.error || '',
      message,
    };
  }

  private formatActionLabel(action: any): string {
    const queryType = action?.query_type ? String(action.query_type) : '';
    const actionType = String(action?.action_type || 'action');
    const actionNames: Record<string, string> = {
      osint_search: `OSINT ${queryType || ''} search`,
      leak_search: `Leak ${queryType || ''} search`,
      face_search: 'Face search',
      ai_analysis: 'AI analysis',
    };
    const base = actionNames[actionType] || actionType.replace(/_/g, ' ');
    const mode = action?.ai_mode ? ` · ${action.ai_mode}` : '';
    return `${base.trim()}${mode}`;
  }

  formatStatusLabel(status: string): string {
    return String(status || 'queued')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  private findNodeLabel(nodeId: string): string {
    if (!nodeId) return '';
    const node = (this.graphPayload?.nodes || []).find((item: any) => item.id === nodeId);
    return node?.label || nodeId;
  }

  private appendPendingActionsToHistory(history: any[]): any[] {
    const merged = [...history];
    this.pendingLocalActions.forEach((pendingLocalAction) => {
      if (!merged.some((action) => action.action_id === pendingLocalAction.action_id)) {
        merged.push(pendingLocalAction);
      }
    });
    return merged;
  }

  private makeActionKey(payload: any): string {
    return [
      payload?.source_node_id || '',
      payload?.action_type || '',
      payload?.query_type || '',
      payload?.ai_mode || '',
    ].join('|');
  }

  private isActionRunningFor(
    sourceNodeId: string,
    actionType: string,
    queryType: string,
    aiMode?: string,
  ): boolean {
    const key = this.makeActionKey({
      source_node_id: sourceNodeId,
      action_type: actionType,
      query_type: queryType,
      ai_mode: aiMode || '',
    });
    if (this.runningActionKeys.has(key)) return true;
    if (
      this.pendingLocalActions.some(
        (pendingLocalAction) =>
          pendingLocalAction.status === 'processing' &&
          pendingLocalAction.source_node_id === sourceNodeId &&
          pendingLocalAction.action_type === actionType &&
          pendingLocalAction.query_type === queryType &&
          (!aiMode || pendingLocalAction.ai_mode === aiMode),
      )
    ) {
      return true;
    }
    return this.actionHistory.some((action) => {
      return (
        action.status === 'processing' &&
        action.source_node_id === sourceNodeId &&
        action.action_type === actionType &&
        action.query_type === queryType &&
        (!aiMode || action.ai_mode === aiMode)
      );
    });
  }

  private isNodeRunningAction(nodeId: string): boolean {
    if (!nodeId) return false;
    if (
      this.pendingLocalActions.some(
        (pendingLocalAction) =>
          pendingLocalAction.status === 'processing' &&
          pendingLocalAction.source_node_id === nodeId,
      )
    ) {
      return true;
    }
    return this.actionHistory.some(
      (action) => action.status === 'processing' && action.source_node_id === nodeId,
    );
  }

  private isRecentlyAddedNode(nodeId: string): boolean {
    const action = this.latestAction;
    return Boolean(
      action?.status === 'completed' &&
      Array.isArray(action.new_node_ids) &&
      action.new_node_ids.includes(nodeId),
    );
  }

  private isPendingLocalActionWithinPollingGrace(action: any): boolean {
    const pollUntil = Number(action?.poll_until || 0);
    if (Number.isFinite(pollUntil) && pollUntil > Date.now()) {
      return true;
    }

    const startedAt = Date.parse(action?.started_at || '');
    return (
      Number.isFinite(startedAt) &&
      Date.now() - startedAt < this.pendingActionPollingGraceMs
    );
  }

  private reconcilePendingAction(currentGraphSignature = this.getGraphProgressSignature(this.graphPayload)): void {
    const investigationStatus = String(this.investigation?.status || '').toLowerCase();
    const investigationComplete =
      investigationStatus === 'completed' || investigationStatus === 'complete';
    const hasServerProcessing = this.actionHistory.some(
      (action) => action.status === 'processing',
    );
    if (investigationComplete && !hasServerProcessing) {
      this.pendingLocalActions = this.pendingLocalActions.filter(
        (pendingLocalAction) => {
          if (pendingLocalAction.status !== 'processing') return true;

          const initialSignature = String(pendingLocalAction.initial_graph_signature || '');
          const graphChangedSinceAction =
            initialSignature && initialSignature !== currentGraphSignature;
          if (graphChangedSinceAction) return false;

          return this.isPendingLocalActionWithinPollingGrace(pendingLocalAction);
        },
      );
    }

    this.pendingLocalActions = this.pendingLocalActions.filter((pendingLocalAction) => {
      const pendingId = pendingLocalAction.action_id;
      const matched = this.actionHistory.find((action) => action.action_id === pendingId);
      if (matched) {
        return false;
      }
      const matchedByShape = this.actionHistory.find((action) => {
        const pendingStartedAt = Date.parse(pendingLocalAction.started_at || '');
        const actionStartedAt = Date.parse(action.started_at || '');
        const actionCompletedAt = Date.parse(action.completed_at || '');
        const isSameImageAction = Boolean(
          pendingLocalAction.action_type === 'face_search' &&
          pendingLocalAction.query_type === 'image' &&
          action.action_type === 'face_search' &&
          action.query_type === 'image' &&
          pendingLocalAction.image_url &&
          action.image_url === pendingLocalAction.image_url,
        );
        return (
          (action.source_node_id === pendingLocalAction.source_node_id || isSameImageAction) &&
          action.action_type === pendingLocalAction.action_type &&
          action.query_type === pendingLocalAction.query_type &&
          action.ai_mode === pendingLocalAction.ai_mode &&
          (!Number.isFinite(pendingStartedAt) ||
            (Number.isFinite(actionStartedAt) && actionStartedAt >= pendingStartedAt) ||
            (Number.isFinite(actionCompletedAt) && actionCompletedAt >= pendingStartedAt))
        );
      });
      return !matchedByShape;
    });
  }

  /** http(s) or auth-gated `/api/osint/image/...` (relative or absolute). */
  private isValidImageSearchUrl(value: string): boolean {
    const trimmed = String(value || '').trim();
    if (!trimmed) return false;
    if (isAuthGatedOsintImageUrl(trimmed)) return true;
    try {
      const parsed = new URL(trimmed);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private sortActionsByTime(actions: any[]): any[] {
    return [...actions].sort((left, right) => {
      const leftTime = this.getActionTime(left);
      const rightTime = this.getActionTime(right);
      return leftTime - rightTime;
    });
  }

  private getActionTime(action: any): number {
    const completed = Date.parse(action?.completed_at || '');
    if (Number.isFinite(completed)) return completed;
    const started = Date.parse(action?.started_at || '');
    if (Number.isFinite(started)) return started;
    return 0;
  }

  private escapeSvg(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
